import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Download } from 'lucide-react';
import { isDebitCompliant, needsFreshJustification, COMPLIANCE_MESSAGE } from '../utils/financialCompliance';
import { parseProofs, serializeProofs, MAX_PROOFS } from '../utils/proofs';
import ProofCell from '../components/ProofCell';
import { colors, spacing, fontSize, fontWeight, radius } from '../styles/theme';
import { BILL_TYPES } from '../constants/billingTypes';
import SearchableSelect, { SearchableSelectOption } from '../components/SearchableSelect';
import { INCOME_ENTRY_ENABLED } from '../constants/featureFlags';
import { useCurrentUser } from '../store/useCurrentUser';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Types ───────────────────────────────────────────────────────────────────

// 'ledger' entries come from financial_transactions and are editable via the
// Entry Panel. 'payroll' and 'billing' entries are read-through aggregates
// from their source modules — display-only, never edited or deleted here.
// 'baddebt' is a manual revenue reduction (its own table), shown as a negative
// Revenue line and managed via its own modal.
type LineSource = 'ledger' | 'payroll' | 'billing' | 'baddebt';

interface Expense {
  id: number;
  month: number;
  year: number;
  category: string;
  sub_category: string | null;
  // Subledger 2-5 names, in slot order. Only ledger entries carry these; the
  // entry panel uses them to nest an entry under its full chain.
  subledger_names?: string[];
  description: string | null;
  amount: number;
  source: LineSource;
}

interface PLSummary {
  month: number;
  year: number;
  expenses: Expense[];
  income: Expense[];
  total_sales: number;
  total_expenses: number;
  ebitda: number;
  ebitda_percent: number;
}

interface PLLine {
  key: string;
  label: string;
  // One subledger name per level under this ledger, for the "(Fuel, Two
  // Wheeler)" suffix — Subledger 1's name, then 2's, and so on.
  // Empty for payroll/billing rows.
  subledgers: string[];
  order: number;
  amount: number;
  source: LineSource;
}

// Petty-cash drawer position for the P&L's selected month (same figures the
// /financial/petty-cash screen shows for that month — shares the OPENING/TOPUP
// events and the paid_from_petty_cash transactions, just summarized here).
interface AccountBalance {
  opening: number;
  topups: number;
  expenses: number;
  balance: number;
}

// Manual bad-debt entry (own table) — subtracts from the month's revenue.
interface BadDebt {
  id: number;
  entry_date: string;
  amount: string | number;
  note: string | null;
  created_at: string;
  patient_id: number | null;
  reason_id: number | null;
  approved_by_id: number | null;
  patient_name: string | null;
  reason_name: string | null;
  approved_by_name: string | null;
  // Resolved server-side from created_by — the users table has no name column.
  created_by_email: string | null;
}

// ── Constants ────────────────────────────────────────────────────────────────

const PARTNERS = [
  { name: 'Leo Devasia', percent: 25 },
  { name: 'Ramnath Cowsik', percent: 71 },
  { name: 'Deepak Manuel', percent: 4 },
];

// Sort order only — the label is always the ledger's own name as the client
// typed it. These used to carry hardcoded labels ("Transport (fuel, vehicles,
// lease)"), which overrode whatever the ledger was actually called and drifted
// as soon as the client renamed or repurposed one.
// Salary lines aren't listed here — buildLines() gives every payroll-sourced
// line order 0 instead, because management is now one line per Class/Level and
// those labels are data, not something a static map can enumerate.
// A manually-entered expense line: what the business paid nursing vendors this
// month. It sits on the REVENUE side as a deduction: a synthetic line with no
// bills behind it, so its amount is purely negative. Stored in
// pl_manual_adjustments, never as a financial_transactions DEBIT — booking it
// as an expense as well would move EBITDA twice for one payment.
export const NURSE_VENDOR_CATEGORY = 'Nurse vendor payments';
// Its line_key. Not a BILL_TYPES key — there is no such bill type.
export const NURSE_VENDOR_LINE_KEY = 'nurse_vendor';

// Same idea for doctor visits: payments sent to doctor vendors, a pure-deduction
// line sitting directly under Doctor Visit. Manually entered, no bills behind it.
export const DOCTOR_VENDOR_CATEGORY = 'Doctor vendor payments';
export const DOCTOR_VENDOR_LINE_KEY = 'doctor_vendor';

// Auto-summed discount given on nursing bills — a deduction line sitting directly
// above Nurse Vendor Payments. Read from the bills (bill discount_amount), never
// entered by hand. Starts with "Nursing" so the export groups it in the nursing
// block, netting that subtotal.
export const NURSING_DISCOUNT_CATEGORY = 'Nursing Discount';

// Misc line items embedded inside other bills (Staff Food, Registration, etc.).
// Its own P&L row — pulled out of nursing revenue server-side — sitting just above
// the standalone-bill-type Misc line. Drill-down lists the bills carrying them.
export const MISC_LINE_CATEGORY = 'Misc (within bills)';
const MISC_LINE_DETAIL_KEY = '__misc_line__';

const EXPENSE_DISPLAY: Record<string, { label?: string; order?: number }> = {
  'transport':         { order: 1 },
  'rent':              { order: 2 },
  'hostel':            { order: 3 },
  'marketing':         { order: 4 },
  'transport_salary':  { order: 5 },
  'food':              { order: 6 },
  'it':                { order: 7 },
  'hr':                { order: 8 },
  'electricity':       { order: 9 },
  'telephone':         { order: 10 },
  'office':            { order: 11 },
  'bank_charges':      { order: 12 },
};

// INCOME_DISPLAY is declared further down, next to NURSING_SHIFT_BUCKETS —
// it derives its ordering from that constant and must come after it.

// bill_type key (from the billing module) → display label, reusing the same
// names shown on the Billing screen instead of inventing new ones.
const BILL_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  BILL_TYPES.map(b => [b.key, b.label])
);

const FALLBACK_ORDER = 999;

// The Subledger 1 names shown in a ledger line's bracket. Capped so a ledger
// with many subledgers can't push the row's figures out of view — the cell
// ellipsises, and the full list is in the row's hover tooltip either way.
const SUBLEDGER_SUFFIX_LIMIT = 3;
const subledgerSuffix = (names: string[]): string => {
  if (names.length <= SUBLEDGER_SUFFIX_LIMIT) return `(${names.join(', ')})`;
  const shown = names.slice(0, SUBLEDGER_SUFFIX_LIMIT).join(', ');
  return `(${shown} +${names.length - SUBLEDGER_SUFFIX_LIMIT} more)`;
};

// Where each payroll expense line sits. Management keeps order 0 and its
// existing alphabetical tie-break (there is one line per Class/Level and those
// labels are data, not something a static map can enumerate); the two named
// lines get explicit slots so their order doesn't depend on the alphabet.
const PAYROLL_ORDER = {
  management:  0,
  nursing:     0.1,
  physio:      0.2,
} as const;

// The per-Class/Level management lines, as get_salary_summary() labels them.
// The Expenses table folds them under one expandable "Management salaries" row;
// the lines themselves (and every total built from them) are untouched.
const MGMT_SALARY_PREFIX = 'Management salaries — ';
const isMgmtSalaryLine = (line: PLLine): boolean =>
  line.source === 'payroll' && line.label.startsWith(MGMT_SALARY_PREFIX);
// activeSalaryLine value for the group row itself: its panel lists every band.
const MGMT_GROUP_KEY = '__mgmt_group__';

// The client has no physiotherapists on payroll, so the Physiotherapist
// salaries line is hidden — but only while it is ₹0. Should a physio salary
// ever be entered, the line comes back, so the visible rows always add up to
// Total Expenses.
const isHiddenExpenseLine = (line: PLLine): boolean =>
  line.key === 'Physiotherapist salaries' && Math.round(line.amount) === 0;

const payrollOrder = (category: string): number =>
  category === 'Nursing staff salaries' ? PAYROLL_ORDER.nursing
  : category === 'Physiotherapist salaries' ? PAYROLL_ORDER.physio
  : PAYROLL_ORDER.management;

// Subledger names cascade: each level offers only the children of the level
// above it. Order matters — changing one clears everything after it.
const CASCADE_LEVELS = [
  'ledger_category_id',
  'ledger_subcategory_id',
  ...[2, 3, 4, 5].map(s => `ledger_subledger_${s}_id`),
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n?: number) =>
  '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// Amounts inside the expense panel, where a negative is a return — money
// coming back on something already spent. It reads as "+₹200", not "₹-200":
// the row is still an expense line, and the sign says which way the money
// went. Expenses stay unsigned, since that's the unremarkable case.
const fmtSigned = (n?: number) => {
  const v = n || 0;
  return v < 0 ? '+' + fmt(Math.abs(v)) : fmt(v);
};

const pct = (n?: number) => (n || 0).toFixed(1) + '%';

function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Snapshots the editable fields of an existing row at load time, so we can
// later tell whether amount/category/etc. were actually changed in this
// session and therefore need a fresh proof/remark — see needsFreshJustification().
function withOriginalSnapshot<T extends Record<string, any>>(row: T): T & { _original: Record<string, any> } {
  const { amount, particular, ledger_category_id, ledger_subcategory_id, payment_mode, proof, remarks } = row;
  return { ...row, _original: { amount, particular, ledger_category_id, ledger_subcategory_id, payment_mode, proof, remarks } };
}

function groupByCategory(entries: Expense[]): Record<string, Expense[]> {
  const map: Record<string, Expense[]> = {};
  for (const e of entries) {
    if (!map[e.category]) map[e.category] = [];
    map[e.category].push(e);
  }
  return map;
}

// One line per category that has at least one entry — guarantees every
// category with spend/income shows up, instead of only the ones matching a
// fixed hardcoded list.
function buildLines(entries: Expense[], displayMap: Record<string, { label?: string; order?: number }>): PLLine[] {
  const grouped = groupByCategory(entries);
  return Object.keys(grouped)
    .map(name => {
      const meta = displayMap[name.trim().toLowerCase()];
      // A category's entries all come from the same source — ledger
      // categories never overlap with payroll/billing-derived ones.
      const source = grouped[name][0].source;
      return {
        key: name,
        // The ledger's own name, as the client typed it. A static label would
        // go stale the moment they rename one.
        label: meta?.label || name,
        // EVERY Subledger 1 name under this ledger, so the line reads
        // "Transport (Fuel, Travel Allowance, Vehicle Loan)" — the ledger's own
        // first-level breakdown, which is what the statement is read for.
        //
        // Deliberately level 1 only. Taking one name per level instead mixed
        // depths: Transport printed "(Fuel, misc)" where Fuel is a Subledger 1
        // and misc a Subledger 2, which both hid the other four level-1 names
        // and implied the two were siblings. Deeper levels stay in the ledger
        // view, where the tree is shown properly. Only ledger-sourced lines
        // have subledgers; payroll and billing rows have none.
        subledgers: source === 'ledger'
          ? Array.from(new Set(
              grouped[name].map(e => (e.sub_category || '').trim()).filter(Boolean)
            )).sort()
          : [],
        // Salary lines lead the statement. Keyed off the source rather than the
        // label so the per-Class/Level management lines — whose labels come
        // from the data — group with the other payroll lines automatically.
        // Payroll lines lead the statement, in the explicit order above.
        order: meta?.order ?? (source === 'payroll' ? payrollOrder(name) : FALLBACK_ORDER),
        amount: grouped[name].reduce((s, e) => s + e.amount, 0),
        source,
      };
    })
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

// Maps a raw billing shift_type string to the client's nursing sales buckets.
// "12 Dual" is only recognized when the label literally says so; plain 12-hour
// shifts (12 Day / 12 Night / 12 Hrs / 12 Single) roll up to "12 Single".
// Anything without a recognizable hour prefix falls to "Other".
// '12 Dual' and '12 Single' were separate lines; the client wants one '12 Hrs'.
// 'Visit' replaces the old 'Other' catch-all: every bill that landed there was
// a nursing_visit bill, so it is a relabel rather than a reclassification.
// The Excel export derives its nursing order from this same list, so both
// screens can't drift.
const NURSING_SHIFT_BUCKETS = ['8 Hrs', '12 Hrs', '24 Hrs', 'Visit'] as const;

function classifyNursingShift(billType?: string | null, shiftType?: string | null): string {
  // bill_type wins: a nursing_visit bill is a Visit whatever its shift_type
  // says. Every bill that used to land in the old 'Other' bucket was one of
  // these, so this is a relabel, not a reclassification.
  if (String(billType || '') === 'nursing_visit') return 'Visit';
  const s = String(shiftType || '').trim().toLowerCase();
  if (!s) return 'Visit';
  if (/^0?8/.test(s)) return '8 Hrs';
  if (/^24/.test(s)) return '24 Hrs';
  // 'dual' now merges into 12 Hrs. The hour prefix is checked first, so
  // '12 Dual' matches /^12/ anyway; the includes() keeps a bare 'Dual' working.
  if (/^12/.test(s) || s.includes('dual')) return '12 Hrs';
  // Falls back to Visit rather than its own bucket. Correct for today's data;
  // a future bill_type='nursing' row with an unparseable shift_type would be
  // mislabelled here — the money is still counted in full, only the label
  // would be wrong.
  return 'Visit';
}

// The single source of truth for a nursing line's category string — used both
// to build the row and to key its sort order below, so the em-dash can never
// drift between the two and silently drop the line back to alphabetical order.
const nursingCategory = (bucket: string) =>
  bucket === 'Visit' ? 'Nursing Visit' : `Nursing — ${bucket}`;

const BAD_DEBT_CATEGORY = 'Bad Debts';

// ── Manual deductions on two Revenue lines ───────────────────────────────────
// Consumables and Medical Equipment (Rental) are shown net of what was spent
// restocking and what expired:  final = bills - purchase - expired
// Neither figure exists anywhere in the system (nothing aggregates batch
// dealer_price or device purchase_cost per month), so both are typed in on the
// statement. Keyed by bill_type key, not label — the label is presentation.
const ADJUSTABLE_REVENUE_LINES = ['consumables', 'equipment_rental', NURSE_VENDOR_LINE_KEY, DOCTOR_VENDOR_LINE_KEY] as const;
const ADJUSTMENT_KINDS = ['purchase', 'vendor_payment', 'expired', 'gst_tax'] as const;

// Credit notes against consumables. Not an AdjustmentKind: it is computed from
// bills rather than typed, so it has no input and is never written to
// pl_manual_adjustments.
const CONSUMABLE_RETURN_TYPE = 'consumable_return';
// Synthetic drill-down keys for lines that have no bill_type of their own: the
// consumable-returns figure inside the Consumables expander, and the nursing
// discount line. Both open the same RevenuePanel via their own cached bill list.
const RETURNS_LINE_KEY = '__consumable_returns__';
const NURSING_DISCOUNT_DETAIL_KEY = '__nursing_discount__';
type AdjustmentKind = typeof ADJUSTMENT_KINDS[number];

// Revenue line_key -> display label. Extends BILL_TYPE_LABEL with the one
// synthetic line that has no bill type behind it, so step 7d and
// ADJUSTABLE_LINE_KEY both resolve every key through one map.
const REVENUE_LINE_LABEL: Record<string, string> = {
  ...BILL_TYPE_LABEL,
  [NURSE_VENDOR_LINE_KEY]: NURSE_VENDOR_CATEGORY,
  [DOCTOR_VENDOR_LINE_KEY]: DOCTOR_VENDOR_CATEGORY,
};

// Operator-facing wording. All of these are subtracted from Billed — the
// expander's footer spells the arithmetic out, so the labels needn't repeat it.
const ADJUSTMENT_LABEL: Record<AdjustmentKind, string> = {
  purchase:       'Purchased amount',
  vendor_payment: 'Vendor payments',
  expired:        'Expired amount',
  gst_tax:        'GST & tax',
};

// Which deductions each line carries, in the order they are subtracted. The
// footer formula is generated from this, so adding a kind can never leave a
// stale description of the arithmetic.
const LINE_ADJUSTMENTS: Record<string, AdjustmentKind[]> = {
  consumables:               ['purchase', 'expired'],
  // gst_tax last: rental income is billed inclusive of GST that is collected
  // for the tax authority rather than earned, so it comes off after the costs.
  equipment_rental:          ['purchase', 'vendor_payment', 'expired', 'gst_tax'],
  [NURSE_VENDOR_LINE_KEY]:   ['vendor_payment'],
  [DOCTOR_VENDOR_LINE_KEY]:  ['vendor_payment'],
};

// vendor_payment is a LIST (several payments, each optionally naming a vendor);
// purchase/expired are single typed figures.
const isListKind = (kind: AdjustmentKind) => kind === 'vendor_payment';

interface PLManualAdjustment {
  id: number;
  month: number;
  year: number;
  line_key: string;
  kind: string;
  amount: number | string;
  vendor_name?: string | null;
  note?: string | null;
}

// Display label -> bill_type key, so a rendered row can find its own key.
// Built from BILL_TYPE_LABEL so it cannot drift from what step 7 assigns as
// the category string.
const ADJUSTABLE_LINE_KEY: Record<string, string> = Object.fromEntries(
  ADJUSTABLE_REVENUE_LINES.map(k => [REVENUE_LINE_LABEL[k], k])
);

// Income sort order. Without this every revenue line fell back to
// FALLBACK_ORDER and sorted alphabetically, which put "12" before "8".
// Keyed off BILL_TYPE_LABEL rather than typed literals: buildLines looks up
// INCOME_DISPLAY[name.toLowerCase()] where `name` came from BILL_TYPE_LABEL, so
// a hard-coded string would silently fall back to 999 the day someone edits
// billingTypes.ts.
const INCOME_DISPLAY: Record<string, { label?: string; order?: number }> = {
  // 1-4: Nursing — 8 Hrs, 12 Hrs, 24 Hrs, then Nursing Visit. Derived from
  // NURSING_SHIFT_BUCKETS so the screen matches the order nurseOrder() gives
  // the Excel export, instead of maintaining a second convention.
  ...Object.fromEntries(
    NURSING_SHIFT_BUCKETS.map((b, i) => [nursingCategory(b).toLowerCase(), { order: i + 1 }])
  ),
  // 4.4 / 4.5: the two Misc rows, directly under Nursing Visit (order 4). 4.4 is
  // misc line items embedded in other bills; 4.5 is standalone misc-type bills.
  [MISC_LINE_CATEGORY.toLowerCase()]:               { order: 4.4 },
  [BILL_TYPE_LABEL.misc.toLowerCase()]:             { order: 4.5 },
  // 4.9: nursing discount, a deduction sitting directly on top of Nurse Vendor.
  [NURSING_DISCOUNT_CATEGORY.toLowerCase()]:        { order: 4.9 },
  // 5: a deduction sitting mid-list with the nursing block it belongs to,
  // rather than pinned at the bottom like Bad Debts.
  [NURSE_VENDOR_CATEGORY.toLowerCase()]:            { order: 5 },
  [BILL_TYPE_LABEL.doctor_visit.toLowerCase()]:     { order: 6 },
  // 6.5: doctor vendor payments, a deduction sitting directly under Doctor Visit.
  [DOCTOR_VENDOR_CATEGORY.toLowerCase()]:           { order: 6.5 },
  [BILL_TYPE_LABEL.physio.toLowerCase()]:           { order: 7 },
  [BILL_TYPE_LABEL.consumables.toLowerCase()]:      { order: 9 },
  [BILL_TYPE_LABEL.equipment_rental.toLowerCase()]: { order: 10 },
  // Anything unlisted (Consumables Return, Medical Equipment (Sale)) keeps
  // FALLBACK_ORDER and lands between the named lines and Bad Debts.
  // Past FALLBACK_ORDER so it sorts after every revenue line: a deduction
  // belongs at the bottom, immediately above Total Sales.
  [BAD_DEBT_CATEGORY.toLowerCase()]: { order: 1000 },
};

// ── Subledger drill-down ─────────────────────────────────────────────────────
// The entry list mirrors the ledger's own hierarchy:
//     Ledger → Subledger 1 → Subledger 2 → … → 5 → entries
// `path` is the chain of subledger names drilled into so far. At any level the
// panel shows that level's subledger folders (with their rolled-up totals and
// counts) plus any entry that stops there — an entry tagged only to Subledger 1
// sits alongside the Subledger 2 folders rather than being hidden inside one.

// The subledger name an entry carries at `depth` (0 = Subledger 1, 1 = slot 2…).
function levelName(e: Expense, depth: number): string {
  if (depth === 0) return e.sub_category || '';
  return e.subledger_names?.[depth - 1] || '';
}

// Does this entry sit under the drilled-into path?
function onPath(e: Expense, path: string[]): boolean {
  return path.every((name, depth) => levelName(e, depth) === name);
}

interface SubledgerGroup {
  name: string;
  entries: Expense[];
  total: number;
}

// Entries under `path`, split into the next level's folders and the entries
// that stop here (no deeper tag).
function splitAtPath(entries: Expense[], path: string[]) {
  const scoped = entries.filter(e => onPath(e, path));
  const depth = path.length;
  const folders = new Map<string, Expense[]>();
  const leaves: Expense[] = [];
  for (const e of scoped) {
    const next = levelName(e, depth);
    if (next) {
      if (!folders.has(next)) folders.set(next, []);
      folders.get(next)!.push(e);
    } else {
      leaves.push(e);
    }
  }
  const groups: SubledgerGroup[] = Array.from(folders, ([name, es]) => ({
    name,
    entries: es,
    total: es.reduce((sum, e) => sum + e.amount, 0),
  })).sort((a, b) => b.total - a.total);
  return { groups, leaves, scoped };
}

// ── Salary register export ───────────────────────────────────────────────────
// One block of a salary register: the staff of one payroll line, A→Z. A heading
// turns it into a titled section with its own Sub Total row — that is how the
// consolidated management register separates its Class/Level bands.
interface SalaryRegisterGroup {
  heading?: string;
  staff: any[];
}

// A name as it should sort: without the leading spaces, zero-width characters
// and stray punctuation that come in with typed or pasted names. Those sort
// ahead of every letter and are invisible on screen, so one " Malavika" lands
// at the top of an otherwise A→Z list. Display is untouched — only the key.
const sortableName = (s: unknown): string =>
  String(s ?? '').replace(/^[\s​-‍⁠﻿.,'"`\-_()]+/, '').trim();

const compareNames = (a: unknown, b: unknown): number =>
  sortableName(a).localeCompare(sortableName(b), undefined, { sensitivity: 'base' });

const byName = (a: any, b: any) => compareNames(a.name, b.name);

// Builds and downloads the salary register for the given groups. Throws on
// failure; callers own the toast and their own busy state.
//
// The breakdown comes from /salary-month-summary, i.e. each person's Salary
// Sheet footer, because the P&L only carries Gross. That means a row's Gross is
// the sheet's, which for a nurse/physio whose month was never configured
// includes the sheet's default hostel deduction the P&L line does not take.
// Hostel is a nurse/physio deduction only; management sheets have no such field.
async function downloadSalaryRegister(
  sheetType: 'nurse' | 'physio' | 'management',
  title: string,
  groups: SalaryRegisterGroup[],
  month: number,
  year: number,
): Promise<void> {
  const res = await axios.get(`${API}/api/salary-month-summary`, {
    params: { staff_type: sheetType, month, year },
    headers: getAuthHeader(),
  });
  const byId: Record<number, any> = {};
  (Array.isArray(res.data) ? res.data : []).forEach((m: any) => { byId[m.staff_id] = m; });

  const includeHostel = sheetType !== 'management';
  const moneyCols: { key: string; header: string; deduction?: boolean }[] = [
    { key: 'subtotal', header: 'Sub Total' },
    { key: 'hra', header: 'HRA' },
    { key: 'allowance', header: 'Allowance' },
    ...(includeHostel ? [{ key: 'hostel', header: 'Hostel Fees', deduction: true }] : []),
    { key: 'gross', header: 'Gross Salary' },
    { key: 'pt', header: 'Professional Tax', deduction: true },
    { key: 'tds', header: 'TDS', deduction: true },
    { key: 'deductions', header: 'Advances', deduction: true },
    { key: 'total_deductions', header: 'Total Deductions', deduction: true },
    { key: 'net_take_home', header: 'Net Take Home Salary' },
  ];

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Salary Register');
  // A blank spacer column so the table starts in column B, as on the
  // Salary Sheets register.
  ws.columns = [
    { key: 'gap', width: 3 },
    { key: 'sl', width: 8 },
    { key: 'name', width: 30 },
    ...moneyCols.map(c => ({ key: c.key, width: Math.max(14, c.header.length + 4) })),
  ];
  const lastCol = ws.columns.length;
  const border = { top: { style: 'thin' as const }, left: { style: 'thin' as const }, bottom: { style: 'thin' as const }, right: { style: 'thin' as const } };
  const paint = (row: ExcelJS.Row, argb: string) => {
    for (let c = 2; c <= lastCol; c++) {
      const cl = row.getCell(c);
      cl.font = { bold: true };
      cl.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      cl.border = border;
    }
  };

  ws.mergeCells(2, 2, 2, lastCol);
  const titleCell = ws.getRow(2).getCell(2);
  ws.getRow(2).height = 30;
  titleCell.value = `${title} — Salary Register — ${MONTH_NAMES[month - 1]} ${year}`;
  titleCell.font = { size: 16, bold: true };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  const head = ws.getRow(4);
  head.height = 25;
  ['Sl No', 'Name', ...moneyCols.map(c => c.header)].forEach((h, i) => {
    const c = head.getCell(i + 2);
    c.value = h;
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    c.border = border;
  });

  let r = 5;
  // Sl No runs on across sections, so the last one is the headcount.
  let sl = 0;
  const grand: Record<string, number> = {};
  groups.forEach(g => {
    if (g.heading) {
      ws.mergeCells(r, 2, r, lastCol);
      const band = ws.getRow(r++);
      band.getCell(2).value = g.heading;
      paint(band, 'FFDBEAFE');
    }
    const sub: Record<string, number> = {};
    [...g.staff].sort(byName).forEach(p => {
      const m = p.staff_id ? byId[p.staff_id] : undefined;
      const row = ws.getRow(r++);
      row.getCell('sl').value = ++sl;
      row.getCell('name').value = p.name;
      moneyCols.forEach(c => {
        // Someone on the P&L line with no sheet figures (no base salary and
        // nothing saved) keeps the Gross the P&L shows; the rest stays blank
        // rather than invent a breakdown.
        const v = m ? Math.round(Number(m[c.key]) || 0)
          : c.key === 'gross' ? Math.round(p.gross || 0) : null;
        row.getCell(c.key).value = v;
        if (v !== null) {
          sub[c.key] = (sub[c.key] || 0) + v;
          grand[c.key] = (grand[c.key] || 0) + v;
        }
        if (c.deduction) row.getCell(c.key).font = { color: { argb: 'FF991B1B' } };
      });
      row.getCell('net_take_home').font = { bold: true };
      for (let c = 2; c <= lastCol; c++) row.getCell(c).border = border;
    });
    if (g.heading) {
      const st = ws.getRow(r++);
      st.getCell('name').value = `Sub Total — ${g.heading}`;
      moneyCols.forEach(c => { st.getCell(c.key).value = sub[c.key] || 0; });
      paint(st, 'FFF1F5F9');
    }
  });

  const tot = ws.getRow(r);
  tot.getCell('name').value = 'TOTAL';
  moneyCols.forEach(c => { tot.getCell(c.key).value = grand[c.key] || 0; });
  paint(tot, 'FFFCD34D');
  moneyCols.forEach(c => { ws.getColumn(c.key).numFmt = '#,##0'; });

  const buf = await wb.xlsx.writeBuffer();
  const safe = title.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  saveAs(new Blob([buf]), `${safe}_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
}

// ── Fill-the-card layout for the Revenue / Expenses statements ──────────────
// The two cards always run to the bottom of the screen. Rather than leave the
// space under a short statement blank, the line rows grow evenly to fill it (up
// to FILL_MAX_ROW_PX, so a sparse month doesn't get giant rows), and whatever
// is still left goes into a spacer row above the total, so Total Sales / Total
// Expenses always sits on the card's bottom edge. A statement too long for the
// card gets natural row heights, no spacer, and scrolls as before.
//
// Mark-up it relies on: the table carries FILL_TABLE_CLASS, the total row
// `pl-total`, and an empty `pl-spacer` row sits just above the total.
const FILL_TABLE_CLASS = 'pl-fill';
const FILL_MAX_ROW_PX = 48;
const FILL_CSS = `.${FILL_TABLE_CLASS} > tbody > tr:not(.pl-total):not(.pl-spacer) { height: var(--pl-row-h, auto); }`;

function useFillTable() {
  const bodyRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const fit = useCallback(() => {
    const body = bodyRef.current;
    const table = tableRef.current;
    const spacer = table?.querySelector<HTMLTableRowElement>('tr.pl-spacer');
    if (!body || !table || !spacer) return;

    // Back to natural heights, measure, then apply — all before the browser
    // paints, so there's no flicker.
    table.style.setProperty('--pl-row-h', 'auto');
    spacer.style.height = '0px';
    const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>(':scope > tbody > tr:not(.pl-spacer)'));
    const lines = rows.filter(r => !r.classList.contains('pl-total'));
    const total = rows.find(r => r.classList.contains('pl-total'));
    const heights = lines.map(r => r.getBoundingClientRect().height).sort((a, b) => a - b);
    const budget = body.clientHeight
      - (table.tHead?.getBoundingClientRect().height || 0)
      - (total?.getBoundingClientRect().height || 0);
    if (!heights.length) return;

    // The row height T that makes every line max(natural, T) exactly fill the
    // budget. Rows already taller than T (ones with a button) keep their own.
    let target = 0;
    let rest = 0;
    for (let k = heights.length; k >= 1; k--) {
      const t = (budget - rest) / k;
      if (t >= heights[k - 1]) { target = t; break; }
      rest += heights[k - 1];
    }
    target = Math.floor(Math.min(target, FILL_MAX_ROW_PX));
    const used = heights.reduce((sum, h) => sum + Math.max(h, target), 0);

    if (target > 0) table.style.setProperty('--pl-row-h', `${target}px`);
    spacer.style.height = `${Math.max(0, Math.floor(budget - used))}px`;
  }, []);

  // After every render (lines change, a group expands) and on resize. The card
  // only mounts once the P&L has loaded, so the resize watch is attached
  // whenever the body element first appears (or is replaced).
  const observed = useRef<HTMLDivElement | null>(null);
  const resizeObs = useRef<ResizeObserver | null>(null);
  useLayoutEffect(() => {
    fit();
    const body = bodyRef.current;
    if (body !== observed.current && typeof ResizeObserver !== 'undefined') {
      resizeObs.current?.disconnect();
      resizeObs.current = body ? new ResizeObserver(() => fit()) : null;
      if (body) resizeObs.current!.observe(body);
      observed.current = body;
    }
  });
  useEffect(() => () => resizeObs.current?.disconnect(), []);

  return { bodyRef, tableRef };
}

// ── Ledger export ────────────────────────────────────────────────────────────
// One ledger's entries for the month, from the Expense Entry panel. Exports
// what the panel is showing: the whole ledger at the top level, or just the
// drilled-into subledger. A return is a negative amount, as in the panel.
async function downloadLedgerEntries(ledger: string, path: string[], entries: Expense[], month: number, year: number): Promise<void> {
  const rows = [...entries].sort((a: any, b: any) =>
    String(a.transaction_date || '').localeCompare(String(b.transaction_date || ''))
    || (a.ledger_no ?? 0) - (b.ledger_no ?? 0));
  // One column per subledger level actually used, then the optional party
  // columns only when some entry fills them, so a simple ledger stays narrow.
  const depth = Math.max(0, ...rows.map(e => [e.sub_category || '', ...(e.subledger_names || [])]
    .reduce((d, n, i) => (n ? i + 1 : d), 0)));
  const has = (k: string) => rows.some((e: any) => e[k]);
  const cols: { header: string; width: number; get: (e: any) => any }[] = [
    // Date part only, pinned to UTC midnight: ExcelJS writes dates as UTC, so a
    // local-midnight Date would land on the previous day in IST.
    { header: 'Date', width: 13, get: e => e.transaction_date ? new Date(`${String(e.transaction_date).slice(0, 10)}T00:00:00Z`) : null },
    ...Array.from({ length: depth }, (_, i) => ({
      header: `Subledger ${i + 1}`, width: 20, get: (e: any) => levelName(e, i) || null,
    })),
    { header: 'Particular', width: 34, get: e => e.particular || null },
    { header: 'Payment Mode', width: 15, get: e => e.payment_mode || null },
    ...(has('transferred_from_name') ? [{ header: 'Transferred From', width: 20, get: (e: any) => e.transferred_from_name || null }] : []),
    ...(has('sender_name') ? [{ header: 'Sender', width: 20, get: (e: any) => e.sender_name || null }] : []),
    ...(has('receiver_name') ? [{ header: 'Receiver', width: 20, get: (e: any) => e.receiver_name || null }] : []),
    ...(has('remarks') ? [{ header: 'Remarks', width: 30, get: (e: any) => e.remarks || null }] : []),
    { header: 'Amount', width: 14, get: e => e.amount },
  ];

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Ledger');
  // Spacer column A, as on the salary register.
  ws.columns = [{ width: 3 }, { width: 8 }, ...cols.map(c => ({ width: c.width }))];
  const lastCol = cols.length + 2;
  const border = { top: { style: 'thin' as const }, left: { style: 'thin' as const }, bottom: { style: 'thin' as const }, right: { style: 'thin' as const } };

  const title = [ledger, ...path].join(' / ');
  ws.mergeCells(2, 2, 2, lastCol);
  ws.getRow(2).height = 30;
  const titleCell = ws.getRow(2).getCell(2);
  titleCell.value = `${title} — ${MONTH_NAMES[month - 1]} ${year}`;
  titleCell.font = { size: 16, bold: true };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  const head = ws.getRow(4);
  head.height = 25;
  ['Sl No', ...cols.map(c => c.header)].forEach((h, i) => {
    const c = head.getCell(i + 2);
    c.value = h;
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    c.border = border;
  });

  let r = 5;
  rows.forEach((e, i) => {
    const row = ws.getRow(r++);
    row.getCell(2).value = i + 1;
    cols.forEach((c, j) => { row.getCell(j + 3).value = c.get(e); });
    for (let c = 2; c <= lastCol; c++) row.getCell(c).border = border;
  });

  const tot = ws.getRow(r);
  tot.getCell(lastCol - 1).value = 'TOTAL';
  tot.getCell(lastCol).value = rows.reduce((s, e) => s + e.amount, 0);
  for (let c = 2; c <= lastCol; c++) {
    const cl = tot.getCell(c);
    cl.font = { bold: true };
    cl.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCD34D' } };
    cl.border = border;
  }
  ws.getColumn(3).numFmt = 'dd-mm-yyyy';
  ws.getColumn(lastCol).numFmt = '#,##0.00;-#,##0.00';

  const buf = await wb.xlsx.writeBuffer();
  const safe = title.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  saveAs(new Blob([buf]), `${safe}_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
}

// ── Salary breakdown panel ───────────────────────────────────────────────────
// Who a payroll line is made of. Read-only: these figures come from the
// Management Salary Sheet, so editing happens there, not here.

interface SalaryPanelProps {
  line?: PLLine;
  staff: any[];
  search: string;
  onSearch: (v: string) => void;
  onClose: () => void;
  // Carried into each person's sheet link so it opens on the month being viewed.
  month: number;
  year: number;
  // Keep `staff` in the order given instead of sorting A→Z — the consolidated
  // management list arrives already in the registration tab's Sl.No order.
  keepOrder?: boolean;
  // Replaces the default Excel (the list as shown) — the consolidated
  // management panel downloads the class-by-class register instead.
  onExportExcel?: () => Promise<void>;
  exportTitle?: string;
  s: Record<string, React.CSSProperties>;
}

function SalaryPanel({ line, staff, search, onSearch, onClose, month, year, keepOrder, onExportExcel, exportTitle, s }: SalaryPanelProps) {
  // Where the two header buttons go, per staff type.
  //
  // Every staff type lands on the salary-sheet index, on its own tab, rather
  // than on one particular sheet — same destination for all three, so the
  // button behaves the same way whichever salary line was clicked.
  const dashboard = line?.key === 'Nursing staff salaries'
    ? { path: '/nurses', label: 'Nurses dashboard', sheet: '/salarysheet?tab=nurse', sheetLabel: 'salary sheets' }
    : line?.key === 'Physiotherapist salaries'
      ? { path: '/administration/physio', label: 'Physiotherapists dashboard', sheet: '/salarysheet?tab=physio', sheetLabel: 'salary sheets' }
      : { path: '/administration/management-staff', label: 'Management staff dashboard', sheet: '/salarysheet?tab=management', sheetLabel: 'salary sheets' };

  // Which per-person sheet route this line's staff belong to. Management has its
  // own daily-grid screen; nurses and physios share the /:type/:id sheet. staff_id
  // is only unique WITHIN a type — id 8 is both a manager and a nurse — so the
  // type has to come from the line, never from the row.
  const sheetType = line?.key === 'Nursing staff salaries'
    ? 'nurse'
    : line?.key === 'Physiotherapist salaries'
      ? 'physio'
      : 'management';
  const personSheet = (staffId: number) =>
    `/salarysheet/${sheetType}/${staffId}?month=${month}&year=${year}`;

  const q = search.trim().toLowerCase();
  // A→Z by name, case-insensitive. The backend hands these over in Sl.No order
  // (management) or query order (nurse/physio), and a band's Transport staff
  // are appended after it — none of which helps someone looking for a name.
  // The consolidated management list is the exception (keepOrder).
  const matched = q
    ? staff.filter(p => [p.name, p.designation, p.department, p.class_level]
        .some(f => String(f || '').toLowerCase().includes(q)))
    : [...staff];
  const shown = keepOrder ? matched : matched.sort(byName);
  const shownTotal = shown.reduce((sum, p) => sum + (p.gross || 0), 0);

  // The salary register for exactly the people the panel is showing — search
  // included, like the revenue panel's export — in the same A→Z order.
  const [exporting, setExporting] = React.useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      if (onExportExcel) await onExportExcel();
      else await downloadSalaryRegister(sheetType, line?.label || 'Salaries', [{ staff: shown }], month, year);
    } catch (err) {
      console.error('salary register export failed', err);
      toast.error('Could not build the Excel file');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ ...s.card, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...s.cardHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: '0 0 auto' }}>
        <div style={{ minWidth: 0, flex: 1, marginRight: spacing(2) }}>
          {/* Wraps to a second line instead of truncating: these labels run to
              "Management salaries — Management - Class 1", and an ellipsis hides
              exactly the part that says which line you are looking at. Capped at
              two lines so a long label can't push the buttons out of view. */}
          <div
            title={line?.label || 'Salaries'}
            style={{
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', lineHeight: 1.25, wordBreak: 'break-word',
            }}
          >
            {line?.label || 'Salaries'}
          </div>
          <span style={{ fontWeight: fontWeight.regular, fontSize: fontSize.xs, color: colors.textFaint }}>
            from Payroll — read only
          </span>
        </div>
        <div style={{ display: 'flex', gap: spacing(1), alignItems: 'center', flex: '0 0 auto' }}>
          <button
            style={{ ...s.btnSm, ...s.btnGhost }}
            title={`Open the ${dashboard.sheetLabel}`}
            onClick={() => window.open(dashboard.sheet, '_blank')}
          >
            Salary Sheet
          </button>
          <button
            style={{ ...s.btnSm, ...s.btnGhost }}
            title={`Open the ${dashboard.label}`}
            onClick={() => window.open(dashboard.path, '_blank')}
          >
            Dashboard
          </button>
          <button
            style={{ ...s.btnSm, ...s.btnGhost }}
            title={exportTitle || 'Download these salaries as an Excel register'}
            disabled={exporting || (onExportExcel ? staff.length === 0 : shown.length === 0)}
            onClick={exportExcel}
          >
            {exporting ? 'Building…' : 'Excel'}
          </button>
          <button
            style={{ ...s.btnSm, ...s.btnGhost, padding: '0.15rem 0.4rem', lineHeight: 1 }}
            title="Close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ padding: spacing(4), paddingBottom: 0, flex: '0 0 auto' }}>
        <input
          autoFocus
          style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
          placeholder="Search name, designation, department…"
          value={search}
          onChange={e => onSearch(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: spacing(2), fontSize: fontSize.sm, color: colors.textMuted }}>
          <span>{shown.length} {shown.length === 1 ? 'person' : 'people'}{q && staff.length !== shown.length ? ` of ${staff.length}` : ''}</span>
          <span>Total: <strong style={{ color: colors.textPrimary }}>{fmt(shownTotal)}</strong></span>
        </div>
      </div>

      <div style={{ padding: spacing(4), flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {shown.length === 0 && (
          <div style={{ textAlign: 'center', padding: spacing(6), color: colors.textFaint, fontSize: fontSize.md }}>
            {q ? 'Nobody matches that search.' : 'No staff on this line.'}
          </div>
        )}

        {shown.map((p, i) => (
          <div
            key={`${p.name}-${i}`}
            style={{ display: 'flex', alignItems: 'center', gap: spacing(2), padding: '0.5rem 0.7rem', background: colors.surfaceMuted, borderRadius: radius.md, marginBottom: 5, border: `1px solid ${colors.borderMuted}` }}
          >
            {/* Serial number over the list as shown (A→Z, or Sl.No order for
                the consolidated management list, after any search), in the
                same chip as the revenue panel's. */}
            <span style={{
              flex: '0 0 auto', minWidth: 20, textAlign: 'center',
              padding: '1px 4px', borderRadius: radius.sm,
              background: colors.surface, border: `1px solid ${colors.borderMuted}`,
              color: colors.textMuted, fontSize: fontSize.xs, fontWeight: fontWeight.semibold,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1.5,
            }}>
              {i + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* The name opens this person's own salary sheet. A row with no
                  staff_id can't be linked, so it stays plain text rather than
                  looking clickable and doing nothing. */}
              <div
                onClick={p.staff_id ? () => window.open(personSheet(p.staff_id), '_blank') : undefined}
                title={p.staff_id ? `Open ${p.name}'s salary sheet` : undefined}
                style={{
                  fontWeight: fontWeight.semibold, fontSize: fontSize.md,
                  color: p.staff_id ? colors.primary : colors.textPrimary,
                  cursor: p.staff_id ? 'pointer' : 'default',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {p.name}
              </div>
              <div style={{ fontSize: fontSize.xs, color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {[p.designation, p.department, p.class_level].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <div style={{ fontWeight: fontWeight.bold, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>
              {fmt(p.gross || 0)}
            </div>
            {p.staff_id && (
              <button
                style={{ ...s.btnSm, ...s.btnGhost, flex: '0 0 auto', padding: '0.1rem 0.45rem' }}
                title={`Open ${p.name}'s salary sheet`}
                onClick={() => window.open(personSheet(p.staff_id), '_blank')}
              >
                View
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Revenue panel ────────────────────────────────────────────────────────────
// The bills behind one revenue line, grouped by patient and expandable to the
// individual bills. Mirrors SalaryPanel's shell so the two drill-downs read the
// same way; the rows differ because revenue is per-bill, not per-person.

interface RevenuePanelProps {
  line?: PLLine;
  rows: any[];
  // Nursing rows are line items keyed `monthly`; other lines are whole bills
  // keyed `total_amount`. The panel is told which rather than guessing per row.
  isNursing: boolean;
  // Consumables / ME Rental show GROSS here: the statement's Amount is net of
  // manual deductions that no bill knows about, so the bills sum to gross.
  headerTotal: number;
  deducted: number;
  // For the export's title row and filename, e.g. "September 2026".
  monthLabel: string;
  loading: boolean;
  search: string;
  onSearch: (v: string) => void;
  expandedPatient: string | null;
  onExpandPatient: (k: string | null) => void;
  onClose: () => void;
  s: Record<string, React.CSSProperties>;
}

function RevenuePanel({
  line, rows, isNursing, headerTotal, deducted, monthLabel, loading,
  search, onSearch, expandedPatient, onExpandPatient, onClose, s,
}: RevenuePanelProps) {
  const rowAmount = (r: any) => Number(isNursing ? r.monthly : r.total_amount) || 0;

  // A consumable bill has its own editor; everything else opens the bill sheet.
  const billHref = (r: any) =>
    String(r.bill_type || '').startsWith('consumable')
      ? `/billing/consumables/edit/${r.bill_id ?? r.id}`
      : `/billing/bill/${r.bill_id ?? r.id}`;

  const q = search.trim().toLowerCase();
  const matches = q
    ? rows.filter(r => [r.patient_name, r.client_name, r.bill_no, r.invoice_no]
        .some(f => String(f ?? '').toLowerCase().includes(q)))
    : rows;

  // Group by patient. Falls back to the name when patient_id is absent, so a
  // row can never be silently dropped from the list.
  const groups = (() => {
    const byPatient = new Map<string, { key: string; name: string; client: string | null; total: number; bills: any[] }>();
    matches.forEach(r => {
      const key = String(r.patient_id ?? r.patient_name ?? 'unknown');
      const g = byPatient.get(key) || {
        key, name: r.patient_name || 'Unknown', client: r.client_name || null, total: 0, bills: [] as any[],
      };
      g.total += rowAmount(r);
      g.bills.push(r);
      byPatient.set(key, g);
    });
    // Sorted by the name the row displays — the client, falling back to the
    // patient when no client is registered — so the list reads alphabetically
    // as shown rather than by a key that is sometimes hidden. compareNames
    // ignores leading spaces/invisible characters in the stored name.
    return Array.from(byPatient.values()).sort((a, b) =>
      compareNames(a.client || a.name, b.client || b.name));
  })();

  const shownTotal = groups.reduce((sum, g) => sum + g.total, 0);

  // Exports exactly what the panel is showing — the current search included, so
  // a filtered view downloads filtered. One row per bill, grouped under its
  // client, with a per-client subtotal and a grand total that ties to the P&L
  // line the panel was opened from.
  const [exporting, setExporting] = React.useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const AMT = '#,##,##0.00;-#,##,##0.00;"-"';
      const BANNER = 'FF1E3A8A';
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Sales Detail');
      ws.columns = [
        { width: 6 },   // Sl
        { width: 28 },  // Client
        { width: 28 },  // Patient
        { width: 16 },  // Bill / Invoice
        { width: 13 },  // Period from
        { width: 13 },  // Period to
        { width: 14 },  // Shift / Item
        { width: 10 },  // Qty
        { width: 13 },  // Rate
        { width: 15 },  // Amount
        { width: 12 },  // Status
      ];
      ws.getColumn(9).numFmt = AMT;
      ws.getColumn(10).numFmt = AMT;

      const title = ws.addRow([`${line?.label || 'Revenue'} — ${monthLabel}`]);
      title.font = { bold: true, size: 13 };
      ws.addRow([]);

      const head = ws.addRow([
        'Sl No', 'Client', 'Patient', 'Bill / Invoice', 'Period From', 'Period To',
        isNursing ? 'Shift' : 'Item', isNursing ? 'Shifts' : 'Qty', 'Rate', 'Amount', 'Status',
      ]);
      head.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      head.eachCell(c => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BANNER } };
      });

      let sl = 1;
      groups.forEach(g => {
        g.bills.forEach((r: any) => {
          ws.addRow([
            sl++,
            g.client || '',
            g.name || '',
            r.bill_no ? `#${r.bill_no}` : (r.invoice_no || `Bill ${r.bill_id ?? r.id}`),
            r.period_start || r.invoice_period_start || '',
            r.period_end || r.invoice_period_end || '',
            isNursing ? (r.shift_type || '') : (r.particulars || r.bill_type || ''),
            isNursing ? (r.shifts || 0) : '',
            isNursing ? (r.rate || 0) : '',
            rowAmount(r),
            r.status || '',
          ]);
        });
        // Per-client subtotal, so the sheet can be read without re-adding it up.
        const st = ws.addRow(['', g.client || g.name, '', '', '', '', '', '', 'Sub Total', g.total, '']);
        st.font = { bold: true };
        st.getCell(10).numFmt = AMT;
      });

      if (groups.length === 0) ws.addRow(['No bills behind this line']);

      ws.addRow([]);
      const tot = ws.addRow(['', 'TOTAL', '', '', '', '', '', '', '', shownTotal, '']);
      tot.font = { bold: true };
      tot.getCell(10).numFmt = AMT;
      if (deducted > 0) {
        // Said on the sheet as well as the panel: this total is gross, and the
        // statement shows it net of manual deductions no bill knows about.
        const note = ws.addRow(['', `Less manual deductions`, '', '', '', '', '', '', '', -deducted, '']);
        note.getCell(10).numFmt = AMT;
        const net = ws.addRow(['', 'As shown on P&L', '', '', '', '', '', '', '', shownTotal - deducted, '']);
        net.font = { bold: true };
        net.getCell(10).numFmt = AMT;
      }

      const buf = await wb.xlsx.writeBuffer();
      const safe = (line?.label || 'Revenue').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
      saveAs(new Blob([buf]), `${safe}_${monthLabel.replace(' ', '_')}.xlsx`);
    } catch (err) {
      console.error('revenue detail export failed', err);
      toast.error('Could not build the Excel file');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ ...s.card, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...s.cardHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: '0 0 auto' }}>
        <div style={{ minWidth: 0, flex: 1, marginRight: spacing(2) }}>
          <div
            title={line?.label || 'Revenue'}
            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.25, wordBreak: 'break-word' }}
          >
            {line?.label || 'Revenue'}
          </div>
          <span style={{ fontWeight: fontWeight.regular, fontSize: fontSize.xs, color: colors.textFaint }}>
            from Billing — read only
          </span>
        </div>
        <div style={{ display: 'flex', gap: spacing(1), alignItems: 'center', flex: '0 0 auto' }}>
          <button
            style={{ ...s.btnSm, ...s.btnGhost }}
            title="Download these bills as Excel"
            disabled={exporting || loading || groups.length === 0}
            onClick={exportExcel}
          >
            {exporting ? 'Building…' : 'Excel'}
          </button>
          <button
            style={{ ...s.btnSm, ...s.btnGhost, padding: '0.15rem 0.4rem', lineHeight: 1 }}
            title="Close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ padding: spacing(4), paddingBottom: 0, flex: '0 0 auto' }}>
        <input
          autoFocus
          style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
          placeholder="Search client, patient, bill no…"
          value={search}
          onChange={e => onSearch(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: spacing(2), fontSize: fontSize.sm, color: colors.textMuted }}>
          <span>{groups.length} {groups.length === 1 ? 'patient' : 'patients'}</span>
          <span>Total: <strong style={{ color: colors.textPrimary }}>{fmt(shownTotal)}</strong></span>
        </div>
        {/* Said plainly, because this figure deliberately differs from the
            statement's Amount column for the two lines carrying deductions. */}
        {deducted > 0 && !q && (
          <div style={{ fontSize: fontSize.xs, color: colors.textMuted, fontStyle: 'italic', marginTop: spacing(1) }}>
            Billed before {fmt(deducted)} of manual deductions — the statement shows {fmt(headerTotal - deducted)}.
          </div>
        )}
      </div>

      <div style={{ padding: spacing(4), flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: spacing(6), color: colors.textFaint, fontSize: fontSize.md }}>
            Loading bills…
          </div>
        )}

        {!loading && groups.length === 0 && (
          <div style={{ textAlign: 'center', padding: spacing(6), color: colors.textFaint, fontSize: fontSize.md }}>
            {q ? 'Nothing matches that search.' : 'No bills behind this line.'}
          </div>
        )}

        {!loading && groups.map((g, gi) => {
          const isOpen = expandedPatient === g.key;
          return (
            <div key={g.key} style={{ marginBottom: 5 }}>
              <div
                onClick={() => onExpandPatient(isOpen ? null : g.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: spacing(2),
                  padding: '0.5rem 0.7rem', background: isOpen ? colors.primaryBg : colors.surfaceMuted,
                  borderRadius: radius.md, border: `1px solid ${colors.borderMuted}`, cursor: 'pointer',
                }}
              >
                <span style={{ flex: '0 0 auto', color: colors.textMuted, fontSize: fontSize.xs }}>
                  {isOpen ? '▾' : '▸'}
                </span>
                {/* Fixed width so names stay aligned once the count reaches
                    double digits. Sits in its own tinted chip because a faint
                    number next to the arrow read as part of the chevron rather
                    than as a row number. */}
                <span style={{
                  flex: '0 0 auto', minWidth: 20, textAlign: 'center',
                  padding: '1px 4px', borderRadius: radius.sm,
                  background: colors.surface, border: `1px solid ${colors.borderMuted}`,
                  color: colors.textMuted, fontSize: fontSize.xs, fontWeight: fontWeight.semibold,
                  fontVariantNumeric: 'tabular-nums', lineHeight: 1.5,
                }}>
                  {gi + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* The client is who the money comes from, so it leads; the
                      patient is who the care was for. Falls back to the patient
                      name when no client is registered, rather than showing a
                      blank headline. */}
                  <div style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {g.client || g.name}
                  </div>
                  <div style={{ fontSize: fontSize.xs, color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {[g.client ? g.name : null, `${g.bills.length} bill${g.bills.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ fontWeight: fontWeight.bold, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>
                  {fmt(g.total)}
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: 3, marginLeft: spacing(4) }}>
                  {g.bills.map((r: any, bi: number) => (
                    <div
                      key={`${r.bill_id ?? r.id}-${bi}`}
                      style={{ display: 'flex', alignItems: 'center', gap: spacing(2), padding: '0.35rem 0.6rem', borderLeft: `2px solid ${colors.borderMuted}`, marginBottom: 2 }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: fontSize.sm, color: colors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.bill_no ? `Bill #${r.bill_no}` : r.invoice_no ? `Invoice ${r.invoice_no}` : `Bill ${r.bill_id ?? r.id}`}
                          {isNursing && r.shift_type ? ` · ${r.shift_type}` : ''}
                        </div>
                        <div style={{ fontSize: fontSize.xs, color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {[
                            (r.period_start || r.invoice_period_start)
                              ? `${r.period_start || r.invoice_period_start} → ${r.period_end || r.invoice_period_end || ''}`
                              : null,
                            isNursing && r.shifts ? `${r.shifts} shifts` : null,
                            r.status || null,
                          ].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <div style={{ fontSize: fontSize.sm, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>
                        {fmt(rowAmount(r))}
                      </div>
                      {(r.bill_id ?? r.id) && (
                        <button
                          style={{ ...s.btnSm, ...s.btnGhost, flex: '0 0 auto', padding: '0.1rem 0.45rem' }}
                          title="Open this bill"
                          onClick={e => { e.stopPropagation(); window.open(billHref(r), '_blank'); }}
                        >
                          View
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ── Entry panel ──────────────────────────────────────────────────────────────
// Income Entry and Expense Entry are the same UI shape (category chips, add
// entry, edit/delete) differing only in transaction_type and which active
// tab they track — shared here instead of duplicated per panel.

interface EntryPanelProps {
  title: string;
  categories: { id: number; name: string }[];
  grouped: Record<string, Expense[]>;
  activeTab: string | null;
  onSelectTab: (name: string | null) => void;
  onOpenAddCategory: () => void;
  month: number;
  year: number;
  onOpenBatch: () => void;
  onEdit: (e: Expense) => void;
  onDelete: (id: number) => void;
  onRenameCategory: (cat: { id: number; name: string }) => void;
  onDeleteCategory: (cat: { id: number; name: string }) => void;
  s: Record<string, React.CSSProperties>;
}

// One row shape for every level of the tree — ledgers, subledger folders and
// entries — so drilling down reads as moving through one list rather than
// switching between three different-looking ones.
const rowBase: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: spacing(2),
  padding: '0.5rem 0.7rem', borderRadius: radius.md, marginBottom: 5,
};
const rowTitle: React.CSSProperties = {
  fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.textPrimary,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const rowMeta: React.CSSProperties = {
  fontSize: fontSize.xs, color: colors.textMuted,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const rowAmount: React.CSSProperties = {
  fontWeight: fontWeight.bold, fontVariantNumeric: 'tabular-nums',
  color: colors.textPrimary, flex: '0 0 auto',
};
const chevron: React.CSSProperties = {
  color: colors.textFaint, fontSize: fontSize.md, flex: '0 0 auto', lineHeight: 1,
};

function EntryPanel({ title, categories, grouped, activeTab, onSelectTab, onOpenAddCategory, month, year, onOpenBatch, onEdit, onDelete, onRenameCategory, onDeleteCategory, s }: EntryPanelProps) {
  const activeCategory = activeTab ? categories.find(c => c.name === activeTab) : undefined;

  // Subledger names drilled into, under the selected ledger. Reset whenever the
  // ledger changes — a path from one ledger is meaningless in another.
  const [path, setPath] = useState<string[]>([]);
  useEffect(() => { setPath([]); }, [activeTab]);
  // Edit/Delete only show on the row under the cursor, so a long list reads as
  // data rather than a wall of buttons.
  const [hoveredEntry, setHoveredEntry] = useState<number | null>(null);

  const allEntries = activeTab ? (grouped[activeTab] || []) : [];
  const { groups, leaves, scoped } = splitAtPath(allEntries, path);
  // Total reflects where you are: the whole ledger at the top, the drilled-into
  // subledger once you descend.
  const total = scoped.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div style={{ ...s.card, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...s.cardHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: '0 0 auto' }}>
        <div>
          {title}
          <span style={{ fontWeight: fontWeight.regular, fontSize: fontSize.xs, color: colors.textFaint, marginLeft: spacing(2) }}>
            — click through ledger / subledger to see entries
          </span>
        </div>
        <button
          style={{ ...s.btnSm, background: colors.surfaceMuted, border: `1px solid ${colors.border}`, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: spacing(1) }}
          onClick={() => window.open(`/financial/ledger?month=${month}&year=${year}`, '_blank')}
        >
          View Full Ledger
        </button>
      </div>

      <div style={{ ...s.tabs, flex: '0 0 auto' }}>
        {categories.map(cat => (
          <button
            key={cat.id}
            style={{ ...s.tab, ...(activeTab === cat.name ? s.tabActive : {}) }}
            onClick={() => onSelectTab(activeTab === cat.name ? null : cat.name)}
          >
            {cat.name}
            {(grouped[cat.name] || []).length > 0 && (
              <span style={{ marginLeft: 4, background: activeTab === cat.name ? 'rgba(255,255,255,0.3)' : colors.primaryBg2, color: activeTab === cat.name ? 'white' : colors.primaryDark, borderRadius: radius.pill, padding: '0px 5px', fontSize: fontSize.xs }}>
                {(grouped[cat.name] || []).length}
              </span>
            )}
          </button>
        ))}

        <button
          style={{ ...s.tab, borderStyle: 'dashed', background: colors.surfaceMuted, color: colors.primary }}
          onClick={onOpenAddCategory}
        >
          + Add Category
        </button>
      </div>

      {/* Scrolls internally if the selected category's entry list grows long, instead of growing the page */}
      <div style={{ padding: spacing(4), flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {/* No ledger picked — list them all, same row shape as the subledger
            folders below, so the whole hierarchy reads as one tree:
            ledger → subledger 1 → 2 → … → entries. */}
        {!activeTab && (
          <>
            {categories.length === 0 && (
              <div style={{ textAlign: 'center', padding: spacing(8), color: colors.textFaint, fontSize: fontSize.md }}>
                No ledgers yet. Click "+ Add Category" to start.
              </div>
            )}
            {categories.map(cat => {
              const es = grouped[cat.name] || [];
              const catTotal = es.reduce((sum, e) => sum + e.amount, 0);
              return (
                <div
                  key={cat.id}
                  style={{ ...rowBase, background: colors.surface, border: `1px solid ${colors.border}`, cursor: 'pointer' }}
                  onClick={() => onSelectTab(cat.name)}
                  title={`Open ${cat.name}`}
                >
                  <span style={chevron}>›</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={rowTitle}>{cat.name}</div>
                    <div style={rowMeta}>
                      {es.length ? `${es.length} entr${es.length === 1 ? 'y' : 'ies'}` : 'No entries'}
                    </div>
                  </div>
                  <div style={{ ...rowAmount, ...(catTotal < 0 ? { color: colors.success } : {}) }}>{fmtSigned(catTotal)}</div>
                </div>
              );
            })}
          </>
        )}

        {activeTab && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing(3) }}>
              <div>
                {/* Breadcrumb: All / Ledger / Subledger 1 / Subledger 2 …
                    Every crumb but the last jumps back to that level. */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing(1), flexWrap: 'wrap' }}>
                  <span
                    style={{ fontSize: fontSize.sm, color: colors.primary, cursor: 'pointer' }}
                    onClick={() => onSelectTab(null)}
                    title="Back to all ledgers"
                  >
                    All
                  </span>
                  <span style={{ color: colors.textFaint, fontSize: fontSize.sm }}>/</span>
                  <span
                    style={{ fontWeight: fontWeight.bold, color: path.length ? colors.primary : colors.textPrimary, fontSize: fontSize.md, cursor: path.length ? 'pointer' : 'default' }}
                    onClick={() => setPath([])}
                  >
                    {activeTab}
                  </span>
                  {path.map((name, i) => (
                    <React.Fragment key={`${name}-${i}`}>
                      <span style={{ color: colors.textFaint, fontSize: fontSize.sm }}>/</span>
                      <span
                        style={{ fontWeight: fontWeight.bold, fontSize: fontSize.md, color: i === path.length - 1 ? colors.textPrimary : colors.primary, cursor: i === path.length - 1 ? 'default' : 'pointer' }}
                        onClick={() => setPath(path.slice(0, i + 1))}
                      >
                        {name}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>
                  Total: <strong>{fmtSigned(total)}</strong>
                  {scoped.length > 0 && <> · {scoped.length} entr{scoped.length === 1 ? 'y' : 'ies'}</>}
                </div>
              </div>
              {/* Compact actions — these sit above the data every time a
                  ledger is open, so they stay out of the way. */}
              <div style={{ display: 'flex', gap: spacing(1), alignItems: 'center', flex: '0 0 auto' }}>
                {activeCategory && (
                  <>
                    <button
                      style={{ ...s.btnSm, ...s.btnGhost, display: 'flex', alignItems: 'center', opacity: scoped.length ? 1 : 0.5 }}
                      title={`Download ${[activeTab, ...path].join(' / ')} as Excel`}
                      aria-label="Download as Excel"
                      disabled={!scoped.length}
                      onClick={() => downloadLedgerEntries(activeTab, path, scoped, month, year)
                        .catch(err => { console.error('ledger export failed', err); toast.error('Could not build the Excel file.'); })}
                    >
                      <Download size={14} />
                    </button>
                    <button
                      style={{ ...s.btnSm, ...s.btnGhost }}
                      title="Rename this ledger"
                      onClick={() => onRenameCategory(activeCategory)}
                    >
                      Rename
                    </button>
                    <button
                      style={{ ...s.btnSm, background: colors.dangerBg2, color: colors.danger }}
                      title="Delete this ledger and its entries"
                      onClick={() => onDeleteCategory(activeCategory)}
                    >
                      Delete
                    </button>
                  </>
                )}
                <button style={{ ...s.btnSm, ...s.btnGhost }} title="Open the full ledger for this category" onClick={() => window.open(`/financial/ledger?month=${month}&year=${year}&category=${activeTab}`, '_blank')}>
                  Ledger
                </button>
                <button style={{ ...s.btnSm, ...s.btnPrimary }} onClick={onOpenBatch}>+ Add</button>
              </div>
            </div>

            {scoped.length === 0 && (
              <div style={{ textAlign: 'center', padding: spacing(6), color: colors.textFaint, fontSize: fontSize.md }}>
                No entries yet. Click "Add Entry" to start.
              </div>
            )}

            {/* Subledger folders at this level — click to drill in. Totals and
                counts roll up everything beneath them. The meta line previews
                what's inside: the next level's names if this folder branches
                again, otherwise the entry descriptions. */}
            {groups.map(g => {
              const inner = splitAtPath(g.entries, [...path, g.name]);
              const preview = inner.groups.length
                ? inner.groups.map(x => x.name)
                : Array.from(new Set(g.entries.map(e => e.description).filter(Boolean) as string[]));
              return (
                <div
                  key={g.name}
                  style={{ ...rowBase, background: colors.surface, border: `1px solid ${colors.border}`, cursor: 'pointer' }}
                  onClick={() => setPath([...path, g.name])}
                  title={`Open ${g.name}`}
                >
                  <span style={chevron}>›</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={rowTitle}>{g.name}</div>
                    <div style={rowMeta}>
                      {g.entries.length} entr{g.entries.length === 1 ? 'y' : 'ies'}
                      {inner.groups.length > 0 && ` · ${inner.groups.length} subledger${inner.groups.length === 1 ? '' : 's'}`}
                      {preview.length > 0 && ` · ${preview.slice(0, 3).join(', ')}${preview.length > 3 ? '…' : ''}`}
                    </div>
                  </div>
                  <div style={{ ...rowAmount, ...(g.total < 0 ? { color: colors.success } : {}) }}>{fmtSigned(g.total)}</div>
                </div>
              );
            })}

            {/* Entries that stop at this level — no deeper subledger tag.
                Edit/Delete appear on hover so the list stays readable. */}
            {leaves.map(e => (
              <div
                key={e.id}
                style={{ ...rowBase, background: colors.surfaceMuted, border: `1px solid ${colors.borderMuted}` }}
                onMouseEnter={() => setHoveredEntry(e.id)}
                onMouseLeave={() => setHoveredEntry(prev => (prev === e.id ? null : prev))}
              >
                <span style={{ ...chevron, color: colors.borderMuted }}>•</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={rowTitle}>
                    {e.description || path[path.length - 1] || e.sub_category || activeTab}
                  </div>
                  {e.description && (
                    <div style={rowMeta}>{path.length ? path.join(' / ') : (e.sub_category || activeTab)}</div>
                  )}
                </div>
                <div style={{ ...rowAmount, ...(e.amount < 0 ? { color: colors.success } : {}) }}>{fmtSigned(e.amount)}</div>
                <div style={{ display: 'flex', gap: 4, flex: '0 0 auto', visibility: hoveredEntry === e.id ? 'visible' : 'hidden' }}>
                  <button style={{ ...s.btnSm, background: colors.primaryBg2, color: colors.primaryDark }} onClick={() => onEdit(e)}>Edit</button>
                  <button style={{ ...s.btnSm, background: colors.dangerBg2, color: colors.danger }} onClick={() => onDelete(e.id)}>Delete</button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const FinancialModule: React.FC = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  // Expense Entry and Income Entry each see their own category list —
  // categories tagged EXPENSE/INCOME only appear on their own side;
  // pre-existing BOTH-tagged categories (Rent, Software, Transport, …)
  // still appear on both, since they already have real entries of both
  // transaction types under them.
  const [expenseCategories, setExpenseCategories] = useState<{id: number, name: string}[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<{id: number, name: string}[]>([]);
  const [subcategories, setSubcategories] = useState<{id: number, name: string}[]>([]);
  // Transferred From / Sender / Receiver add-to-list dropdowns (party options),
  // matching Ledger Workspace.
  const [transferredFromOptions, setTransferredFromOptions] = useState<{id: number, name: string}[]>([]);
  const [senderOptions, setSenderOptions] = useState<{id: number, name: string}[]>([]);
  const [receiverOptions, setReceiverOptions] = useState<{id: number, name: string}[]>([]);
  // Subledger 2-5 — "Subledger 1" is the subcategories list above. Each
  // active slot has its own distinct value list, matching Ledger Workspace.
  const [activeSlots, setActiveSlots] = useState<number[]>([]);
  const [subledgerValues, setSubledgerValues] = useState<Record<number, {id: number, name: string}[]>>({});
  const [year, setYear] = useState(now.getFullYear());

  const [pl, setPl] = useState<PLSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadingRowId, setUploadingRowId] = useState<string | null>(null);
  const [batchRows,   setBatchRows]   = useState<any[]>([]);
  const [batchType, setBatchType] = useState<'DEBIT' | 'CREDIT'>('DEBIT');
  // Entry panel state — separate active tab per panel so both can have a
  // category open at the same time.
  const [activeExpenseTab, setActiveExpenseTab] = useState<string | null>(null);
  const [activeIncomeTab, setActiveIncomeTab] = useState<string | null>(null);
  // Per-staff pay behind the payroll lines, for the breakdown panel.
  const [salaryDetail, setSalaryDetail] = useState<any | null>(null);
  // Which payroll line's breakdown is showing on the right (its `key`), plus
  // the name filter inside it.
  const [activeSalaryLine, setActiveSalaryLine] = useState<string | null>(null);
  // Whether the grouped "Management salaries" row is showing its Class/Level rows.
  const [mgmtExpanded, setMgmtExpanded] = useState(false);
  // Revenue / Expenses statements stretch their rows to fill the card.
  const revenueFill = useFillTable();
  const expenseFill = useFillTable();
  // ── Revenue drill-down ──
  // Per-bill nursing rows for the month (the four nursing lines share one feed,
  // bucketed client-side). Non-nursing lines are fetched lazily per bill_type
  // on click — nine eager requests would be wasted on lines nobody opens.
  const [nursingDetail, setNursingDetail] = useState<any[]>([]);
  const [billDetail, setBillDetail] = useState<Record<string, any[]>>({});
  const [billDetailLoading, setBillDetailLoading] = useState(false);
  const [activeRevenueLine, setActiveRevenueLine] = useState<string | null>(null);
  const [revenueSearch, setRevenueSearch] = useState('');
  const [expandedPatient, setExpandedPatient] = useState<string | null>(null);
  const [salarySearch, setSalarySearch] = useState('');
  // Manual purchase/expired deductions on the two billing revenue lines.
  const [adjustments, setAdjustments] = useState<PLManualAdjustment[]>([]);
  const [expandedIncomeLine, setExpandedIncomeLine] = useState<string | null>(null);
  // Consumables credit notes for the month, as a positive figure. Read-only —
  // it comes from consumable_return bills, not from anything typed here.
  const [consumableReturns, setConsumableReturns] = useState(0);
  const [adjDrafts, setAdjDrafts] = useState<Record<string, string>>({});
  const [adjSaving, setAdjSaving] = useState(false);
  // Un-saved "+ Add payment" rows, keyed by line_key. Kept local until the
  // amount is valid, so a stray click can't leave a zero-value orphan.
  const [vendorDrafts, setVendorDrafts] = useState<Record<string, { vendor_name: string; amount: string }[]>>({});
  // Shared vendor name list (both Nurse and Medical Equipment payments).
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  // Memoized so the array identity is stable across renders. SearchableSelect
  // derives its text box from `options.find(id === value)`, and resets the box
  // whenever that lookup changes -- so a fresh array on every keystroke made
  // the field impossible to type in. Keyed by name, not row id, because
  // vendor_name is free text: a payment may name a vendor never added.
  const vendorSelectOptions = useMemo(
    () => vendorOptions.map((o: any) => ({ id: o.name, name: o.name })),
    [vendorOptions]
  );
  // Collapse-after-N for the Revenue/Expenses summary lines
  // Add Category modal — replaces window.prompt; tracks which panel opened it
  const [addCategoryFor, setAddCategoryFor] = useState<'DEBIT' | 'CREDIT' | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  // Rename category modal — holds the category being renamed and the draft name.
  const [renameCategory, setRenameCategory] = useState<{ id: number; name: string } | null>(null);
  const [renameCategoryName, setRenameCategoryName] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  // Delete category modal — password-gated because it also deletes the entries.
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<{ id: number; name: string } | null>(null);
  const [deleteCategoryPassword, setDeleteCategoryPassword] = useState('');
  // Single-entry delete — same password gate as deleting a whole category.
  const [deleteEntryTarget, setDeleteEntryTarget] = useState<{ id: number; entry: Expense | null } | null>(null);
  const [deleteEntryPassword, setDeleteEntryPassword] = useState('');
  const [deletingEntry, setDeletingEntry] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(false);

  // Petty-cash drawer widget (live account balance + top-up / set-balance actions)
  const [account, setAccount] = useState<AccountBalance | null>(null);
  const [drawerModal, setDrawerModal] = useState<null | 'TOPUP' | 'OPENING'>(null);
  const [drawerAmount, setDrawerAmount] = useState('');
  const [drawerNote, setDrawerNote] = useState('');
  // Who moved the top-up and out of which account. Their own party-option
  // lists (PETTY_* kinds) — deliberately not the ledger's Transferred From /
  // Sender / Receiver lists, which hold hundreds of unrelated names.
  const [drawerWithdrawnFrom, setDrawerWithdrawnFrom] = useState('');
  const [drawerSender, setDrawerSender] = useState('');
  const [drawerReceiver, setDrawerReceiver] = useState('');
  const [pettyWithdrawnFromOptions, setPettyWithdrawnFromOptions] = useState<{id: number, name: string}[]>([]);
  const [pettySenderOptions, setPettySenderOptions] = useState<{id: number, name: string}[]>([]);
  const [pettyReceiverOptions, setPettyReceiverOptions] = useState<{id: number, name: string}[]>([]);
  const [savingDrawer, setSavingDrawer] = useState(false);

  const currentUser = useCurrentUser(state => state.user);
  const fetchCurrentUser = useCurrentUser(state => state.fetchCurrentUser);
  useEffect(() => { fetchCurrentUser(); }, [fetchCurrentUser]);

  // Bad debts — manual revenue reductions for the selected month
  const [badDebts, setBadDebts] = useState<BadDebt[]>([]);
  const [badDebtModalOpen, setBadDebtModalOpen] = useState(false);
  const [badDebtAmount, setBadDebtAmount] = useState('');
  const [badDebtNote, setBadDebtNote] = useState('');
  const [badDebtDate, setBadDebtDate] = useState('');
  const [savingBadDebt, setSavingBadDebt] = useState(false);
  // Attribution on a write-off: whose debt, why, and who authorised it. All
  // optional, so an amount-only entry stays a two-field job.
  const [badDebtPatientId, setBadDebtPatientId] = useState('');
  const [badDebtReasonId, setBadDebtReasonId] = useState('');
  const [badDebtApproverId, setBadDebtApproverId] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [reasonOptions, setReasonOptions] = useState<any[]>([]);
  const [approverOptions, setApproverOptions] = useState<any[]>([]);

  const fetchCategories = useCallback(async () => {
    try {
      const [expRes, incRes, subRes, tfRes, sndRes, rcvRes, rsnRes, vndRes, aprRes, patRes,
             pcTfRes, pcSndRes, pcRcvRes] = await Promise.all([
        axios.get(`${API}/api/financial/categories`, { params: { category_type: 'EXPENSE' }, headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/categories`, { params: { category_type: 'INCOME' }, headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/subcategories`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=TRANSFERRED_FROM`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=SENDER`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=RECEIVER`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=BAD_DEBT_REASON`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=VENDOR`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=APPROVER`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/patients/`, { headers: getAuthHeader() }),
        // Petty-cash top-up parties — separate lists from the three above.
        axios.get(`${API}/api/financial/party-options?kind=PETTY_WITHDRAWN_FROM`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=PETTY_SENDER`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/party-options?kind=PETTY_RECEIVER`, { headers: getAuthHeader() }),
      ]);
      setExpenseCategories(expRes.data);
      setIncomeCategories(incRes.data);
      setSubcategories(subRes.data);
      setTransferredFromOptions(tfRes.data);
      setSenderOptions(sndRes.data);
      setReceiverOptions(rcvRes.data);
      setReasonOptions(rsnRes.data);
      setVendorOptions(vndRes.data);
      setApproverOptions(aprRes.data);
      setPettyWithdrawnFromOptions(pcTfRes.data);
      setPettySenderOptions(pcSndRes.data);
      setPettyReceiverOptions(pcRcvRes.data);
      // SearchableSelect keys off `name`, so map the patient label onto it.
      setPatientOptions(
        (Array.isArray(patRes.data) ? patRes.data : []).map((p: any) => ({ id: p.id, name: p.patient_name }))
      );
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const fetchSubledgerValues = async (slot: number) => {
    const res = await axios.get(`${API}/api/financial/subledger-values`, { params: { slot }, headers: getAuthHeader() });
    setSubledgerValues(prev => ({ ...prev, [slot]: res.data }));
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/financial/subledger-slots`, { headers: getAuthHeader() });
        const slots: number[] = (res.data || []).map((s: any) => s.slot).sort((a: number, b: number) => a - b);
        setActiveSlots(slots);
        await Promise.all(slots.map(fetchSubledgerValues));
      } catch {
        // Subledger columns are an enhancement on top of the core ledger —
        // fail quietly so a hiccup here doesn't block the rest of the page.
      }
    })();
  }, []);

  // Attaches to the level above within this row: slot 2 to its Subledger 1,
  // slots 3-5 to the value one slot up.
  const addNewSubledgerValueFor = (slot: number, row?: any) => async (name: string): Promise<SearchableSelectOption | null> => {
    const parentId = row ? parentIdForSlot(row, slot) : null;
    if (row && !parentId) {
      toast.error(slot === 2 ? 'Pick a Subledger 1 first' : `Pick a Subledger ${slot - 1} first`);
      return null;
    }
    try {
      const res = await axios.post(
        `${API}/api/financial/subledger-values`,
        {
          slot,
          name,
          ...(parentId
            ? (slot === 2
                ? { parent_subcategory_id: Number(parentId) }
                : { parent_value_id: Number(parentId) })
            : {}),
        },
        { headers: getAuthHeader() },
      );
      await fetchSubledgerValues(slot);
      toast.success(row
        ? `Added under ${parentNameForRow(row, slot) || `Subledger ${slot - 1}`}`
        : `Subledger ${slot} value added!`);
      return { id: res.data.id, name: res.data.name };
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add value');
      return null;
    }
  };

  // Subledger columns (slots 2-5) are shared workspace-wide with the full ledger.
  const MAX_SUBLEDGER_SLOT = 5;

  const reloadSubledgerSlots = async () => {
    const res = await axios.get(`${API}/api/financial/subledger-slots`, { headers: getAuthHeader() });
    const slots: number[] = (res.data || []).map((s: any) => s.slot).sort((a: number, b: number) => a - b);
    setActiveSlots(slots);
    await Promise.all(slots.map(fetchSubledgerValues));
  };

  const addSubledgerColumn = async () => {
    try {
      const res = await axios.post(`${API}/api/financial/subledger-slots`, {}, { headers: getAuthHeader() });
      if (res.data?.slot) {
        toast.success(`Subledger ${res.data.slot} added`);
        await reloadSubledgerSlots();
      } else {
        toast.error(`All ${MAX_SUBLEDGER_SLOT} Subledger columns are already active`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add Subledger column');
    }
  };

  const removeSubledgerColumn = async (slot: number) => {
    if (!window.confirm(`Remove Subledger ${slot}? This also deletes any values defined under it.`)) return;
    try {
      await axios.delete(`${API}/api/financial/subledger-slots/${slot}`, { headers: getAuthHeader() });
      toast.success(`Subledger ${slot} removed`);
      await reloadSubledgerSlots();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || `Failed to remove Subledger ${slot}`);
    }
  };

  // ── Fetch P&L (ledger + read-through payroll/billing aggregates) ──
  const fetchPL = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Raw transactions from the ledger table
      const txRes = await axios.get(`${API}/api/financial/transactions`, {
        params: { month, year },
        headers: getAuthHeader(),
      });

      // 2. Salary totals, read live from the payroll module (nurse, physio,
      // management)
      const salaryRes = await axios.get(`${API}/api/financial/salary-summary`, {
        params: { month, year },
        headers: getAuthHeader(),
      });

      // 2b. Per-staff breakdown behind those totals, so clicking a salary line
      // can show who it's made of. Same endpoint the Excel export uses, so the
      // panel and the sheet can't disagree. Fetched defensively — a failure
      // here must not take the whole P&L down with it.
      try {
        const detailRes = await axios.get(`${API}/api/financial/salary-detail`, {
          params: { month, year },
          headers: getAuthHeader(),
        });
        setSalaryDetail(detailRes.data);
      } catch (err) {
        // Not fatal — the P&L totals still render; only the per-staff
        // breakdown is unavailable. Logged so a silent failure here doesn't
        // look like "the salary rows just aren't clickable".
        console.error('salary-detail failed; salary lines will not be clickable', err);
        setSalaryDetail(null);
      }

      // Per-bill nursing rows, for the revenue drill-down. Same accrual basis as
      // the nursing P&L lines, so bucketing these by classifyNursingShift sums
      // back to them exactly. Defensive for the same reason as above: without it
      // the nursing lines are simply not clickable.
      try {
        const nurseRes = await axios.get(`${API}/api/billing/nursing-sales-detail`, {
          params: { month, year },
          headers: getAuthHeader(),
        });
        setNursingDetail(Array.isArray(nurseRes.data) ? nurseRes.data : []);
      } catch (err) {
        console.error('nursing-sales-detail failed; nursing lines will not be clickable', err);
        setNursingDetail([]);
      }

      // 3. Billing income, read live from the billing module, grouped by
      // service category (bill_type) — nursing is excluded server-side now,
      // since it's recognized separately below on an accrual basis.
      const revenueRes = await axios.get(`${API}/api/billing/revenue-summary`, {
        params: { month, year },
        headers: getAuthHeader(),
      });

      // 3b. Nursing income, billed amount (accrual), broken down by shift
      // type — payments are recorded per-bill not per-line-item, so a
      // shift-type breakdown isn't derivable from collections at all.
      const nursingRes = await axios.get(`${API}/api/billing/nursing-revenue-by-shift`, {
        params: { month, year },
        headers: getAuthHeader(),
      });

      // 3c. Nursing discount, auto-summed from the bills' discount_amount. Nursing
      // revenue above is gross, so this shows as its own deduction line.
      const nursingDiscountRes = await axios.get(`${API}/api/billing/nursing-discount`, {
        params: { month, year },
        headers: getAuthHeader(),
      }).catch(() => ({ data: { amount: 0 } }));

      // 3d. Misc line items embedded in other bills, pulled out server-side so they
      // show on their own Misc row instead of inflating nursing.
      const miscLineRes = await axios.get(`${API}/api/billing/misc-line-revenue`, {
        params: { month, year },
        headers: getAuthHeader(),
      }).catch(() => ({ data: { amount: 0 } }));

      // 3c. Bad debts — manual revenue reductions for this month. Fetched
      // defensively so a missing table (before the migration is run) doesn't
      // break the rest of the P&L.
      let badDebtList: BadDebt[] = [];
      try {
        const bdRes = await axios.get(`${API}/api/financial/bad-debts`, {
          params: { month, year },
          headers: getAuthHeader(),
        });
        badDebtList = Array.isArray(bdRes.data) ? bdRes.data : [];
      } catch { /* table may not exist yet — treat as no bad debts */ }
      setBadDebts(badDebtList);

      // 3d. Manual purchase/expired deductions on the Consumables and Medical
      // Equipment (Rental) revenue lines. Defensive for the same reason as bad
      // debts: the frontend may ship before the table exists, and a 404 here
      // must not take the whole P&L down with it.
      let adjList: PLManualAdjustment[] = [];
      try {
        const adjRes = await axios.get(`${API}/api/financial/pl-manual-adjustments`, {
          params: { month, year },
          headers: getAuthHeader(),
        });
        adjList = Array.isArray(adjRes.data) ? adjRes.data : [];
      } catch { /* table may not exist yet — treat as no adjustments */ }
      setAdjustments(adjList);

      // 4. Extract the transactions array safely
      const transactions = Array.isArray(txRes.data) ? txRes.data : (txRes.data.data || []);

      // 5. Transform the ledger transaction schema into the "Expense" interface
      const mapTxn = (t: any) => ({
        id: t.id,
        month: month,
        year: year,
        category: t.ledger_category_name || 'Uncategorized',
        sub_category: t.ledger_subcategory_name || '',
        description: t.particular || t.remarks || '',
        amount: parseFloat(t.amount) || 0,
        transaction_date: t.transaction_date,
        ledger_category_id: t.ledger_category_id,
        ledger_subcategory_id: t.ledger_subcategory_id,
        ledger_subledger_2_id: t.ledger_subledger_2_id,
        ledger_subledger_3_id: t.ledger_subledger_3_id,
        ledger_subledger_4_id: t.ledger_subledger_4_id,
        ledger_subledger_5_id: t.ledger_subledger_5_id,
        // Names for slots 2-5, so the entry panel can nest an entry under its
        // full chain instead of only its Subledger 1.
        subledger_names: [2, 3, 4, 5].map(n => t[`ledger_subledger_${n}_name`] || ''),
        transferred_from_id: t.transferred_from_id,
        sender_id: t.sender_id,
        receiver_id: t.receiver_id,
        payment_mode: t.payment_mode,
        remarks: t.remarks,
        proof: t.proof,
        // Read only by the per-ledger Excel download.
        ledger_no: t.ledger_no,
        particular: t.particular,
        transferred_from_name: t.transferred_from_name,
        sender_name: t.sender_name,
        receiver_name: t.receiver_name,
        source: 'ledger' as const,
      });

      // Returns (CREDIT rows from the Ledger Workspace) belong in the expense
      // panel, not the income one: they're money coming back on something
      // already spent, so they net off the expense rather than counting as
      // revenue. Carried as a negative amount because every total here is a
      // plain sum — that makes a return reduce its category, its tab and
      // EBITDA without any of those needing to know about transaction types.
      const ledgerExpenses = transactions
        .filter((t: any) => t.transaction_type === 'DEBIT' || t.transaction_type === 'CREDIT')
        .map((t: any) => {
          const row = mapTxn(t);
          return t.transaction_type === 'CREDIT' ? { ...row, amount: -row.amount } : row;
        });
      // Empty while returns own the CREDIT rows above — counting them here too
      // would book the same money as both a negative expense and income. If
      // manual income entry is ever restored (INCOME_ENTRY_ENABLED), these two
      // lists need a real way to tell an income row from a return, since
      // transaction_type alone no longer distinguishes them.
      const ledgerIncome: any[] = [];

      // 6. Salary items (payroll) → Expense-shaped read-only rows, one per role
      const salaryExpenses = (salaryRes.data.items || []).map((item: any, idx: number) => ({
        id: -1000 - idx,
        month, year,
        category: item.label,
        sub_category: null,
        description: null,
        amount: item.amount,
        source: 'payroll' as const,
      }));

      // 7. Billing items → Expense-shaped read-only Revenue rows, one per bill_type
      // Non-zero, not just positive: credit notes (Consumables Return) carry a
      // negative total and have to reduce revenue. Dropping them would overstate
      // sales by the full value of everything ever returned.
      const billingIncome = (revenueRes.data || [])
        .filter((item: any) => item.amount !== 0)
        .map((item: any, idx: number) => ({
          id: -2000 - idx,
          month, year,
          // Credit notes carry a negative total and belong to the line they
          // reverse, so they are filed under Consumables rather than as a line
          // of their own — buildLines group-sums by category, which nets them
          // off with no further arithmetic. Their own figure is recovered for
          // the expander from consumableReturnTotal below.
          category: item.bill_type === CONSUMABLE_RETURN_TYPE
            ? BILL_TYPE_LABEL.consumables
            : (BILL_TYPE_LABEL[item.bill_type] || item.bill_type || 'Other billing income'),
          sub_category: null,
          description: null,
          amount: item.amount,
          source: 'billing' as const,
        }));

      // The returns total on its own, for the Consumables expander. Stored as a
      // POSITIVE number: every other deduction there is positive and subtracted,
      // so keeping the sign convention consistent means the footer formula needs
      // no special case for this one.
      setConsumableReturns(Math.abs(
        (revenueRes.data || [])
          .filter((i: any) => i.bill_type === CONSUMABLE_RETURN_TYPE)
          .reduce((sum: number, i: any) => sum + (Number(i.amount) || 0), 0)
      ));

      // Always surface a Misc line, even at ₹0 — the client wants it on every
      // statement, not only in months that happened to have a misc bill (the
      // amount !== 0 filter above, plus the backend omitting empty types, would
      // otherwise hide it).
      const MISC_LABEL = BILL_TYPE_LABEL['misc'] || 'Misc';
      if (!billingIncome.some((r: any) => r.category === MISC_LABEL)) {
        billingIncome.push({
          id: -2999,
          month, year,
          category: MISC_LABEL,
          sub_category: null,
          description: null,
          amount: 0,
          source: 'billing' as const,
        });
      }

      // 7b. Nursing income, split by shift type into the buckets the client's
      // P&L uses — 8 Hrs / 12 Dual / 12 Single / 24 Hrs — plus an "Other" catch-
      // all (home-visit bills etc.). See classifyNursingShift(). Only non-zero
      // buckets are emitted, so empty ones never clutter the statement.
      const nursingBuckets: Record<string, number> = {};
      (nursingRes.data || []).forEach((item: any) => {
        const amt = item.amount || 0;
        if (amt <= 0) return;
        const bucket = classifyNursingShift(item.bill_type, item.shift_type);
        nursingBuckets[bucket] = (nursingBuckets[bucket] || 0) + amt;
      });
      const nursingIncome = NURSING_SHIFT_BUCKETS
        .filter(b => nursingBuckets[b])
        .map((b, idx) => ({
          id: -3000 - idx,
          month, year,
          category: nursingCategory(b),
          sub_category: null,
          description: null,
          amount: nursingBuckets[b],
          source: 'billing' as const,
        }));

      // 7b-ii. Nursing discount → a negative Revenue line directly above Nurse
      // Vendor Payments, auto-summed from the nursing bills. Nursing revenue is
      // billed gross, so this is what nets it down to the discounted figure. Always
      // shown, even at ₹0, so the line is a permanent fixture of the statement.
      const nursingDiscountAmt = Number(nursingDiscountRes.data?.amount) || 0;
      const nursingDiscountIncome = [{
        id: -3500,
        month, year,
        category: NURSING_DISCOUNT_CATEGORY,
        sub_category: null,
        description: null,
        amount: -nursingDiscountAmt,
        source: 'billing' as const,
      }];

      // 7b-iii. Misc-within-bills → its own Revenue line (embedded misc items,
      // already pulled out of nursing server-side). Always shown, even at ₹0.
      const miscLineAmt = Number(miscLineRes.data?.amount) || 0;
      const miscLineIncome = [{
        id: -3600,
        month, year,
        category: MISC_LINE_CATEGORY,
        sub_category: null,
        description: null,
        amount: miscLineAmt,
        source: 'billing' as const,
      }];

      // 7c. Bad debts → a single negative Revenue line, so Total Sales becomes
      // net revenue and EBITDA drops accordingly. Never a ledger transaction, so
      // the petty-cash drawer is unaffected.
      const badDebtTotal = badDebtList.reduce((s, b) => s + (Number(b.amount) || 0), 0);
      const badDebtIncome = badDebtTotal > 0 ? [{
        id: -4000,
        month, year,
        category: BAD_DEBT_CATEGORY,
        sub_category: null,
        description: null,
        amount: -badDebtTotal,
        source: 'baddebt' as const,
      }] : [];

      const mappedExpenses = [...ledgerExpenses, ...salaryExpenses];
      // 7d. Manual deductions -> negative rows under the SAME category string as
      // the billing line they reduce, so buildLines' existing group-sum nets
      // them off and totalSales below picks the net up with no further work.
      // source:'billing' deliberately matches the row they net against —
      // buildLines takes a line's source from whichever row sorts first, so a
      // different source here would make the line's source nondeterministic.
      const manualDeductionIncome = adjList
        .filter(a => Number(a.amount) > 0)
        .map((a, idx) => ({
          id: -5000 - idx,
          month, year,
          category: REVENUE_LINE_LABEL[a.line_key] || a.line_key,
          sub_category: null,
          description: a.kind,
          amount: -Number(a.amount),
          source: 'billing' as const,
        }));

      const mappedIncome = [...ledgerIncome, ...billingIncome, ...nursingIncome, ...nursingDiscountIncome, ...miscLineIncome, ...badDebtIncome, ...manualDeductionIncome];

      // 8. total_sales / total_expenses / EBITDA are derived from the same
      // merged categories rendered on screen (not a separate backend figure),
      // so the statement total can never drift from the sum of its own lines.
      const totalSales = mappedIncome.reduce((s: number, e: any) => s + e.amount, 0);
      const totalExpenses = mappedExpenses.reduce((s: number, e: any) => s + e.amount, 0);
      const ebitda = totalSales - totalExpenses;

      setPl({
        month,
        year,
        expenses: mappedExpenses,
        income: mappedIncome,
        total_sales: totalSales,
        total_expenses: totalExpenses,
        ebitda,
        ebitda_percent: totalSales > 0 ? (ebitda / totalSales) * 100 : 0,
      });

    } catch (err) {
      console.error(err);
      toast.error('Failed to load P&L data');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { fetchPL(); }, [fetchPL]);

  // A salary breakdown belongs to the month it was opened from — close it when
  // the period changes rather than leaving last month's staff on screen.
  useEffect(() => {
    setActiveSalaryLine(null); setSalarySearch('');
    setExpandedIncomeLine(null); setAdjDrafts({});
    // Bills are per-month: drop the open panel and the cache with it.
    setActiveRevenueLine(null); setRevenueSearch(''); setExpandedPatient(null);
    setBillDetail({});
  }, [month, year]);

  // ── Petty-cash drawer: same month-scoped figures as the /financial/petty-cash
  // screen, for whichever month/year the P&L page has selected — so the two
  // screens always agree instead of one showing an all-time running total.
  const fetchAccount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/financial/petty-cash/summary`, {
        headers: getAuthHeader(), params: { month, year },
      });
      // expenses shown here is net (cash out − cash in) so Opening + Top-ups −
      // Expenses = Balance still holds, matching expected_on_hand exactly.
      const cashOut = Number(res.data.cash_out) || 0;
      const cashIn = Number(res.data.cash_in) || 0;
      setAccount({
        opening: Number(res.data.opening) || 0,
        topups: Number(res.data.topups) || 0,
        expenses: cashOut - cashIn,
        balance: Number(res.data.expected_on_hand) || 0,
      });
    } catch {
      // Non-fatal — the drawer just shows a dash until it loads.
    }
  }, [month, year]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  const openDrawerModal = (type: 'TOPUP' | 'OPENING') => {
    setDrawerAmount('');
    setDrawerNote('');
    setDrawerWithdrawnFrom('');
    setDrawerSender('');
    setDrawerReceiver('');
    setDrawerModal(type);
  };

  const saveDrawerEvent = async () => {
    const amt = parseFloat(drawerAmount);
    if (isNaN(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    setSavingDrawer(true);
    try {
      await axios.post(`${API}/api/financial/petty-cash/events`, {
        event_date: new Date().toISOString().split('T')[0],
        event_type: drawerModal,
        amount: amt,
        note: drawerNote || null,
        // Collected on the top-up form only; a Set Balance leaves them null.
        withdrawn_from_id: drawerWithdrawnFrom ? parseInt(drawerWithdrawnFrom) : null,
        sender_id: drawerSender ? parseInt(drawerSender) : null,
        receiver_id: drawerReceiver ? parseInt(drawerReceiver) : null,
      }, { headers: getAuthHeader() });
      toast.success(drawerModal === 'TOPUP' ? 'Top-up recorded' : 'Balance set');
      setDrawerModal(null);
      fetchAccount();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingDrawer(false);
    }
  };

  // ── Bad debts (manual revenue reductions) ──
  const openBadDebtModal = () => {
    setBadDebtAmount('');
    setBadDebtNote('');
    setBadDebtDate(new Date().toISOString().split('T')[0]);
    setBadDebtPatientId('');
    setBadDebtReasonId('');
    setBadDebtApproverId('');
    setBadDebtModalOpen(true);
  };

  const saveBadDebt = async () => {
    const amt = parseFloat(badDebtAmount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSavingBadDebt(true);
    try {
      await axios.post(`${API}/api/financial/bad-debts`, {
        entry_date: badDebtDate || new Date().toISOString().split('T')[0],
        amount: amt,
        note: badDebtNote || null,
        patient_id: badDebtPatientId ? parseInt(badDebtPatientId) : null,
        reason_id: badDebtReasonId ? parseInt(badDebtReasonId) : null,
        approved_by_id: badDebtApproverId ? parseInt(badDebtApproverId) : null,
      }, { headers: getAuthHeader() });
      toast.success('Bad debt added');
      setBadDebtAmount('');
      setBadDebtNote('');
      setBadDebtPatientId('');
      setBadDebtReasonId('');
      setBadDebtApproverId('');
      fetchPL();  // refetches the list + rebuilds the negative Revenue line
    } catch {
      toast.error('Failed to add bad debt');
    } finally {
      setSavingBadDebt(false);
    }
  };

  const deleteBadDebt = async (id: number) => {
    try {
      await axios.delete(`${API}/api/financial/bad-debts/${id}`, { headers: getAuthHeader() });
      toast.success('Bad debt removed');
      fetchPL();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const categoriesFor = (type: 'DEBIT' | 'CREDIT') => type === 'CREDIT' ? incomeCategories : expenseCategories;

  const createEmptyRow = (type: 'DEBIT' | 'CREDIT') => {
    const activeTab = type === 'CREDIT' ? activeIncomeTab : activeExpenseTab;
    return {
      id:                    'new_' + Date.now(),
      isNew:                 true,
      transaction_date:      new Date().toISOString().split('T')[0],
      transaction_type:      type,
      ledger_category_id:    categoriesFor(type).find(c => c.name === activeTab)?.id || '',
      ledger_subcategory_id: '',
      ledger_subledger_2_id: '',
      ledger_subledger_3_id: '',
      ledger_subledger_4_id: '',
      ledger_subledger_5_id: '',
      transferred_from_id:   '',
      sender_id:             '',
      receiver_id:           '',
      particular:            '',
      amount:                '',
      payment_mode:          'CASH',
      paid_from_petty_cash:  false,
      remarks:               '',
      proof:                 null,
    };
  };

  const openBatchModal = (type: 'DEBIT' | 'CREDIT') => {
    const activeTab = type === 'CREDIT' ? activeIncomeTab : activeExpenseTab;
    if (!activeTab || !pl) return;

    const grouped = groupByCategory(type === 'CREDIT' ? pl.income : pl.expenses);
    const existing = (grouped[activeTab] || []).map(e => withOriginalSnapshot({
      id:                    e.id,
      isNew:                 false,
      transaction_date:      (e as any).transaction_date
                               ? (e as any).transaction_date.split('T')[0]
                               : new Date().toISOString().split('T')[0],
      // Negative rows in the expense panel are returns and keep their CREDIT
      // type, so a batch edit can't quietly turn one back into an expense.
      transaction_type:      (type === 'DEBIT' && e.amount < 0 ? 'CREDIT' : type) as 'DEBIT' | 'CREDIT',
      ledger_category_id:    (e as any).ledger_category_id || categoriesFor(type).find(c => c.name === activeTab)?.id || '',
      ledger_subcategory_id: (e as any).ledger_subcategory_id || '',
      ledger_subledger_2_id: (e as any).ledger_subledger_2_id || '',
      ledger_subledger_3_id: (e as any).ledger_subledger_3_id || '',
      ledger_subledger_4_id: (e as any).ledger_subledger_4_id || '',
      ledger_subledger_5_id: (e as any).ledger_subledger_5_id || '',
      transferred_from_id:   (e as any).transferred_from_id || '',
      sender_id:             (e as any).sender_id || '',
      receiver_id:           (e as any).receiver_id || '',
      particular:            e.description || '',
      // Magnitude only — the sign lives in transaction_type, and the amount
      // field is a plain positive number.
      amount:                String(Math.abs(e.amount)),
      payment_mode:          (e as any).payment_mode || 'CASH',
      paid_from_petty_cash:  (e as any).paid_from_petty_cash || false,
      remarks:               (e as any).remarks || '',
      proof:                 (e as any).proof   || null,
    }));

    setBatchType(type);
    setBatchRows(existing.length > 0 ? [...existing, createEmptyRow(type)] : [createEmptyRow(type)]);
    setIsModalOpen(true);
  };

  // ── Manual revenue deductions ──────────────────────────────────────────────

  // Current figure for one cell, 0 when nothing has been entered yet.
  // Sums rather than finds: vendor_payment has several rows per line, and the
  // singletons have at most one, so summing is correct for both.
  const adjAmount = (lineKey: string, kind: AdjustmentKind): number =>
    adjustments
      .filter(a => a.line_key === lineKey && a.kind === kind)
      .reduce((sum, a) => sum + (Number(a.amount) || 0), 0);

  // The individual payments behind a vendor_payment total, in insertion order.
  const vendorPaymentsFor = (lineKey: string): PLManualAdjustment[] =>
    adjustments.filter(a => a.line_key === lineKey && a.kind === 'vendor_payment');

  // Total deducted from a line, for the collapsed row's sub-label.
  const adjDeducted = (lineKey: string): number =>
    (LINE_ADJUSTMENTS[lineKey] || []).reduce((sum, k) => sum + adjAmount(lineKey, k), 0);

  const addVendorPayment = async (lineKey: string, amount: number, vendorName: string) => {
    setAdjSaving(true);
    try {
      await axios.post(`${API}/api/financial/pl-manual-adjustments/vendor-payments`,
        { month, year, line_key: lineKey, amount, vendor_name: vendorName.trim() || null },
        { headers: getAuthHeader() });
      toast.success(vendorName.trim()
        ? `Payment to ${vendorName.trim()} saved — ${fmt(amount)}`
        : `Vendor payment saved — ${fmt(amount)}`);
      await fetchPL();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not save the payment');
    } finally {
      setAdjSaving(false);
    }
  };

  const updateVendorPayment = async (rowId: number, patch: { amount?: number; vendor_name?: string | null }) => {
    setAdjSaving(true);
    try {
      await axios.patch(`${API}/api/financial/pl-manual-adjustments/vendor-payments/${rowId}`,
        patch, { headers: getAuthHeader() });
      await fetchPL();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not update the payment');
    } finally {
      setAdjSaving(false);
    }
  };

  const deleteVendorPayment = async (rowId: number) => {
    setAdjSaving(true);
    try {
      await axios.delete(`${API}/api/financial/pl-manual-adjustments/vendor-payments/${rowId}`,
        { headers: getAuthHeader() });
      toast.success('Payment removed');
      await fetchPL();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not remove the payment');
    } finally {
      setAdjSaving(false);
    }
  };

  const saveAdjustment = async (lineKey: string, kind: AdjustmentKind, raw: string) => {
    const trimmed = raw.trim();
    const amount = trimmed === '' ? 0 : parseFloat(trimmed);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    // Nothing changed — skip the round trip so tabbing through the two inputs
    // doesn't fire two pointless writes.
    if (amount === adjAmount(lineKey, kind)) return;
    setAdjSaving(true);
    try {
      await axios.put(`${API}/api/financial/pl-manual-adjustments`,
        { month, year, line_key: lineKey, kind, amount },
        { headers: getAuthHeader() });
      // From the shared label map, not a ternary — a new kind would otherwise be
      // announced as "Expired amount" by the fall-through.
      const what = ADJUSTMENT_LABEL[kind] || 'Amount';
      toast.success(amount === 0
        ? `${what} cleared`
        : `${what} saved — ${fmt(amount)}`);
      await fetchPL();
      // Deliberately NOT fetchAccount(): these never touch
      // financial_transactions, so the petty-cash drawer cannot have moved.
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not save the amount');
    } finally {
      setAdjSaving(false);
    }
  };

  // Deleting an entry is password-gated (the backend verifies it), so this
  // opens the confirm dialog rather than deleting straight away.
  const deleteExpense = (id: number) => {
    const entry = (pl?.expenses || []).concat(pl?.income || []).find(e => e.id === id) || null;
    setDeleteEntryTarget({ id, entry });
    setDeleteEntryPassword('');
  };

  const submitDeleteEntry = async () => {
    if (!deleteEntryTarget) return;
    if (!deleteEntryPassword) { toast.error('Enter your password to confirm'); return; }
    setDeletingEntry(true);
    try {
      await axios.delete(`${API}/api/financial/transactions/${deleteEntryTarget.id}`, {
        headers: getAuthHeader(),
        data: { password: deleteEntryPassword },
      });
      toast.success('Entry deleted');
      setDeleteEntryTarget(null);
      setDeleteEntryPassword('');
      fetchPL();
      fetchAccount();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setDeletingEntry(false);
    }
  };

  const startEdit = (e: Expense, type: 'DEBIT' | 'CREDIT') => {
    // A negative amount in the expense panel is a return (a CREDIT row), so
    // the row keeps its own type rather than taking the panel's — editing one
    // from here would otherwise save it back as an ordinary expense.
    const isReturn = type === 'DEBIT' && e.amount < 0;
    const rowType: 'DEBIT' | 'CREDIT' = isReturn ? 'CREDIT' : type;
    setBatchType(rowType);
    setBatchRows([withOriginalSnapshot({
      id:                    e.id,
      isNew:                 false,
      transaction_date:      (e as any).transaction_date
                               ? (e as any).transaction_date.split('T')[0]
                               : new Date().toISOString().split('T')[0],
      transaction_type:      rowType,
      ledger_category_id:    categoriesFor(type).find(c => c.name === e.category)?.id || '',
      ledger_subcategory_id: (e as any).ledger_subcategory_id || '',
      ledger_subledger_2_id: (e as any).ledger_subledger_2_id || '',
      ledger_subledger_3_id: (e as any).ledger_subledger_3_id || '',
      ledger_subledger_4_id: (e as any).ledger_subledger_4_id || '',
      ledger_subledger_5_id: (e as any).ledger_subledger_5_id || '',
      transferred_from_id:   (e as any).transferred_from_id || '',
      sender_id:             (e as any).sender_id || '',
      receiver_id:           (e as any).receiver_id || '',
      particular:            e.description || '',
      // Magnitude only — the sign lives in transaction_type, and the amount
      // field is a plain positive number.
      amount:                String(Math.abs(e.amount)),
      payment_mode:          (e as any).payment_mode || 'CASH',
      paid_from_petty_cash:  (e as any).paid_from_petty_cash || false,
      remarks:               (e as any).remarks || '',
      proof:                 (e as any).proof || null,
    })]);
    setIsModalOpen(true);
  };

  const submitNewCategory = async () => {
    const name = newCategoryName.trim();
    const type = addCategoryFor;
    if (!name || !type) return;
    setSavingCategory(true);
    try {
      await axios.post(
        `${API}/api/financial/categories`,
        { name, category_type: type === 'CREDIT' ? 'INCOME' : 'EXPENSE' },
        { headers: getAuthHeader() },
      );
      toast.success('Category added!');
      await fetchCategories();
      (type === 'CREDIT' ? setActiveIncomeTab : setActiveExpenseTab)(name);
      setAddCategoryFor(null);
      setNewCategoryName('');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add category');
    } finally {
      setSavingCategory(false);
    }
  };

  const submitRenameCategory = async () => {
    if (!renameCategory) return;
    const name = renameCategoryName.trim();
    if (!name || name === renameCategory.name) { setRenameCategory(null); return; }
    setSavingRename(true);
    try {
      await axios.patch(
        `${API}/api/financial/categories/${renameCategory.id}`,
        { name },
        { headers: getAuthHeader() },
      );
      toast.success('Category renamed');
      await fetchCategories();
      // Keep the renamed category selected under whichever panel it belongs to.
      if (activeExpenseTab === renameCategory.name) setActiveExpenseTab(name);
      if (activeIncomeTab === renameCategory.name) setActiveIncomeTab(name);
      setRenameCategory(null);
      setRenameCategoryName('');
      fetchPL();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to rename category');
    } finally {
      setSavingRename(false);
    }
  };

  const submitDeleteCategory = async () => {
    if (!deleteCategoryTarget) return;
    if (!deleteCategoryPassword) { toast.error('Enter your password to confirm'); return; }
    setDeletingCategory(true);
    try {
      await axios.delete(`${API}/api/financial/categories/${deleteCategoryTarget.id}`, {
        headers: getAuthHeader(),
        data: { password: deleteCategoryPassword },
      });
      toast.success('Category deleted');
      // Clear selection if the deleted category was active.
      if (activeExpenseTab === deleteCategoryTarget.name) setActiveExpenseTab(null);
      if (activeIncomeTab === deleteCategoryTarget.name) setActiveIncomeTab(null);
      setDeleteCategoryTarget(null);
      setDeleteCategoryPassword('');
      await fetchCategories();
      fetchPL();
      fetchAccount();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to delete category');
    } finally {
      setDeletingCategory(false);
    }
  };

  // Same "add new" affordance, used by the batch entry modal's category/
  // subcategory pickers (SearchableSelect) instead of the dedicated modal above.
  const addCategoryInline = async (name: string, type: 'DEBIT' | 'CREDIT'): Promise<SearchableSelectOption | null> => {
    const category_type = type === 'CREDIT' ? 'INCOME' : 'EXPENSE';
    try {
      await axios.post(`${API}/api/financial/categories`, { name, category_type }, { headers: getAuthHeader() });
      toast.success('Category added!');
      await fetchCategories();
      const res = await axios.get(`${API}/api/financial/categories`, { params: { category_type }, headers: getAuthHeader() });
      return res.data.find((c: any) => c.name.toLowerCase() === name.toLowerCase()) || null;
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add category');
      return null;
    }
  };

  // ── Cascading subledger options (per batch row) ────────────────────────────
  // Functions of the row rather than memos: every row in the batch modal has
  // its own independent chain. Filtered from the lists already loaded — one
  // fetch per row per keystroke would be a request storm for no benefit.
  //
  // `withCurrent` keeps an entry opened for edit readable when its saved value
  // no longer sits under the chosen parent (SearchableSelect resolves its
  // label from `options`, so it would otherwise render blank).
  const withCurrent = (scoped: any[], all: any[], currentId: any) => {
    if (!currentId) return scoped;
    if (scoped.some(o => String(o.id) === String(currentId))) return scoped;
    const current = all.find(o => String(o.id) === String(currentId));
    return current ? [current, ...scoped] : scoped;
  };

  const subcategoriesForRow = (row: any) => withCurrent(
    row?.ledger_category_id
      ? subcategories.filter((s: any) => String(s.ledger_category_id) === String(row.ledger_category_id))
      : [],
    subcategories,
    row?.ledger_subcategory_id,
  );

  // Parent of slot 2 is the Subledger 1 row; of 3-5, the value one slot up.
  const parentIdForSlot = (row: any, slot: number) =>
    slot === 2 ? row?.ledger_subcategory_id : row?.[`ledger_subledger_${slot - 1}_id`];

  const subledgerValuesForRow = (row: any, slot: number) => {
    const all = subledgerValues[slot] || [];
    const parentId = parentIdForSlot(row, slot);
    const scoped = parentId
      ? all.filter((v: any) => String(
          slot === 2 ? v.parent_subcategory_id : v.parent_value_id
        ) === String(parentId))
      : [];
    return withCurrent(scoped, all, row?.[`ledger_subledger_${slot}_id`]);
  };

  // Name of the parent a new value would attach to, for the "Add X to <parent>"
  // footer. slot 1 means the Subledger 1 dropdown, whose parent is the ledger.
  const parentNameForRow = (row: any, slot: number): string => {
    if (slot === 1) {
      // Categories are split by panel here, so check both rather than assuming
      // the row's type matches the modal's current one.
      return [...expenseCategories, ...incomeCategories]
        .find((c: any) => String(c.id) === String(row?.ledger_category_id))?.name || '';
    }
    const parentId = parentIdForSlot(row, slot);
    if (!parentId) return '';
    if (slot === 2) {
      return subcategories.find((s: any) => String(s.id) === String(parentId))?.name || '';
    }
    return (subledgerValues[slot - 1] || [])
      .find((v: any) => String(v.id) === String(parentId))?.name || '';
  };

  // Created under the row's own ledger. Returns the created row directly —
  // several ledgers can hold the same name, so a name lookup is ambiguous.
  const addNewSubcategoryFM = (row: any) => async (name: string): Promise<SearchableSelectOption | null> => {
    const catId = row?.ledger_category_id;
    if (!catId) {
      toast.error('Pick a ledger first');
      return null;
    }
    try {
      const res = await axios.post(
        `${API}/api/financial/subcategories`,
        { name, ledger_category_id: Number(catId) },
        { headers: getAuthHeader() },
      );
      const subRes = await axios.get(`${API}/api/financial/subcategories`, { headers: getAuthHeader() });
      setSubcategories(subRes.data);
      toast.success(`Added under ${parentNameForRow(row, 1) || 'this ledger'}`);
      return { id: res.data.id, name: res.data.name };
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add subcategory');
      return null;
    }
  };

  // Shared add-to-list handler for the Transferred From / Sender / Receiver
  // dropdowns — one discriminated /party-options list per `kind`.
  const addNewParty = (kind: string, setOptions: (opts: any[]) => void, label: string) =>
    async (name: string): Promise<SearchableSelectOption | null> => {
      try {
        await axios.post(`${API}/api/financial/party-options`, { kind, name }, { headers: getAuthHeader() });
        const res = await axios.get(`${API}/api/financial/party-options?kind=${kind}`, { headers: getAuthHeader() });
        setOptions(res.data);
        toast.success(`${label} added!`);
        return res.data.find((o: any) => o.name.toLowerCase() === name.toLowerCase()) || null;
      } catch (err: any) {
        toast.error(err?.response?.data?.detail || `Failed to add ${label.toLowerCase()}`);
        return null;
      }
    };
  const addNewTransferredFrom = addNewParty('TRANSFERRED_FROM', setTransferredFromOptions, 'Transferred From');
  const addNewSender = addNewParty('SENDER', setSenderOptions, 'Sender');
  const addNewReceiver = addNewParty('RECEIVER', setReceiverOptions, 'Receiver');
  const addNewBadDebtReason = addNewParty('BAD_DEBT_REASON', setReasonOptions, 'Reason');
  // One shared list across Nurse and Medical Equipment payments — the same
  // vendor may supply both, and a shared list is rarely wrong.
  const addNewVendor = addNewParty('VENDOR', setVendorOptions, 'Vendor');
  const addNewApprover = addNewParty('APPROVER', setApproverOptions, 'Approver');
  // Petty-cash top-up parties — their own lists, kept clear of the ledger's.
  const addNewPettyWithdrawnFrom = addNewParty('PETTY_WITHDRAWN_FROM', setPettyWithdrawnFromOptions, 'Account');
  const addNewPettySender = addNewParty('PETTY_SENDER', setPettySenderOptions, 'Sender');
  const addNewPettyReceiver = addNewParty('PETTY_RECEIVER', setPettyReceiverOptions, 'Receiver');

  // ── Excel export ──
  const downloadExcel = async () => {
    if (!pl) return;
    const data = pl;   // narrowed; keeps nested closures happy
    const wb = new ExcelJS.Workbook();
    const monthLabel = `${MONTH_NAMES[month - 1]} ${year}`;

    // Number formats + section fills, tuned to mirror the client's workbook.
    // Amounts are written as real numbers (not "₹…" strings) so the client can
    // sum/filter them, using Indian lakh grouping with 2 decimals; zero renders
    // as a dash. Percentages likewise show a dash at zero.
    const AMT_FMT = '#,##,##0.00;-#,##,##0.00;"-"';
    const PCT_FMT = '0.00;-0.00;"-"';
    const WHITE = 'FFFFFFFF';
    const FILL = {
      banner:  'FF4472C4',   // blue title + header band (white text)
      nursing: 'FFD9D2E9',   // lavender
      physio:  'FFB7DEE8',   // light blue
      equip:   'FFD9D9D9',   // grey
      other:   'FFEDEDED',   // neutral grey
      ebitda:  'FFF2DCDB',   // pink
      expRow:  'FFD9D9D9',   // grey expense rows
      salesTot:'FFDBEAFE',
      expTot:  'FFDCFCE7',
    };
    const pctSales = (n: number) => (data.total_sales > 0 ? (n / data.total_sales) * 100 : 0);

    // Excel sheet names: max 31 chars, no \/?*[]:, must be unique
    const usedSheetNames = new Set<string>();
    const safeSheetName = (raw: string) => {
      let name = raw.replace(/[\\/?*[\]:]/g, '').slice(0, 31).trim() || 'Sheet';
      let candidate = name;
      let i = 2;
      while (usedSheetNames.has(candidate)) {
        candidate = `${name.slice(0, 28)} ${i++}`;
      }
      usedSheetNames.add(candidate);
      return candidate;
    };

    // ── Pull the two case/staff-level detail feeds (best-effort; a failure here
    // must not block the rest of the workbook) ──
    // fetchPL already holds both feeds in state for the drill-down panels; only
    // re-fetch what is actually missing, so the usual case costs no request.
    // Renamed off the state variables they seed from: `let nursingDetail` would
    // otherwise shadow the state of the same name for the rest of this function.
    let nursingRows: any[] = nursingDetail;
    let salaryRows: any = salaryDetail;
    try {
      if (nursingRows.length === 0 || !salaryRows) {
        const [ndRes, sdRes] = await Promise.all([
          nursingRows.length === 0
            ? axios.get(`${API}/api/billing/nursing-sales-detail`, { params: { month, year }, headers: getAuthHeader() })
            : Promise.resolve({ data: nursingRows }),
          !salaryRows
            ? axios.get(`${API}/api/financial/salary-detail`, { params: { month, year }, headers: getAuthHeader() })
            : Promise.resolve({ data: salaryRows }),
        ]);
        nursingRows = Array.isArray(ndRes.data) ? ndRes.data : [];
        salaryRows = sdRes.data || null;
      }
    } catch (err) {
      console.error('detail-sheet fetch failed', err);
    }

    const grouped = groupByCategory(data.expenses);
    const incomeLines = buildLines(data.income, INCOME_DISPLAY);
    const expLines = buildLines(data.expenses, EXPENSE_DISPLAY).filter(l => !isHiddenExpenseLine(l));

    // ── Sheet 1: two-panel P&L (Sales left, Expenses right) ──
    // Columns: A spacer | B SL No | C Sales | D Amount | E % | F Amount(section)
    //          | G gap | H SL No | I Expenses | J % | K Amount
    const ws = wb.addWorksheet(safeSheetName(`P & L ${MONTH_NAMES[month - 1].slice(0, 3)} ${String(year).slice(2)}`));
    ws.columns = [
      { width: 4 }, { width: 7 }, { width: 32 }, { width: 16 }, { width: 12 }, { width: 16 },
      { width: 4 }, { width: 7 }, { width: 32 }, { width: 14 }, { width: 16 },
    ];

    const cell = (r: number, c: number) => ws.getRow(r).getCell(c);
    const set = (
      r: number, c: number, val: any,
      o?: { bold?: boolean; fmt?: string; align?: 'left' | 'right' | 'center'; fill?: string; color?: string; size?: number },
    ) => {
      const cl = cell(r, c);
      cl.value = val;
      if (o?.fmt) cl.numFmt = o.fmt;
      const font: any = {};
      if (o?.bold) font.bold = true;
      if (o?.color) font.color = { argb: o.color };
      if (o?.size) font.size = o.size;
      if (Object.keys(font).length) cl.font = font;
      if (o?.align) cl.alignment = { horizontal: o.align, vertical: 'middle' };
      if (o?.fill) cl.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: o.fill } };
      return cl;
    };
    // Paint a horizontal colour band across a column range, so section-header and
    // subtotal strips fill the whole strip (not just the cells holding values).
    const band = (r: number, c1: number, c2: number, argb: string) => {
      for (let c = c1; c <= c2; c++) {
        cell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      }
    };

    // Title banner — row 1 left blank, blue banner spanning rows 2–3.
    ws.mergeCells('B2:K3');
    band(2, 2, 11, FILL.banner);
    band(3, 2, 11, FILL.banner);
    const t = ws.getCell('B2');
    t.value = `LORDS  Profit & Loss A/C ${monthLabel}`;
    t.font = { bold: true, size: 16, color: { argb: WHITE } };
    t.alignment = { horizontal: 'center', vertical: 'middle' };

    // Column headers (row 5) — blue band, white text.
    const HR = 5;
    const hdr = (c: number, txt: string, align: 'left' | 'right' = 'left') =>
      set(HR, c, txt, { bold: true, align, fill: FILL.banner, color: WHITE });
    hdr(2, 'SL No'); hdr(3, 'Sales'); hdr(4, 'Amount', 'right'); hdr(5, '% Of Sale', 'right'); hdr(6, 'Amount', 'right');
    hdr(8, 'SL No'); hdr(9, 'Expenses'); hdr(10, '% Expenses', 'right'); hdr(11, 'Amount', 'right');

    // Partition income into the client's sales sections.
    const groupOf = (label: string): 'nursing' | 'physio' | 'equipment' | 'other' => {
      // "Nurse vendor payments" starts with "Nurse", not "Nursing", so the
      // regex below misses it — it belongs with the nursing block it deducts
      // from, which makes that subtotal net rather than gross.
      if (label === NURSE_VENDOR_CATEGORY) return 'nursing';
      if (/^nursing/i.test(label)) return 'nursing';
      if (label === 'Physiotherapy') return 'physio';
      if (/^medical equipment|^consumables/i.test(label)) return 'equipment';
      return 'other';
    };
    // Label -> position, built from the same constant the screen orders by, so
    // the export can't drift from it. Looking the whole label up beats stripping
    // a prefix: 'Nursing Visit' has no em-dash and would miss the regex,
    // landing last by accident rather than by rule.
    const NURSE_LABEL_ORDER: Record<string, number> = Object.fromEntries(
      NURSING_SHIFT_BUCKETS.map((b, i) => [nursingCategory(b), i])
    );
    const nurseOrder = (label: string) => {
      // The deduction sorts after the shift lines, inside the same section.
      if (label === NURSE_VENDOR_CATEGORY) return 98;
      const idx = NURSE_LABEL_ORDER[label];
      return idx === undefined ? 99 : idx;
    };
    const nursingLines = incomeLines.filter(l => groupOf(l.label) === 'nursing').sort((a, b) => nurseOrder(a.label) - nurseOrder(b.label));
    const physioLines = incomeLines.filter(l => groupOf(l.label) === 'physio');
    const equipmentLines = incomeLines.filter(l => groupOf(l.label) === 'equipment');
    const otherLines = incomeLines.filter(l => groupOf(l.label) === 'other');

    // ── Left panel: Sales (first content row 7; row 6 left blank) ──
    let sr = 7;
    let sectionNo = 0;
    // Mirrors the client: a coloured header strip (section total in col F), a
    // blank row, the numbered line items, a blank row, a coloured "Sub Total"
    // strip, then a trailing blank row. A single-line section (e.g. Physiotherapy)
    // is rendered inline on its header strip instead.
    const renderSalesSection = (heading: string, lines: PLLine[], fill: string) => {
      if (lines.length === 0) return;
      sectionNo++;
      const total = lines.reduce((s, l) => s + l.amount, 0);

      if (lines.length === 1) {
        band(sr, 2, 6, fill);
        set(sr, 2, sectionNo, { bold: true, align: 'right' });
        set(sr, 3, heading, { bold: true });
        set(sr, 4, total, { bold: true, fmt: AMT_FMT, align: 'right' });
        set(sr, 5, pctSales(total), { bold: true, fmt: PCT_FMT, align: 'right' });
        set(sr, 6, total, { bold: true, fmt: AMT_FMT, align: 'right' });
        sr += 2;
        return;
      }

      band(sr, 2, 6, fill);
      set(sr, 2, sectionNo, { bold: true, align: 'right' });
      set(sr, 3, heading, { bold: true });
      set(sr, 6, total, { bold: true, fmt: AMT_FMT, align: 'right' });
      sr += 2; // header + blank
      lines.forEach((l, i) => {
        set(sr, 2, i + 1, { align: 'right' });
        set(sr, 3, l.label);
        set(sr, 4, l.amount, { fmt: AMT_FMT, align: 'right' });
        set(sr, 5, pctSales(l.amount), { fmt: PCT_FMT, align: 'right' });
        sr++;
      });
      sr++; // blank
      band(sr, 2, 6, fill);
      set(sr, 3, `Sub Total ${heading}`, { bold: true });
      set(sr, 4, total, { bold: true, fmt: AMT_FMT, align: 'right' });
      set(sr, 5, pctSales(total), { bold: true, fmt: PCT_FMT, align: 'right' });
      sr += 2; // subtotal + trailing blank
    };
    renderSalesSection('Nursing Services', nursingLines, FILL.nursing);
    renderSalesSection('Physiotherapy Services', physioLines, FILL.physio);
    renderSalesSection('Medical Equipment Division', equipmentLines, FILL.equip);
    renderSalesSection('Other Income', otherLines, FILL.other);

    // EBITDA profit-sharing block (pink band), then Total EBITDA / Net Profit / Total Sales.
    sr++;
    band(sr, 2, 6, FILL.ebitda);
    set(sr, 2, 'SL No', { bold: true });
    set(sr, 3, 'EBITDA ( Profit ) Sharing', { bold: true });
    set(sr, 4, '% EBITDA', { bold: true, align: 'right' });
    set(sr, 5, 'Amount', { bold: true, align: 'right' });
    sr++;
    PARTNERS.forEach((p, i) => {
      set(sr, 2, i + 1, { align: 'right' });
      set(sr, 3, p.name);
      set(sr, 4, p.percent, { fmt: PCT_FMT, align: 'right' });
      set(sr, 5, (data.ebitda * p.percent) / 100, { fmt: AMT_FMT, align: 'right' });
      sr++;
    });
    set(sr, 3, 'Total EBITDA', { bold: true });
    set(sr, 4, 100, { fmt: PCT_FMT, align: 'right', bold: true });
    set(sr, 5, data.ebitda, { fmt: AMT_FMT, align: 'right', bold: true });
    sr += 2;
    set(sr, 3, 'Net Profit', { bold: true });
    set(sr, 4, 'EBITDA', { align: 'right' });
    set(sr, 5, pctSales(data.ebitda), { fmt: PCT_FMT, align: 'right', bold: true });
    set(sr, 6, data.ebitda, { fmt: AMT_FMT, align: 'right', bold: true });
    sr += 2;
    band(sr, 2, 6, FILL.salesTot);
    set(sr, 3, `Total Sales ${monthLabel}`, { bold: true });
    set(sr, 6, data.total_sales, { fmt: AMT_FMT, align: 'right', bold: true });

    // ── Right panel: Expenses (independent cursor; grey banded rows, blank
    // rows between items). Salary (payroll-sourced) lines share SL No 1, matching
    // the client's grouping; the remaining categories number 2, 3, 4 … ──
    let er = 7;
    let expNo = 1;
    expLines.forEach((l) => {
      const sl = l.source === 'payroll' ? 1 : (++expNo);
      band(er, 8, 11, FILL.expRow);
      set(er, 8, sl, { bold: true, align: 'right' });
      set(er, 9, l.label, { bold: true });
      set(er, 10, pctSales(l.amount), { fmt: PCT_FMT, align: 'right' });
      set(er, 11, l.amount, { fmt: AMT_FMT, align: 'right' });
      er += 2; // blank row between items
    });
    er++;
    band(er, 8, 11, FILL.expTot);
    set(er, 9, `Total Expenditure ${monthLabel}`, { bold: true });
    set(er, 10, pctSales(data.total_expenses), { fmt: PCT_FMT, align: 'right', bold: true });
    set(er, 11, data.total_expenses, { fmt: AMT_FMT, align: 'right', bold: true });

    // ── Sheet: Sales Nursing (case-by-case, grouped by shift bucket) ──
    const wsSN = wb.addWorksheet(safeSheetName('Sales Nursing'));
    wsSN.columns = [
      { width: 6 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 22 }, { width: 16 },
      { width: 22 }, { width: 8 }, { width: 26 }, { width: 26 }, { width: 16 }, { width: 12 },
      { width: 20 }, { width: 12 }, { width: 8 }, { width: 10 }, { width: 12 },
    ];
    const snHead = wsSN.addRow([
      'Sl No', 'Case Start', 'Period Start', 'Period End', 'Client Name', 'Contact No',
      'Patient Name', 'A/S', 'Consultant Doctor', 'Hospital', 'Manager', 'Case Status',
      'Area', 'Shift Type', 'Shifts', 'Rate', 'Monthly',
    ]);
    snHead.font = { bold: true, color: { argb: WHITE } };
    snHead.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.banner } }; });
    wsSN.getColumn(16).numFmt = AMT_FMT;
    wsSN.getColumn(17).numFmt = AMT_FMT;

    const byBucket: Record<string, any[]> = {};
    for (const r of nursingRows) {
      const b = classifyNursingShift(r.bill_type, r.shift_type);
      (byBucket[b] ||= []).push(r);
    }
    let sl = 1;
    for (const b of NURSING_SHIFT_BUCKETS) {
      const rows = byBucket[b];
      if (!rows || rows.length === 0) continue;
      wsSN.addRow([b === 'Visit' ? 'Nursing Visit' : `${b} Nursing`]).font = { bold: true };
      let sub = 0;
      for (const r of rows) {
        wsSN.addRow([
          sl++, r.case_start_date || '', r.period_start || '', r.period_end || '',
          r.client_name || '', r.contact_number || '', r.patient_name || '', r.age_sex || '',
          r.consultant_doctor || '', r.hospital || '', r.manager || '', r.case_status || '',
          r.area || '', r.shift_type || '', r.shifts || 0, r.rate || 0, r.monthly || 0,
        ]);
        sub += r.monthly || 0;
      }
      const stRow = wsSN.addRow(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'Sub Total', sub]);
      stRow.font = { bold: true };
    }
    if (nursingRows.length === 0) wsSN.addRow(['No nursing bills for this month']);

    // ── Sheet: 1. Salary (staff-by-staff) ──
    const wsSal = wb.addWorksheet(safeSheetName('1. Salary'));
    wsSal.columns = [{ width: 6 }, { width: 40 }, { width: 16 }];
    wsSal.getColumn(3).numFmt = AMT_FMT;
    if (salaryRows) {
      const h = wsSal.addRow(['Sl No', 'Salary Expenses', 'Gross Salary']);
      h.font = { bold: true, color: { argb: WHITE } };
      h.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.banner } }; });
      const nursing = salaryRows.nursing_total || 0;
      const transport = salaryRows.transport_total || 0;
      const mgmt = salaryRows.management_total || 0;
      const physio = salaryRows.physio_total || 0;
      wsSal.addRow([1, 'Nursing Staff Salary', nursing]);
      wsSal.addRow([2, 'Transport Team Salary', transport]);
      wsSal.addRow([3, 'Top Level Management Salary', mgmt]);
      // Hidden at ₹0, like the P&L's Physiotherapist salaries line.
      if (Math.round(physio) !== 0) wsSal.addRow([4, 'Physiotherapy', physio]);
      wsSal.addRow(['', 'Sub Total Salary Expenses', nursing + transport + mgmt + physio]).font = { bold: true };
      wsSal.addRow([]);

      // Top management, grouped into its Class/Level pay bands with a sub-total
      // each — the same shape the P&L Expenses table now shows. Sl No runs
      // continuously across the bands so it still numbers the section as a whole.
      wsSal.addRow(['', 'Top Level Management Salary', 'Gross Salary']).font = { bold: true };
      let mgmtSl = 1;
      (salaryRows.management_levels || []).forEach((lvl: any) => {
        wsSal.addRow(['', lvl.level]).font = { bold: true, italic: true };
        (lvl.staff || []).forEach((st: any) => wsSal.addRow([mgmtSl++, st.name, st.gross]));
        wsSal.addRow(['', `Sub Total ${lvl.level}`, lvl.total]).font = { bold: true };
      });
      wsSal.addRow(['', 'Sub Total Top Level Management', mgmt]).font = { bold: true };
      wsSal.addRow([]);

      wsSal.addRow(['', 'Transport Team Salary', 'Gross Salary']).font = { bold: true };
      (salaryRows.transport_staff || []).forEach((st: any, i: number) => wsSal.addRow([i + 1, st.name, st.gross]));
      wsSal.addRow(['', 'Sub Total Transport Team', transport]).font = { bold: true };
    } else {
      wsSal.addRow(['Salary detail unavailable']);
    }

    // ── Numbered detail sheets for each non-salary expense category. Salary
    // (payroll-sourced) lines are already itemised in the "1. Salary" sheet. ──
    let sheetNo = 2;
    for (const line of expLines) {
      if (line.source === 'payroll') continue;
      const entries = grouped[line.key] || [];
      const ws3 = wb.addWorksheet(safeSheetName(`${sheetNo}. ${line.label}`));
      sheetNo++;
      ws3.columns = [{ width: 25 }, { width: 35 }, { width: 18 }];
      ws3.getColumn(3).numFmt = AMT_FMT;
      const h = ws3.addRow(['Sub-category', 'Description', 'Amount']);
      h.font = { bold: true };
      h.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }; });
      let total = 0;
      for (const e of entries) {
        ws3.addRow([e.sub_category || '', e.description || '', e.amount]);
        total += e.amount;
      }
      ws3.addRow(['TOTAL', '', total]).font = { bold: true };
    }

    // ── Consolidated EXP sheet ──
    const wsCons = wb.addWorksheet(safeSheetName('Consolidated EXP'));
    wsCons.columns = [{ width: 6 }, { width: 38 }, { width: 16 }, { width: 10 }];
    wsCons.getColumn(3).numFmt = AMT_FMT;
    wsCons.getColumn(4).numFmt = PCT_FMT;
    const consHdr = wsCons.addRow(['Sl No', 'Expenses', 'Amount', '% of Sales']);
    consHdr.font = { bold: true };
    consHdr.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7ED' } }; });
    expLines.forEach((line, i) => {
      wsCons.addRow([i + 1, line.label, line.amount, pctSales(line.amount)]);
    });
    wsCons.addRow(['', 'TOTAL', data.total_expenses, pctSales(data.total_expenses)]).font = { bold: true };

    // ── Save ──
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `PL_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
    toast.success('Excel downloaded');
  };

  // ── Derived values ──
  const expGrouped = pl ? groupByCategory(pl.expenses) : {};
  const incGrouped = pl ? groupByCategory(pl.income) : {};
  // Every line is rendered — the cards scroll internally rather than hiding
  // rows behind a "show more", so the full statement is visible at a glance.
  // The people behind a payroll line. Management lines are labelled
  // "Management salaries — <level>", and salary-detail groups the same staff
  // under that level, so the level is the join key. Nursing and physio have no
  // per-person payroll to show (see get_salary_detail), so they stay inert.
  // ── Revenue drill-down: which bills sit behind a revenue line ──────────────
  //
  // Two shapes, because the two P&L feeds aggregate differently and mixing them
  // would put rows in the panel that don't add up to its own header:
  //   • nursing  -> BillLineItem rows, bucketed by shift (the line sums items)
  //   • the rest -> whole Bill rows                      (the line sums bills)
  // Measured on real data: line items exceed bill totals by 2,000 on nursing and
  // 420 on equipment_rental, because total_amount is net of discount and floored
  // for consumables.

  // Label -> bill_type, the inverse of BILL_TYPE_LABEL. Nurse vendor payments is
  // absent by construction: it has no bill type and no bills.
  const BILL_TYPE_FOR_LABEL: Record<string, string> = Object.fromEntries(
    Object.entries(BILL_TYPE_LABEL).map(([k, v]) => [v, k])
  );

  // The nursing bucket a line's label refers to ('8 Hrs' etc.), or null.
  const nursingBucketForLabel = (label: string): string | null =>
    (NURSING_SHIFT_BUCKETS as readonly string[]).find(b => nursingCategory(b) === label) || null;

  const revenueRowsFor = (line: PLLine): any[] => {
    if (!line || line.source !== 'billing') return [];
    // Lines with no bill_type of their own read from their dedicated cache.
    if (line.label === NURSING_DISCOUNT_CATEGORY) return billDetail[NURSING_DISCOUNT_DETAIL_KEY] || [];
    if (line.label === MISC_LINE_CATEGORY) return billDetail[MISC_LINE_DETAIL_KEY] || [];
    if (line.key === RETURNS_LINE_KEY) return billDetail[CONSUMABLE_RETURN_TYPE] || [];
    const bucket = nursingBucketForLabel(line.label);
    if (bucket) {
      // Same helper the P&L line itself is built with, so the two agree.
      return nursingDetail.filter(r => classifyNursingShift(r.bill_type, r.shift_type) === bucket);
    }
    const billType = BILL_TYPE_FOR_LABEL[line.label];
    if (!billType) return [];
    return billDetail[billType] || [];
  };

  // Whether a line has a drill-down at all. Nursing can answer immediately;
  // other lines can't be known until fetched, so any mapped bill type counts.
  const revenueLineHasDetail = (line: PLLine): boolean => {
    if (!line || line.source !== 'billing') return false;
    // Nursing Discount / Misc-within-bills drill into their own bill lists —
    // clickable whenever there's a value to show.
    if (line.label === NURSING_DISCOUNT_CATEGORY) return Math.abs(line.amount) > 0.005;
    if (line.label === MISC_LINE_CATEGORY) return Math.abs(line.amount) > 0.005;
    const bucket = nursingBucketForLabel(line.label);
    if (bucket) return nursingDetail.some(r => classifyNursingShift(r.bill_type, r.shift_type) === bucket);
    return !!BILL_TYPE_FOR_LABEL[line.label];
  };

  // Open a revenue line, fetching its bills first if they aren't cached.
  const openRevenueLine = async (line: PLLine) => {
    setActiveSalaryLine(null);
    setActiveExpenseTab(null);
    setRevenueSearch('');
    setExpandedPatient(null);
    if (activeRevenueLine === line.key) { setActiveRevenueLine(null); return; }
    setActiveRevenueLine(line.key);

    // Nursing Discount / Misc-within-bills have their own detail endpoints
    // (no bill_type of their own).
    const synthEndpoint =
      line.label === NURSING_DISCOUNT_CATEGORY
        ? { url: 'nursing-discount-detail', key: NURSING_DISCOUNT_DETAIL_KEY }
        : line.label === MISC_LINE_CATEGORY
          ? { url: 'misc-line-detail', key: MISC_LINE_DETAIL_KEY }
          : null;
    if (synthEndpoint) {
      if (billDetail[synthEndpoint.key]) return;   // already cached
      setBillDetailLoading(true);
      try {
        const res = await axios.get(`${API}/api/billing/${synthEndpoint.url}`, {
          params: { month, year }, headers: getAuthHeader(),
        });
        setBillDetail(d => ({ ...d, [synthEndpoint.key]: Array.isArray(res.data) ? res.data : [] }));
      } catch (err) {
        console.error(`${synthEndpoint.url} failed`, err);
        setBillDetail(d => ({ ...d, [synthEndpoint.key]: [] }));
      } finally {
        setBillDetailLoading(false);
      }
      return;
    }

    const billType = nursingBucketForLabel(line.label) ? null : BILL_TYPE_FOR_LABEL[line.label];
    if (!billType || billDetail[billType]) return;   // nursing, or already cached
    setBillDetailLoading(true);
    try {
      const res = await axios.get(`${API}/api/billing/sales-detail`, {
        params: { month, year, bill_type: billType },
        headers: getAuthHeader(),
      });
      setBillDetail(d => ({ ...d, [billType]: Array.isArray(res.data) ? res.data : [] }));
    } catch (err) {
      console.error(`sales-detail failed for ${billType}`, err);
      setBillDetail(d => ({ ...d, [billType]: [] }));
    } finally {
      setBillDetailLoading(false);
    }
  };

  // Open the consumable-returns detail (the figure inside the Consumables
  // expander). Return bills have a real bill_type, so this reuses /sales-detail —
  // it just isn't reachable as a standalone P&L line, hence its own opener.
  const openReturnsDetail = async () => {
    setActiveSalaryLine(null);
    setActiveExpenseTab(null);
    setRevenueSearch('');
    setExpandedPatient(null);
    if (activeRevenueLine === RETURNS_LINE_KEY) { setActiveRevenueLine(null); return; }
    setActiveRevenueLine(RETURNS_LINE_KEY);
    if (billDetail[CONSUMABLE_RETURN_TYPE]) return;   // already cached
    setBillDetailLoading(true);
    try {
      const res = await axios.get(`${API}/api/billing/sales-detail`, {
        params: { month, year, bill_type: CONSUMABLE_RETURN_TYPE },
        headers: getAuthHeader(),
      });
      setBillDetail(d => ({ ...d, [CONSUMABLE_RETURN_TYPE]: Array.isArray(res.data) ? res.data : [] }));
    } catch (err) {
      console.error('sales-detail failed for consumable_return', err);
      setBillDetail(d => ({ ...d, [CONSUMABLE_RETURN_TYPE]: [] }));
    } finally {
      setBillDetailLoading(false);
    }
  };

  const staffForSalaryLine = (line: PLLine): any[] => {
    if (line.source !== 'payroll' || !salaryDetail) return [];
    // Nursing and physio are single lines with no Class/Level split.
    if (line.key === 'Nursing staff salaries') return salaryDetail.nursing_staff || [];
    if (line.key === 'Physiotherapist salaries') return salaryDetail.physio_staff || [];
    const level = line.label.split('—')[1]?.trim();
    if (!level) return [];
    const key = level.toLowerCase();
    const band = (salaryDetail.management_levels || [])
      .find((l: any) => String(l.level).trim().toLowerCase() === key);
    // salary-detail pulls Transport staff into their own list to match the
    // Excel layout, but the P&L bands everyone by Class/Level — so a level
    // whose only people are Transport has no band here at all. Sweep that list
    // too, or those lines look empty (and unclickable) despite showing a total.
    const transport = (salaryDetail.transport_staff || [])
      .filter((p: any) => String(p.level ?? p.class_level ?? '').trim().toLowerCase() === key);
    return [...(band?.staff || []), ...transport];
  };

  // Every management Class/Level in one register, a titled section per band in
  // the P&L's own order. Each band's staff are exactly that line's panel list —
  // Transport included, as on screen — so a section's Sub Total ties to its row.
  // Throws on failure; the panel's Excel button owns the toast and busy state.
  const exportManagementRegister = (mgmtLines: PLLine[]) =>
    downloadSalaryRegister(
      'management',
      'Management salaries',
      mgmtLines.map(l => ({ heading: l.label.slice(MGMT_SALARY_PREFIX.length), staff: staffForSalaryLine(l) })),
      month,
      year,
    );

  const expenseLines = pl ? buildLines(pl.expenses, EXPENSE_DISPLAY).filter(l => !isHiddenExpenseLine(l)) : [];

  // Everyone across the management bands, for the group row's panel, in the
  // Management Staff registration tab's Sl.No order (sl_no from salary-detail).
  // A row without sl_no — a backend that predates it — falls back to the
  // band's order on the P&L (Class 1, 2, …) and then A→Z within the band.
  const mgmtSalaryLines = expenseLines.filter(isMgmtSalaryLine);
  const consolidatedMgmtStaff = (): any[] =>
    mgmtSalaryLines
      .flatMap((l, band) => staffForSalaryLine(l).map(p => ({ p, band })))
      .sort((a, b) => {
        if (a.p.sl_no != null && b.p.sl_no != null) return a.p.sl_no - b.p.sl_no;
        return a.band - b.band || byName(a.p, b.p);
      })
      .map(x => x.p);
  // Sum of the four nursing lines, net of the Nurse vendor payments deduction —
  // the client's "Total Nursing Sales". Read off the rendered lines rather than
  // recomputed from billing, so it always equals what is printed above it.
  // Nurse vendor payments is already a NEGATIVE row, so a plain sum nets it.
  const NURSING_BLOCK = new Set<string>([
    ...NURSING_SHIFT_BUCKETS.map(b => nursingCategory(b)),
    NURSE_VENDOR_CATEGORY,
  ]);
  const nursingSubtotal = (lines: PLLine[]) =>
    lines.filter(l => NURSING_BLOCK.has(l.label)).reduce((sum, l) => sum + l.amount, 0);

  const incomeLines = (() => {
    if (!pl) return [];
    const lines = buildLines(pl.income, INCOME_DISPLAY);
    // Vendor-payment lines are 100% deduction — with nothing entered there are
    // no rows at all and buildLines emits no line, leaving nothing to click.
    // A placeholder keeps each on the statement as the prompt to fill it in.
    let injected = false;
    [NURSE_VENDOR_CATEGORY, DOCTOR_VENDOR_CATEGORY].forEach(cat => {
      if (!lines.some(l => l.key === cat)) {
        lines.push({
          key: cat,
          label: cat,
          subledgers: [],
          // Read from INCOME_DISPLAY rather than hard-coded, so the two can't drift.
          order: INCOME_DISPLAY[cat.toLowerCase()]?.order ?? FALLBACK_ORDER,
          amount: 0,
          source: 'billing' as const,
        });
        injected = true;
      }
    });
    if (injected) lines.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
    return lines;
  })();

  // ── Styles ──────────────────────────────────────────────────────────────────

  const s: Record<string, React.CSSProperties> = {
    page: { height: '100vh', boxSizing: 'border-box' as const, padding: spacing(8), width: '100%', display: 'flex', flexDirection: 'column' as const, overflow: 'hidden', gap: spacing(6) },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: spacing(4), flex: '0 0 auto' },
    title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: colors.textPrimary },
    controls: { display: 'flex', gap: spacing(3), alignItems: 'center', flexWrap: 'wrap' },
    select: { padding: '0.5rem 0.75rem', borderRadius: radius.md, border: `1px solid ${colors.border}`, fontSize: fontSize.lg, background: colors.surface },
    btn: { padding: '0.5rem 1.25rem', borderRadius: radius.md, border: 'none', cursor: 'pointer', fontSize: fontSize.lg, fontWeight: fontWeight.semibold },
    btnPrimary: { background: colors.primary, color: 'white' },
    btnGreen: { background: colors.success, color: 'white' },
    btnGhost: { background: colors.borderMuted, color: colors.textSecondary, border: `1px solid ${colors.border}` },
    btnRed: { background: '#ef4444', color: 'white' },
    btnSm: { padding: '0.25rem 0.65rem', fontSize: fontSize.sm, borderRadius: radius.sm, border: 'none', cursor: 'pointer', fontWeight: fontWeight.medium },
    // Field label inside the petty-cash drawer modal — same look as the
    // Amount / Note labels above it, with the spacing that separates fields.
    drawerFieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary, marginTop: spacing(3), display: 'block' },

    // ── Layout: full-width two-column body below the header — right column
    // fixed at ~28% for the entry panels, left column takes the rest. Both
    // columns fill the remaining viewport height; individual panels size to
    // content and scroll internally rather than the page growing past 100vh.
    bodyGrid: { flex: '1 1 auto', minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 28%', gap: spacing(6) },
    leftCol: { display: 'flex', flexDirection: 'column' as const, gap: spacing(6), minHeight: 0, overflow: 'hidden' },
    rightCol: { display: 'flex', flexDirection: 'column' as const, gap: spacing(6), minHeight: 0, overflow: 'hidden' },
    // Revenue + Expenses cards, side by side, equal height, running to the
    // bottom of the screen. useFillTable spreads their rows over that height
    // and pins the total to the bottom edge, so it doesn't read as dead space.
    topRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing(6), flex: '1 1 auto', minHeight: 0 },

    card: { background: colors.surface, borderRadius: radius.xl, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' },
    cardHeader: { padding: '0.875rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontWeight: fontWeight.bold, fontSize: fontSize.lg, borderBottom: `1px solid ${colors.borderMuted}` },

    // P&L table
    table: { width: '100%', borderCollapse: 'collapse', fontSize: fontSize.md },
    th: { padding: '0.5rem 1rem', textAlign: 'left' as const, fontWeight: fontWeight.semibold, background: colors.surfaceMuted, color: colors.textMuted, fontSize: fontSize.xs, textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    td: { padding: '0.6rem 1rem', borderBottom: `1px solid ${colors.surfaceMuted}` },
    // The category cell specifically: one row per line, always. maxWidth:0 lets
    // the cell shrink so the ellipsis can engage instead of the name wrapping
    // and pushing the statement taller. Not folded into `td` — that's shared
    // with the colSpan empty-state and the Total rows, which must not truncate.
    tdCategory: { padding: '0.6rem 1rem', borderBottom: `1px solid ${colors.surfaceMuted}`, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 },
    tdRight: { padding: '0.6rem 1rem', textAlign: 'right' as const, borderBottom: `1px solid ${colors.surfaceMuted}`, fontVariantNumeric: 'tabular-nums' },
    // % column reads as secondary (muted), Amount as the primary figure — so the
    // rightmost money column carries the visual weight after the swap.
    // width:1% + nowrap shrinks these to their content, so the Category column
    // keeps every pixel they don't need — long names like "Management salaries
    // — Unassigned" were being cut off otherwise.
    tdPct: { padding: '0.6rem 0.5rem', textAlign: 'right' as const, borderBottom: `1px solid ${colors.surfaceMuted}`, fontVariantNumeric: 'tabular-nums', color: colors.textMuted, fontSize: fontSize.sm, width: '1%', whiteSpace: 'nowrap' as const },
    tdAmount: { padding: '0.6rem 1rem 0.6rem 0.5rem', textAlign: 'right' as const, borderBottom: `1px solid ${colors.surfaceMuted}`, fontVariantNumeric: 'tabular-nums', color: colors.textPrimary, fontWeight: fontWeight.medium, width: '1%', whiteSpace: 'nowrap' as const },
    // Serial-number column: narrow and muted so it numbers the statement
    // without competing with the category or the figures.
    thSl: { padding: '0.5rem 0.5rem 0.5rem 1rem', textAlign: 'right' as const, fontWeight: fontWeight.semibold, background: colors.surfaceMuted, color: colors.textMuted, fontSize: fontSize.xs, textTransform: 'uppercase' as const, letterSpacing: '0.04em', width: '1%', whiteSpace: 'nowrap' as const },
    // Must carry the same width:1% as their cells, or the browser sizes the
    // column from the header and the shrink-to-content never takes effect.
    thPct: { padding: '0.5rem 0.5rem', textAlign: 'right' as const, fontWeight: fontWeight.semibold, background: colors.surfaceMuted, color: colors.textMuted, fontSize: fontSize.xs, textTransform: 'uppercase' as const, letterSpacing: '0.04em', width: '1%', whiteSpace: 'nowrap' as const },
    thAmount: { padding: '0.5rem 1rem 0.5rem 0.5rem', textAlign: 'right' as const, fontWeight: fontWeight.semibold, background: colors.surfaceMuted, color: colors.textMuted, fontSize: fontSize.xs, textTransform: 'uppercase' as const, letterSpacing: '0.04em', width: '1%', whiteSpace: 'nowrap' as const },
    tdSl: { padding: '0.6rem 0.5rem 0.6rem 1rem', borderBottom: `1px solid ${colors.surfaceMuted}`, textAlign: 'right' as const, color: colors.textFaint, fontSize: fontSize.sm, fontVariantNumeric: 'tabular-nums', width: '1%', whiteSpace: 'nowrap' as const },
    rowAlt: { background: colors.surfaceMuted },
    sectionHdr: { padding: '0.5rem 1rem', fontWeight: fontWeight.bold, fontSize: fontSize.sm, textTransform: 'uppercase' as const, letterSpacing: '0.06em' },
    totalRow: { borderTop: `2px solid ${colors.border}` },
    showMoreRow: { cursor: 'pointer', color: colors.primary, fontWeight: fontWeight.semibold },

    // Entry panel
    tabs: { display: 'flex', flexWrap: 'wrap', gap: spacing(1) + ' ' + spacing(1), padding: '0.75rem 1rem', borderBottom: `1px solid ${colors.borderMuted}` },
    tab: { padding: '0.3rem 0.75rem', borderRadius: radius.pill, fontSize: fontSize.xs, cursor: 'pointer', border: `1px solid ${colors.border}`, background: colors.surface, color: colors.textMuted, fontWeight: fontWeight.medium, transition: 'all 0.15s' },
    tabActive: { background: colors.primary, color: 'white', borderColor: colors.primary },

    input: { width: '100%', padding: '0.5rem 0.75rem', borderRadius: radius.md, border: `1px solid ${colors.border}`, fontSize: fontSize.md, boxSizing: 'border-box' as const, background: colors.surface },

    // EBITDA — same card shell as everything else, just sized to content
    ebitdaBlock: { background: colors.warningBg, borderRadius: radius.lg, padding: '1rem 1.25rem', margin: '1rem', border: `1px solid ${colors.warningBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },

    // Report cards (Revenue / Expenses / "Where expenses go") — header fixed,
    // body scrolls internally if its content outgrows the available space.
    reportCard: { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' as const },
    reportBody: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' as const },
  };

  const years = [year - 1, year, year + 1];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      <style>{FILL_CSS}</style>
      {/* Header */}
      <div style={s.header}>
        <div style={s.title}>Financial Module — P&amp;L</div>
        <div style={s.controls}>
          <select style={s.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select style={s.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={{ ...s.btn, ...s.btnGreen }} onClick={downloadExcel} disabled={!pl}>
            Download Excel
          </button>
        </div>
      </div>

      {loading && !pl && (
        <div style={{ textAlign: 'center', padding: '3rem', color: colors.textFaint }}>Loading P&amp;L…</div>
      )}

      {pl && (
        <div style={s.bodyGrid}>
          {/* ── Left column: read-only report ── */}
          <div style={s.leftCol}>
            {/* Revenue | Expenses — side by side, equal height */}
            <div style={s.topRow}>
              <div style={{ ...s.card, ...s.reportCard }}>
                <div style={{ ...s.cardHeader, background: colors.primaryBg, color: colors.primaryDark, flex: '0 0 auto' }}>
                  <span>Revenue</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing(3) }}>
                    <button
                      style={{ ...s.btnSm, background: colors.surface, color: colors.primaryDark, border: `1px solid ${colors.border}` }}
                      onClick={openBadDebtModal}
                      title="Record a bad debt (subtracts from revenue)"
                    >
                      + Bad debt
                    </button>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(pl.total_sales)}</span>
                  </div>
                </div>
                <div ref={revenueFill.bodyRef} style={s.reportBody}>
                  <table ref={revenueFill.tableRef} className={FILL_TABLE_CLASS} style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.thSl}>#</th>
                        <th style={s.th}>Category</th>
                        <th style={s.thPct}>% Sales</th>
                        <th style={s.thAmount}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomeLines.length === 0 && (
                        <tr><td colSpan={4} style={{ ...s.td, color: colors.textFaint, fontStyle: 'italic' }}>No income recorded this period.</td></tr>
                      )}
                      {incomeLines.map((line, i) => {
                        // Consumables / Medical Equipment (Rental) expand to show
                        // the purchase & expired figures netted off them.
                        const adjKey = ADJUSTABLE_LINE_KEY[line.label];
                        const expandable = !!adjKey;
                        const isExpanded = expandable && expandedIncomeLine === line.key;
                        const kinds = expandable ? (LINE_ADJUSTMENTS[adjKey] || []) : [];
                        const deducted = expandable ? adjDeducted(adjKey) : 0;
                        // Credit notes are netted into the Consumables line too, so
                        // they have to be added back with the manual deductions to
                        // recover what was actually billed.
                        const lineReturns = adjKey === 'consumables' ? consumableReturns : 0;
                        // line.amount is already net (the deductions ride in as
                        // negative rows), so the gross is recovered by adding them
                        // back — exact by construction, no id-range reasoning.
                        const gross = line.amount + deducted + lineReturns;
                        // Nurse vendor payments has no bills behind it — it is
                        // 100% deduction, so there is no "Billed" row to show.
                        const isPureDeduction = adjKey === NURSE_VENDOR_LINE_KEY || adjKey === DOCTOR_VENDOR_LINE_KEY;
                        // Generated from the kinds list, so adding a deduction can
                        // never leave a footer that misdescribes the arithmetic.
                        const formulaLabel = isPureDeduction
                          ? 'Total vendor payments'
                          : `Billed${lineReturns > 0 ? ' − consumables returns' : ''}`
                            + kinds.map(k => ` − ${ADJUSTMENT_LABEL[k].toLowerCase()}`).join('');
                        // The expander shares its parent's stripe so the pair reads
                        // as one block; `i` is untouched for every later row.
                        const zebra = i % 2 === 1 ? colors.surfaceMuted : undefined;
                        // Bill-backed lines drill down to the patients behind them.
                        const hasRevenueDetail = revenueLineHasDetail(line);
                        const isRevenueOpen = activeRevenueLine === line.key;
                        // Built once so the hover tooltip can repeat it verbatim
                        // — the cell ellipsises, and the suffix is usually the
                        // first thing to get cut off.
                        const incomeSuffix = line.source === 'ledger'
                          ? (line.subledgers.length > 0
                              ? subledgerSuffix(line.subledgers)
                              : `${incGrouped[line.key].length} entr${incGrouped[line.key].length === 1 ? 'y' : 'ies'}`)
                          : line.source === 'baddebt'
                            ? 'manual adjustment'
                            : isPureDeduction
                              ? 'entered manually'
                              : deducted > 0
                                ? `from Billing · less ${kinds.filter(k => adjAmount(adjKey, k) > 0).map(k => ADJUSTMENT_LABEL[k].toLowerCase()).join(' & ')}`
                                : 'from Billing';
                        return (
                        <React.Fragment key={line.key}>
                        <tr
                          style={{ cursor: (line.source === 'ledger' || line.source === 'baddebt' || expandable || hasRevenueDetail) ? 'pointer' : 'default', background: (activeIncomeTab === line.key || isExpanded || isRevenueOpen) ? colors.primaryBg : zebra }}
                          onClick={() => {
                            // Bad debts open their own modal (which lists every
                            // entry for the month); ledger rows toggle the entry tab.
                            // Consumables / ME Rental keep the row click for their
                            // deductions expander and get a separate View bills
                            // button; the rest open the drill-down directly.
                            if (expandable) setExpandedIncomeLine(prev => prev === line.key ? null : line.key);
                            else if (line.source === 'baddebt') openBadDebtModal();
                            else if (line.source === 'ledger') setActiveIncomeTab(prev => prev === line.key ? null : line.key);
                            else if (hasRevenueDetail) openRevenueLine(line);
                          }}
                        >
                          <td style={s.tdSl}>{i + 1}</td>
                          <td
                            style={s.tdCategory}
                            title={line.subledgers.length > SUBLEDGER_SUFFIX_LIMIT
                              ? `${line.label} — (${line.subledgers.join(', ')})`
                              : `${line.label} — ${incomeSuffix}`}
                          >
                            {expandable && (
                              <span style={{ marginRight: 6, color: colors.textFaint, fontSize: fontSize.xs }}>
                                {isExpanded ? '▾' : '▸'}
                              </span>
                            )}
                            {line.label}
                            <span style={{ marginLeft: 6, fontSize: fontSize.xs, color: colors.textFaint, fontStyle: line.source === 'ledger' ? undefined : 'italic' }}>
                              {incomeSuffix}
                            </span>
                          </td>
                          <td style={s.tdPct}>
                            {pl.total_sales > 0 ? pct(line.amount / pl.total_sales * 100) : '—'}
                          </td>
                          <td style={{ ...s.tdAmount, color: (line.source === 'baddebt' || line.amount < 0) ? colors.danger : undefined }}>
                            {/* Consumables / ME Rental spend their row click on the
                                deductions expander, so the drill-down needs its own
                                affordance. It lives in this cell rather than beside
                                the label because tdCategory clips (maxWidth: 0 +
                                overflow hidden) — a long label ate the button. */}
                            {expandable && !isPureDeduction && hasRevenueDetail ? (
                              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                <button
                                  style={{ ...s.btnSm, ...s.btnGhost, padding: '0 0.35rem', fontSize: fontSize.xs, lineHeight: 1.6, flex: '0 0 auto' }}
                                  title={`Show the bills behind ${line.label}`}
                                  onClick={e => { e.stopPropagation(); openRevenueLine(line); }}
                                >
                                  View bills
                                </button>
                                <span>{fmt(line.amount)}</span>
                              </span>
                            ) : fmt(line.amount)}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr style={{ background: zebra }}>
                            <td style={s.tdSl} />
                            <td colSpan={3} style={{ ...s.td, padding: '0.5rem 1rem 0.75rem' }}>
                              {/* Consumables returns: computed from credit-note
                                  bills, so it reads as text rather than an input
                                  and sits above the typed figures. Shown even at
                                  zero once the line is opened, so its absence is
                                  a stated fact rather than a missing row. */}
                              {adjKey === 'consumables' && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing(3), marginBottom: 4 }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: fontSize.sm, color: colors.textMuted }}>
                                    Consumables returns
                                    <span style={{ fontSize: fontSize.xs, color: colors.textFaint, fontStyle: 'italic' }}>
                                      from credit notes
                                    </span>
                                    {consumableReturns > 0 && (
                                      <button
                                        style={{ ...s.btnSm, ...s.btnGhost, padding: '0 0.35rem', fontSize: fontSize.xs, lineHeight: 1.6 }}
                                        title="Show the return bills behind this figure"
                                        onClick={e => { e.stopPropagation(); openReturnsDetail(); }}
                                      >
                                        View returns
                                      </button>
                                    )}
                                  </span>
                                  <span style={{ width: 120, textAlign: 'right', fontSize: fontSize.sm, fontVariantNumeric: 'tabular-nums', color: colors.textPrimary, paddingRight: '0.4rem' }}>
                                    {fmt(consumableReturns)}
                                  </span>
                                </div>
                              )}
                              {kinds.map(kind => {
                                const label = ADJUSTMENT_LABEL[kind];

                                // ── The list-shaped kind: several payments ──
                                if (isListKind(kind)) {
                                  const rows = vendorPaymentsFor(adjKey);
                                  const drafts = vendorDrafts[adjKey] || [];
                                  const setDrafts = (next: typeof drafts) =>
                                    setVendorDrafts(d => ({ ...d, [adjKey]: next }));
                                  // Save the draft if its amount is valid, then drop the
                                  // row either way — an abandoned "+" leaves nothing
                                  // behind. Reads the draft out of `drafts` by index so
                                  // it always sees the latest typed values.
                                  const commitDraft = (di: number) => {
                                    const d = drafts[di];
                                    if (!d) return;
                                    const amt = parseFloat(d.amount);
                                    if (Number.isFinite(amt) && amt > 0) {
                                      addVendorPayment(adjKey, amt, d.vendor_name);
                                    }
                                    setDrafts(drafts.filter((_, j) => j !== di));
                                  };
                                  return (
                                    <div key={kind} style={{ marginBottom: 6 }}>
                                      <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: 3 }}>{label}</div>

                                      {rows.length === 0 && drafts.length === 0 && (
                                        <div style={{ fontSize: fontSize.xs, color: colors.textFaint, fontStyle: 'italic', marginBottom: 4 }}>
                                          None yet.
                                        </div>
                                      )}

                                      {rows.map(r => (
                                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: spacing(2), marginBottom: 3 }}>
                                          <span style={{ flex: 1, minWidth: 0, fontSize: fontSize.sm, color: r.vendor_name ? colors.textPrimary : colors.textFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {r.vendor_name || 'Unnamed vendor'}
                                          </span>
                                          <span style={{ fontSize: fontSize.sm, fontVariantNumeric: 'tabular-nums' }}>{fmt(Number(r.amount))}</span>
                                          <button
                                            title="Remove this payment"
                                            disabled={adjSaving}
                                            onClick={e => { e.stopPropagation(); deleteVendorPayment(r.id); }}
                                            style={{ ...s.btnSm, background: colors.dangerBg2, color: colors.danger, padding: '0.1rem 0.35rem', lineHeight: 1 }}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      ))}

                                      {/* The draft commits when focus leaves the ROW, not the
                                          amount box: clicking from the amount into the vendor
                                          field used to blur-and-discard the draft, so the vendor
                                          name could never be reached. relatedTarget is null when
                                          focus goes nowhere (e.g. a portaled dropdown click),
                                          which must not discard it either. */}
                                      {drafts.map((d, di) => (
                                        <div key={`draft-${di}`} style={{ display: 'flex', alignItems: 'center', gap: spacing(2), marginBottom: 3 }}
                                             onClick={e => e.stopPropagation()}
                                             onBlur={e => {
                                               const next = e.relatedTarget as Node | null;
                                               if (!next || e.currentTarget.contains(next)) return;
                                               // The vendor dropdown is portaled to <body>, so
                                               // it is not a DOM descendant of this row.
                                               if (next instanceof Element && next.closest('[data-portal-dropdown="true"]')) return;
                                               commitDraft(di);
                                             }}>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <SearchableSelect
                                              // The half-typed name is appended when it
                                              // isn't on the list yet, so the lookup that
                                              // drives the text box always resolves.
                                              options={
                                                d.vendor_name && !vendorSelectOptions.some(o => o.id === d.vendor_name)
                                                  ? [...vendorSelectOptions, { id: d.vendor_name, name: d.vendor_name }]
                                                  : vendorSelectOptions
                                              }
                                              value={d.vendor_name}
                                              onChange={val => setDrafts(drafts.map((x, j) => j === di ? { ...x, vendor_name: String(val) } : x))}
                                              onAddNew={async (name) => { await addNewVendor(name); return { id: name, name }; }}
                                              placeholder="Vendor (optional)"
                                              addNewLabel="vendor"
                                              allowClear
                                            />
                                          </div>
                                          {/* No autoFocus: a new row is filled vendor-first,
                                              and stealing focus to the amount put the caret in
                                              the wrong box. */}
                                          <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            disabled={adjSaving}
                                            placeholder="0.00"
                                            value={d.amount}
                                            onChange={e => setDrafts(drafts.map((x, j) => j === di ? { ...x, amount: e.target.value } : x))}
                                            onKeyDown={e => {
                                              // Enter commits straight away rather than
                                              // waiting for focus to leave the row.
                                              if (e.key === 'Enter') { e.preventDefault(); commitDraft(di); }
                                              if (e.key === 'Escape') setDrafts(drafts.filter((_, j) => j !== di));
                                            }}
                                            style={{
                                              width: 110, textAlign: 'right', padding: '0.2rem 0.4rem',
                                              borderRadius: radius.sm, border: `1px solid ${colors.primary}`,
                                              fontSize: fontSize.sm, fontVariantNumeric: 'tabular-nums',
                                              outline: 'none', boxSizing: 'border-box',
                                            }}
                                          />
                                          <button
                                            title="Discard"
                                            onClick={e => { e.stopPropagation(); setDrafts(drafts.filter((_, j) => j !== di)); }}
                                            style={{ ...s.btnSm, ...s.btnGhost, padding: '0.1rem 0.35rem', lineHeight: 1 }}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      ))}

                                      <button
                                        disabled={adjSaving}
                                        onClick={e => { e.stopPropagation(); setDrafts([...drafts, { vendor_name: '', amount: '' }]); }}
                                        style={{ ...s.btnSm, ...s.btnGhost, marginTop: 2 }}
                                      >
                                        + Add payment
                                      </button>
                                    </div>
                                  );
                                }

                                // ── The singleton kinds: one typed figure ──
                                const draftKey = `${adjKey}:${kind}`;
                                const current = adjAmount(adjKey, kind);
                                const draft = adjDrafts[draftKey];
                                return (
                                  <div key={kind} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing(3), marginBottom: 4 }}>
                                    <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>{label}</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      disabled={adjSaving}
                                      placeholder="0.00"
                                      value={draft !== undefined ? draft : (current ? String(current) : '')}
                                      onChange={e => setAdjDrafts(d => ({ ...d, [draftKey]: e.target.value }))}
                                      onBlur={() => {
                                        if (draft !== undefined) saveAdjustment(adjKey, kind, draft);
                                        setAdjDrafts(d => { const n = { ...d }; delete n[draftKey]; return n; });
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                        if (e.key === 'Escape') {
                                          setAdjDrafts(d => { const n = { ...d }; delete n[draftKey]; return n; });
                                          (e.target as HTMLInputElement).blur();
                                        }
                                      }}
                                      onClick={e => e.stopPropagation()}
                                      style={{
                                        width: 120, textAlign: 'right', padding: '0.2rem 0.4rem',
                                        borderRadius: radius.sm, border: `1px solid ${colors.border}`,
                                        fontSize: fontSize.sm, fontVariantNumeric: 'tabular-nums',
                                        outline: 'none', boxSizing: 'border-box',
                                      }}
                                    />
                                  </div>
                                );
                              })}
                              {!isPureDeduction && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing(3), borderTop: `1px solid ${colors.borderMuted}`, paddingTop: 5, marginTop: 5 }}>
                                  <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>Billed</span>
                                  <span style={{ fontSize: fontSize.sm, fontVariantNumeric: 'tabular-nums', color: colors.textMuted }}>{fmt(gross)}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing(3), ...(isPureDeduction ? { borderTop: `1px solid ${colors.borderMuted}`, paddingTop: 5, marginTop: 5 } : {}) }}>
                                <span style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textPrimary }}>
                                  {formulaLabel}
                                </span>
                                <span style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, fontVariantNumeric: 'tabular-nums', color: line.amount < 0 ? colors.danger : colors.textPrimary }}>
                                  {fmt(line.amount)}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                        {/* Subtotal for the nursing block, printed after its last
                            line. Display-only — pl.total_sales sums the income
                            rows, so emitting this as data would count it twice. */}
                        {line.label === NURSE_VENDOR_CATEGORY && (
                          <tr style={{ background: colors.primaryBg }}>
                            <td style={s.tdSl} />
                            <td style={{ ...s.tdCategory, fontWeight: fontWeight.bold }}>
                              Total Nursing Sales
                              <span style={{ marginLeft: 6, fontSize: fontSize.xs, color: colors.textFaint, fontWeight: fontWeight.regular, fontStyle: 'italic' }}>
                                8 + 12 + 24 Hrs + Visit − vendor payments
                              </span>
                            </td>
                            <td style={{ ...s.tdPct, fontWeight: fontWeight.bold }}>
                              {pl.total_sales > 0 ? pct(nursingSubtotal(incomeLines) / pl.total_sales * 100) : '—'}
                            </td>
                            <td style={{ ...s.tdAmount, fontWeight: fontWeight.bold }}>
                              {fmt(nursingSubtotal(incomeLines))}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                        );
                      })}
                      <tr className="pl-spacer" aria-hidden="true"><td colSpan={4} style={{ padding: 0, border: 'none' }} /></tr>
                      <tr className="pl-total" style={s.totalRow}>
                        <td style={s.tdSl} />
                        <td style={{ ...s.td, fontWeight: fontWeight.bold }}>Total Sales</td>
                        <td style={{ ...s.tdRight, fontWeight: fontWeight.bold }}>100%</td>
                        <td style={{ ...s.tdRight, fontWeight: fontWeight.bold }}>{fmt(pl.total_sales)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...s.card, ...s.reportCard }}>
                <div style={{ ...s.cardHeader, background: colors.successBg, color: colors.successDark, flex: '0 0 auto' }}>
                  <span>Expenses</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(pl.total_expenses)}</span>
                </div>
                <div ref={expenseFill.bodyRef} style={s.reportBody}>
                  <table ref={expenseFill.tableRef} className={FILL_TABLE_CLASS} style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.thSl}>#</th>
                        <th style={s.th}>Category</th>
                        <th style={s.thPct}>% Sales</th>
                        <th style={s.thAmount}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenseLines.length === 0 && (
                        <tr><td colSpan={4} style={{ ...s.td, color: colors.textFaint, fontStyle: 'italic' }}>No expenses recorded this period.</td></tr>
                      )}
                      {(() => {
                      // One table row per expense line. `sl` is null for a band
                      // inside the Management salaries group, which is numbered
                      // once as a whole; `zebra` is passed in so a group's rows
                      // share their parent's stripe.
                      const renderExpenseRow = (line: PLLine, sl: number | null, zebra: string | undefined) => {
                        // Payroll lines open their per-staff breakdown on the
                        // right, the same way a ledger line opens its entries.
                        const staff = staffForSalaryLine(line);
                        const isChild = sl === null;
                        const clickable = line.source === 'ledger' || staff.length > 0;
                        const isOpen = line.source === 'ledger'
                          ? activeExpenseTab === line.key
                          : activeSalaryLine === line.key;
                        // Built once so the hover tooltip can repeat it verbatim
                        // — the cell ellipsises, and the suffix is usually the
                        // first thing to get cut off.
                        const expenseSuffix = line.source === 'ledger'
                            // The ledger's Subledger 1 names, from the entries
                            // themselves — capped so the row can't wrap.
                            ? (line.subledgers.length > 0
                                ? subledgerSuffix(line.subledgers)
                                : `${expGrouped[line.key].length} entr${expGrouped[line.key].length === 1 ? 'y' : 'ies'}`)
                            : staff.length > 0
                              ? `${staff.length} ${staff.length === 1 ? 'person' : 'people'}`
                              : 'from Payroll';
                        return (
                        <tr
                          key={line.key}
                          style={{ cursor: clickable ? 'pointer' : 'default', background: isOpen ? colors.primaryBg : zebra }}
                          onClick={() => {
                            // The right column holds one panel at a time, so an
                            // expense line always dismisses the revenue one.
                            setActiveRevenueLine(null);
                            if (line.source === 'ledger') {
                              setActiveSalaryLine(null);
                              setActiveExpenseTab(prev => prev === line.key ? null : line.key);
                            } else if (staff.length > 0) {
                              setActiveExpenseTab(null);
                              setSalarySearch('');
                              setActiveSalaryLine(prev => prev === line.key ? null : line.key);
                            }
                          }}
                        >
                          <td style={s.tdSl}>{sl ?? ''}</td>
                          <td
                            style={isChild ? { ...s.tdCategory, paddingLeft: '1.9rem', color: colors.textSecondary } : s.tdCategory}
                            title={line.subledgers.length > SUBLEDGER_SUFFIX_LIMIT
                              ? `${line.label} — (${line.subledgers.join(', ')})`
                              : `${line.label} — ${expenseSuffix}`}
                          >
                            {/* Inside the group the "Management salaries — " half is
                                already said by the parent row. */}
                            {isChild ? line.label.slice(MGMT_SALARY_PREFIX.length) : line.label}
                            <span style={{ marginLeft: 6, fontSize: fontSize.xs, color: colors.textFaint, fontStyle: line.source !== 'ledger' ? 'italic' : undefined }}>
                              {expenseSuffix}
                            </span>
                          </td>
                          <td style={isChild ? { ...s.tdPct, color: colors.textMuted } : s.tdPct}>
                            {pl.total_sales > 0 ? pct(line.amount / pl.total_sales * 100) : '—'}
                          </td>
                          <td style={{ ...s.tdAmount, ...(isChild ? { color: colors.textSecondary } : {}), ...(line.amount < 0 ? { color: colors.success } : {}) }}>{fmtSigned(line.amount)}</td>
                        </tr>
                        );
                      };

                      // The Class/Level bands fold into one "Management salaries"
                      // row, expanded in place like the revenue side's expanders.
                      // Display only: expenseLines and every total still carry the
                      // bands individually, so nothing can be counted twice.
                      const mgmtLines = mgmtSalaryLines;
                      const mgmtTotal = mgmtLines.reduce((sum, l) => sum + l.amount, 0);
                      const mgmtPeople = mgmtLines.reduce((n, l) => n + staffForSalaryLine(l).length, 0);

                      const rows: React.ReactNode[] = [];
                      let sl = 0;
                      let groupDone = false;
                      expenseLines.forEach(line => {
                        if (isMgmtSalaryLine(line)) {
                          if (groupDone) return;
                          groupDone = true;
                          sl += 1;
                          const zebra = sl % 2 === 0 ? colors.surfaceMuted : undefined;
                          const suffix = `${mgmtPeople} ${mgmtPeople === 1 ? 'person' : 'people'} · ${mgmtLines.length} ${mgmtLines.length === 1 ? 'class' : 'classes'}`;
                          rows.push(
                            <tr
                              key="__mgmt_group__"
                              style={{ cursor: 'pointer', background: mgmtExpanded || activeSalaryLine === MGMT_GROUP_KEY ? colors.primaryBg : zebra }}
                              onClick={() => {
                                // Selecting the group expands it and lists every
                                // band's staff together on the right; clicking it
                                // again folds it and closes that list.
                                const opening = activeSalaryLine !== MGMT_GROUP_KEY;
                                setMgmtExpanded(opening);
                                setActiveRevenueLine(null);
                                setActiveExpenseTab(null);
                                setSalarySearch('');
                                setActiveSalaryLine(opening && mgmtPeople > 0 ? MGMT_GROUP_KEY : null);
                              }}
                            >
                              <td style={s.tdSl}>{sl}</td>
                              <td style={s.tdCategory} title={`Management salaries — ${suffix}`}>
                                <span style={{ marginRight: 6, color: colors.textFaint, fontSize: fontSize.xs }}>
                                  {mgmtExpanded ? '▾' : '▸'}
                                </span>
                                Management salaries
                                <span style={{ marginLeft: 6, fontSize: fontSize.xs, color: colors.textFaint, fontStyle: 'italic' }}>
                                  {suffix}
                                </span>
                              </td>
                              <td style={s.tdPct}>
                                {pl.total_sales > 0 ? pct(mgmtTotal / pl.total_sales * 100) : '—'}
                              </td>
                              {/* The all-classes Excel register lives in this row's
                                  panel on the right. */}
                              <td style={s.tdAmount}>{fmtSigned(mgmtTotal)}</td>
                            </tr>,
                          );
                          if (mgmtExpanded) mgmtLines.forEach(l => rows.push(renderExpenseRow(l, null, zebra)));
                          return;
                        }
                        sl += 1;
                        rows.push(renderExpenseRow(line, sl, sl % 2 === 0 ? colors.surfaceMuted : undefined));
                      });
                      return rows;
                      })()}
                      <tr className="pl-spacer" aria-hidden="true"><td colSpan={4} style={{ padding: 0, border: 'none' }} /></tr>
                      <tr className="pl-total" style={s.totalRow}>
                        <td style={s.tdSl} />
                        <td style={{ ...s.td, fontWeight: fontWeight.bold }}>Total Expenses</td>
                        <td style={{ ...s.tdRight, fontWeight: fontWeight.bold }}>{pl.total_sales > 0 ? pct(pl.total_expenses / pl.total_sales * 100) : '—'}</td>
                        <td style={{ ...s.tdRight, fontWeight: fontWeight.bold }}>{fmt(pl.total_expenses)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* EBITDA + Petty Cash side by side — one short row instead of two
                stacked cards, so the statement above keeps the height. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing(6), flex: '0 0 auto', alignItems: 'stretch' }}>
            <div style={{ ...s.card, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ ...s.ebitdaBlock, padding: '0.5rem 1.25rem', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: spacing(3) }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing(2) }}>
                  <span style={{ fontWeight: fontWeight.bold, fontSize: fontSize.md, color: colors.warningText }}>EBITDA (Profit)</span>
                  <span style={{ fontSize: fontSize.xs, color: colors.warningTextMuted }}>{MONTH_NAMES[month - 1]} {year}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing(2) }}>
                  <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>{pct(pl.ebitda_percent)} of sales</span>
                  <span style={{ fontSize: fontSize.xl, fontWeight: fontWeight.extrabold, fontVariantNumeric: 'tabular-nums', color: pl.ebitda >= 0 ? colors.successDark : colors.danger }}>
                    {fmt(pl.ebitda)}
                  </span>
                </div>
              </div>
            </div>

            {/* Petty Cash Drawer — this month's position, same numbers as the
                /financial/petty-cash screen for the P&L's selected month/year
                (not an all-time running balance). */}
            <div style={{ ...s.card, display: 'flex', flexDirection: 'column' }}>
              <div style={{ ...s.cardHeader, flex: '0 0 auto', padding: '0.4rem 1rem', fontSize: fontSize.md }}>
                <span>Petty Cash Drawer</span>
                <div style={{ display: 'flex', gap: spacing(2) }}>
                  <button style={{ ...s.btnSm, background: colors.primary, color: 'white' }} onClick={() => openDrawerModal('TOPUP')}>+ Top up</button>
                </div>
              </div>
              {/* Compact: the Opening + Top-ups − Expenses = Balance strip
                  already ends in the balance, so the big standalone figure and
                  the explanatory footnote were saying it twice. */}
              <div style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: spacing(2), flexWrap: 'wrap', flex: 1 }}>
                {!account && (
                  <span style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textFaint }}>—</span>
                )}
                {account && [
                  { label: 'Opening', value: account.opening, color: colors.textSecondary, op: '' },
                  { label: 'Top-ups', value: account.topups, color: colors.primaryDark, op: '+' },
                  { label: 'Expenses', value: account.expenses, color: colors.danger, op: '−' },
                  { label: 'Balance', value: account.balance, color: account.balance < 0 ? colors.danger : colors.successDark, op: '=', strong: true },
                ].map(st => (
                  <React.Fragment key={st.label}>
                    {st.op && <span style={{ fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: colors.textFaint }}>{st.op}</span>}
                    <div style={{ minWidth: 62 }}>
                      <div style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{st.label}</div>
                      <div style={{ fontSize: st.strong ? fontSize.md : fontSize.sm, fontWeight: st.strong ? fontWeight.extrabold : fontWeight.bold, color: st.color, fontVariantNumeric: 'tabular-nums' }}>{fmt(st.value)}</div>
                    </div>
                  </React.Fragment>
                ))}
                {account && account.balance < 0 && (
                  <span style={{ fontSize: fontSize.xs, color: colors.danger, fontWeight: fontWeight.medium }}>Overdrawn — add a top-up.</span>
                )}
              </div>
            </div>
            </div>
          </div>

          {/* ── Right column: data entry ── */}
          <div style={s.rightCol}>
            {/* A payroll line takes over this column while it's open — salaries
                come from the Payroll module and can't be edited here, so the
                entry panel would be the wrong thing to show alongside them. */}
            {activeRevenueLine ? (() => {
              // The consumable-returns figure has no line in incomeLines — it's
              // folded into Consumables — so build a synthetic line for its panel.
              const rline = activeRevenueLine === RETURNS_LINE_KEY
                ? ({ key: RETURNS_LINE_KEY, label: 'Consumables Returns', amount: -consumableReturns, source: 'billing', order: 0, subledgers: [] } as unknown as PLLine)
                : incomeLines.find(l => l.key === activeRevenueLine) as PLLine;
              const adjKey = rline ? ADJUSTABLE_LINE_KEY[rline.label] : undefined;
              // Consumables / ME Rental carry manual deductions that no bill
              // knows about, so the bills sum to gross, not the statement's net.
              const ded = adjKey ? adjDeducted(adjKey) : 0;
              const rows = rline ? revenueRowsFor(rline) : [];
              return (
                <RevenuePanel
                  line={rline}
                  rows={rows}
                  isNursing={!!rline && !!(NURSING_SHIFT_BUCKETS as readonly string[])
                    .find(b => nursingCategory(b) === rline.label)}
                  headerTotal={(rline?.amount || 0) + ded}
                  deducted={ded}
                  monthLabel={`${MONTH_NAMES[month - 1]} ${year}`}
                  loading={billDetailLoading}
                  search={revenueSearch}
                  onSearch={setRevenueSearch}
                  expandedPatient={expandedPatient}
                  onExpandPatient={setExpandedPatient}
                  onClose={() => setActiveRevenueLine(null)}
                  s={s}
                />
              );
            })() : activeSalaryLine === MGMT_GROUP_KEY ? (
              <SalaryPanel
                key={MGMT_GROUP_KEY}
                line={mgmtSalaryLines.length ? {
                  ...mgmtSalaryLines[0],
                  key: MGMT_GROUP_KEY,
                  label: 'Management salaries — all classes',
                  amount: mgmtSalaryLines.reduce((sum, l) => sum + l.amount, 0),
                } : undefined}
                staff={consolidatedMgmtStaff()}
                keepOrder
                onExportExcel={() => exportManagementRegister(mgmtSalaryLines)}
                exportTitle="Download every management class as one Excel register"
                search={salarySearch}
                onSearch={setSalarySearch}
                onClose={() => setActiveSalaryLine(null)}
                month={month}
                year={year}
                s={s}
              />
            ) : activeSalaryLine ? (
              <SalaryPanel
                line={expenseLines.find(l => l.key === activeSalaryLine)}
                staff={staffForSalaryLine(expenseLines.find(l => l.key === activeSalaryLine) as PLLine)}
                search={salarySearch}
                onSearch={setSalarySearch}
                onClose={() => setActiveSalaryLine(null)}
                month={month}
                year={year}
                s={s}
              />
            ) : (
            <EntryPanel
              title="Expense Entry"
              categories={expenseCategories}
              grouped={expGrouped}
              activeTab={activeExpenseTab}
              onSelectTab={setActiveExpenseTab}
              onOpenAddCategory={() => { setAddCategoryFor('DEBIT'); setNewCategoryName(''); }}
              month={month}
              year={year}
              onOpenBatch={() => openBatchModal('DEBIT')}
              onEdit={(e) => startEdit(e, 'DEBIT')}
              onDelete={deleteExpense}
              onRenameCategory={(cat) => { setRenameCategory(cat); setRenameCategoryName(cat.name); }}
              onDeleteCategory={(cat) => { setDeleteCategoryTarget(cat); setDeleteCategoryPassword(''); }}
              s={s}
            />
            )}
            {/* Income Entry hidden — client wants income to come only from
                billing, not manual ledger entries. Kept behind a flag rather
                than removed, since the client may want it back. */}
            {INCOME_ENTRY_ENABLED && (
              <EntryPanel
                title="Income Entry"
                categories={incomeCategories}
                grouped={incGrouped}
                activeTab={activeIncomeTab}
                onSelectTab={setActiveIncomeTab}
                onOpenAddCategory={() => { setAddCategoryFor('CREDIT'); setNewCategoryName(''); }}
                month={month}
                year={year}
                onOpenBatch={() => openBatchModal('CREDIT')}
                onEdit={(e) => startEdit(e, 'CREDIT')}
                onDelete={deleteExpense}
                onRenameCategory={(cat) => { setRenameCategory(cat); setRenameCategoryName(cat.name); }}
                onDeleteCategory={(cat) => { setDeleteCategoryTarget(cat); setDeleteCategoryPassword(''); }}
                s={s}
              />
            )}
          </div>
        </div>
      )}
      {/* ── Multi-Row Batch Modal (Compliance & Excel Merging) ── */}
      {isModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '10vh' }}>
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', width: '1800px', maxWidth: '98vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>

            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                  Batch {batchType === 'CREDIT' ? 'Income' : 'Expense'} Entry: {batchType === 'CREDIT' ? activeIncomeTab : activeExpenseTab}
                </h2>
                <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.15rem 0 0' }}>
                  Entered By: <span style={{ fontWeight: 600, color: '#0f172a' }}>{currentUser?.email || '—'}</span>
                </p>
              </div>
              <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>

            {/* Table Area */}
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10 }}>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '130px' }}>Date</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '160px' }}>Category</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '220px' }}>Subledger 1</th>
                    {activeSlots.map(slot => (
                      <th key={slot} style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '160px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                          Subledger {slot}
                          <button
                            onClick={() => removeSubledgerColumn(slot)}
                            title={`Remove Subledger ${slot} (only if it has no entries)`}
                            style={{ background: '#ef4444', border: 'none', cursor: 'pointer', color: 'white', fontSize: '0.8rem', fontWeight: 'bold', lineHeight: 1, width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
                          >&times;</button>
                        </span>
                      </th>
                    ))}
                    {activeSlots.length < 4 && (
                      <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '34px' }}>
                        <button onClick={addSubledgerColumn} title="Add another Subledger column" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '0 8px', cursor: 'pointer', color: '#2563eb', fontWeight: 'bold', fontSize: '1rem' }}>+</button>
                      </th>
                    )}
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', minWidth: '260px' }}>Particulars</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '130px' }}>Mode</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '110px' }}>Amount (₹)</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '150px' }}>Transferred From</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '150px' }}>Sender</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '150px' }}>Receiver</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '90px' }}>Petty Cash</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '110px' }}>Proof</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '200px' }}>Remarks</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#475569', width: '50px' }} />
                  </tr>
                </thead>
                <tbody>
                  {batchRows.map((row, index) => {

                    // 2. COMPLIANCE LOGIC — missing proof/remark entirely, or an
                    // edited expense still riding on its old (now stale) one.
                    const isStaleEdit = needsFreshJustification(row);
                    const needsRemark = !isDebitCompliant(row.transaction_type, row.proof, row.remarks) || isStaleEdit;

                    // Changing a level invalidates everything below it — those
                    // values belong to the parent just replaced. Only fires on
                    // a real change to an already-set field, so filling in a
                    // blank row clears nothing.
                    const updateRow = (field: string, value: any) => {
                      const level = CASCADE_LEVELS.indexOf(field);
                      const prev = batchRows[index][field];
                      const newRows = [...batchRows];
                      if (level !== -1 && prev && String(prev) !== String(value)) {
                        const below = CASCADE_LEVELS.slice(level + 1);
                        const hadTags = below.some(f => batchRows[index][f]);
                        newRows[index] = {
                          ...newRows[index],
                          [field]: value,
                          ...Object.fromEntries(below.map(f => [f, ''])),
                        };
                        if (hadTags) toast('Subledgers below cleared — they belonged to the previous selection');
                      } else {
                        newRows[index] = { ...newRows[index], [field]: value };
                      }
                      setBatchRows(newRows);
                    };

                    // Function to inject a Sub-Row right below this one
                    const addSubRow = () => {
                      const newRows = [...batchRows];
                      newRows.splice(index + 1, 0, {
                        id: 'new_' + Date.now(),
                        isNew: true,
                        transaction_date: row.transaction_date, // INHERIT DATE
                        transaction_type: row.transaction_type,
                        ledger_category_id: row.ledger_category_id, // INHERIT CATEGORY
                        ledger_subcategory_id: '',
                        ledger_subledger_2_id: '',
                        ledger_subledger_3_id: '',
                        ledger_subledger_4_id: '',
                        ledger_subledger_5_id: '',
                        transferred_from_id: '',
                        sender_id: '',
                        receiver_id: '',
                        particular: '',
                        amount: '',
                        payment_mode: 'CASH',
                        paid_from_petty_cash: false,
                        remarks: '',
                        proof: null
                      });
                      setBatchRows(newRows);
                    };

                    return (
                      <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        
                        {/* DATE */}
                        <td style={{ padding: '0.5rem' }}>
                          <input type="date" style={s.input} value={row.transaction_date} onChange={e => updateRow('transaction_date', e.target.value)} />
                        </td>
                        
                        {/* CATEGORY */}
                        <td style={{ padding: '0.5rem' }}>
                          <SearchableSelect
                            options={categoriesFor(batchType)}
                            value={row.ledger_category_id}
                            onChange={(val) => updateRow('ledger_category_id', val)}
                            onAddNew={(name) => addCategoryInline(name, batchType)}
                            placeholder="Select category..."
                            addNewLabel="category"
                          />
                        </td>

                        {/* SUBLEDGER 1 (existing Subcategory system, searchable, with inline "add new" + Inject Sub-Row Button on the LEFT) */}
                        <td style={{ padding: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            {/* Inject Sub-Row Button — adds another horizontal line for this Date & Category */}
                            <button
                              title="Add another item for this Date & Category"
                              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '0 6px', cursor: 'pointer', color: '#2563eb', fontWeight: 'bold' }}
                              onClick={addSubRow}
                            >+</button>

                            <div style={{ flex: 1 }}>
                              <SearchableSelect
                                options={subcategoriesForRow(row)}
                                value={row.ledger_subcategory_id}
                                onChange={(val) => updateRow('ledger_subcategory_id', val)}
                                onAddNew={addNewSubcategoryFM(row)}
                                placeholder={row.ledger_category_id ? 'Select subledger...' : 'Pick a ledger first'}
                                disabled={!row.ledger_category_id}
                                addNewLabel={parentNameForRow(row, 1) ? `to ${parentNameForRow(row, 1)}` : 'subcategory'}
                              />
                            </div>
                          </div>
                        </td>

                        {/* SUBLEDGER 2-5 — each scoped to the level above it in THIS row */}
                        {activeSlots.map(slot => {
                          const field = `ledger_subledger_${slot}_id`;
                          const parentId = parentIdForSlot(row, slot);
                          const parentName = parentNameForRow(row, slot);
                          return (
                            <td key={slot} style={{ padding: '0.5rem' }}>
                              <SearchableSelect
                                options={subledgerValuesForRow(row, slot)}
                                value={row[field] || ''}
                                onChange={(val) => updateRow(field, val)}
                                onAddNew={addNewSubledgerValueFor(slot, row)}
                                placeholder={parentId ? `Select Subledger ${slot}...` : `Pick Subledger ${slot - 1} first`}
                                disabled={!parentId}
                                addNewLabel={parentName ? `to ${parentName}` : `Subledger ${slot} value`}
                              />
                            </td>
                          );
                        })}
                        {/* Spacer under the "add Subledger column" (+) header */}
                        {activeSlots.length < 4 && <td style={{ padding: '0.5rem' }} />}

                        {/* PARTICULARS */}
                        <td style={{ padding: '0.5rem', minWidth: '260px' }}>
                          <input type="text" style={s.input} placeholder="Description..." value={row.particular} onChange={e => updateRow('particular', e.target.value)} />
                        </td>

                        {/* MODE / PAYMENT MODE */}
                        <td style={{ padding: '0.5rem' }}>
                          <select style={s.input} value={row.payment_mode || 'CASH'} onChange={e => updateRow('payment_mode', e.target.value)}>
                            <option value="UPI">UPI</option>
                            <option value="CASH">Cash</option>
                            <option value="BANK_TRANSFER">Bank Transfer</option>
                            <option value="CARD">Card</option>
                          </select>
                        </td>

                        {/* AMOUNT */}
                        <td style={{ padding: '0.5rem' }}>
                          <input type="number" step="0.01" style={{ ...s.input, textAlign: 'right' }} placeholder="0.00" value={row.amount} onChange={e => updateRow('amount', e.target.value)} />
                        </td>

                        {/* TRANSFERRED FROM / SENDER / RECEIVER */}
                        <td style={{ padding: '0.5rem' }}>
                          <SearchableSelect options={transferredFromOptions} value={row.transferred_from_id || ''} onChange={(val) => updateRow('transferred_from_id', val)} onAddNew={addNewTransferredFrom} placeholder="Transferred from..." addNewLabel="option" />
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <SearchableSelect options={senderOptions} value={row.sender_id || ''} onChange={(val) => updateRow('sender_id', val)} onAddNew={addNewSender} placeholder="Sender..." addNewLabel="sender" />
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <SearchableSelect options={receiverOptions} value={row.receiver_id || ''} onChange={(val) => updateRow('receiver_id', val)} onAddNew={addNewReceiver} placeholder="Receiver..." addNewLabel="receiver" />
                        </td>

                        {/* PAID FROM PETTY CASH */}
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
                            checked={!!row.paid_from_petty_cash}
                            onChange={e => updateRow('paid_from_petty_cash', e.target.checked)}
                            title="Deduct this from the petty cash drawer"
                          />
                        </td>

                        {/* PROOF COLUMN — view any of the entry's proofs; upload appends
                            (the full ledger is where several are attached at once) */}
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {(() => {
                            const attached = parseProofs(row.proof);
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
                                {attached.length > 0 && (
                                  <>
                                    <ProofCell value={row.proof} thumbnailSize={24} />
                                    <button
                                      onClick={() => updateRow('proof', serializeProofs(attached.slice(0, -1)))}
                                      style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: 0, display: 'flex', alignItems: 'center' }}
                                      title={attached.length > 1 ? 'Remove last file' : 'Remove file'}
                                    >&times;</button>
                                  </>
                                )}
                                {attached.length < MAX_PROOFS && (
                                  <label style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#64748b', cursor: uploadingRowId === row.id ? 'wait' : 'pointer', display: 'block', flex: 1, textAlign: 'center', boxSizing: 'border-box' }}>
                                    {uploadingRowId === row.id ? '...' : attached.length ? '+' : 'Upload'}
                                    <input
                                      type="file"
                                      style={{ display: 'none' }}
                                      disabled={uploadingRowId === row.id}
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = '';
                                        if (!file) return;
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        setUploadingRowId(row.id);
                                        try {
                                          const res = await axios.post(`${API}/api/financial/upload-proof`, formData, {
                                            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                          });
                                          // Append — this row may already carry proofs added in the full ledger.
                                          updateRow('proof', serializeProofs([...attached, res.data.proof_url]));
                                          toast.success('File attached');
                                        } catch (err) {
                                          toast.error('Upload failed');
                                        } finally {
                                          setUploadingRowId(null);
                                        }
                                      }}
                                    />
                                  </label>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        
                        {/* REMARKS */}
                        <td style={{ padding: '0.5rem' }}>
                          <input 
                            type="text" 
                            style={{ ...s.input, borderColor: needsRemark ? '#ef4444' : '#e2e8f0', backgroundColor: needsRemark ? '#fef2f2' : 'white' }}
                            placeholder={isStaleEdit ? "New reason required (entry was edited)..." : needsRemark ? "Reason required..." : "Notes..."}
                            value={row.remarks} 
                            onChange={e => updateRow('remarks', e.target.value)} 
                          />
                        </td>
                        
                        {/* DELETE ROW */}
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                           <button onClick={() => setBatchRows(batchRows.filter((_, i) => i !== index))} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>&times;</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
              <button 
                style={{ ...s.btn, ...s.btnGhost, color: '#2563eb', borderColor: '#bfdbfe', background: '#eff6ff' }} 
                onClick={() => setBatchRows([...batchRows, createEmptyRow(batchType)])} 
              >
                + Add Another Row
              </button>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button style={{ ...s.btn, ...s.btnGhost }} onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button
                  style={{ ...s.btn, ...s.btnPrimary }}
                  onClick={async () => {
                    const rowsToProcess = batchRows.filter(r => parseFloat(r.amount) > 0);
                    const invalidRows   = rowsToProcess.filter(r =>
                      !isDebitCompliant(r.transaction_type, r.proof, r.remarks) || needsFreshJustification(r)
                    );
                    if (invalidRows.length > 0) {
                      toast.error(COMPLIANCE_MESSAGE);
                      return;
                    }
                    try {
                      await Promise.all(rowsToProcess.map(row => {
                        const payload: any = {
                          transaction_date:      row.transaction_date,
                          transaction_type:      row.transaction_type,
                          ledger_category_id:    parseInt(row.ledger_category_id)    || null,
                          ledger_subcategory_id: row.ledger_subcategory_id ? parseInt(row.ledger_subcategory_id) : null,
                          particular:            row.particular  || null,
                          amount:                parseFloat(row.amount),
                          payment_mode:          row.payment_mode,
                          paid_from_petty_cash:  !!row.paid_from_petty_cash,
                          transferred_from_id:   row.transferred_from_id ? parseInt(row.transferred_from_id) : null,
                          sender_id:             row.sender_id ? parseInt(row.sender_id) : null,
                          receiver_id:           row.receiver_id ? parseInt(row.receiver_id) : null,
                          remarks:               row.remarks     || null,
                          proof: row.proof || null,
                        };
                        for (const slot of activeSlots) {
                          const field = `ledger_subledger_${slot}_id`;
                          payload[field] = row[field] ? parseInt(row[field]) : null;
                        }
                        return row.isNew
                          ? axios.post(`${API}/api/financial/transactions`, payload, { headers: getAuthHeader() })
                          : axios.patch(`${API}/api/financial/transactions/${row.id}`, payload, { headers: getAuthHeader() });
                      }));
                      toast.success('Batch saved successfully!');
                      setIsModalOpen(false);
                      fetchPL();
                      fetchAccount();
                    } catch (err: any) {
                      toast.error(err?.response?.data?.detail || 'Failed to save some entries');
                    }
                  }}
                >
                  Save All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Category modal ── */}
      {addCategoryFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: colors.surface, padding: '1.5rem', borderRadius: radius.xl, width: '420px', maxWidth: '90vw', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textPrimary, marginBottom: spacing(1) }}>
              New {addCategoryFor === 'CREDIT' ? 'Income' : 'Expense'} Category
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: spacing(4) }}>
              Only visible under {addCategoryFor === 'CREDIT' ? 'Income Entry' : 'Expense Entry'}.
            </div>
            <input
              autoFocus
              type="text"
              style={s.input}
              placeholder="Category name…"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitNewCategory(); }}
            />
            <div style={{ display: 'flex', gap: spacing(2), justifyContent: 'flex-end', marginTop: spacing(5) }}>
              <button
                style={{ ...s.btn, ...s.btnGhost }}
                onClick={() => { setAddCategoryFor(null); setNewCategoryName(''); }}
                disabled={savingCategory}
              >
                Cancel
              </button>
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                onClick={submitNewCategory}
                disabled={savingCategory || !newCategoryName.trim()}
              >
                {savingCategory ? 'Adding…' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rename category modal ── */}
      {renameCategory && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: colors.surface, padding: '1.5rem', borderRadius: radius.xl, width: '420px', maxWidth: '90vw', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textPrimary, marginBottom: spacing(4) }}>
              Rename Category
            </div>
            <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Category name</label>
            <input
              autoFocus
              type="text"
              style={{ ...s.input, marginTop: spacing(1) }}
              value={renameCategoryName}
              onChange={e => setRenameCategoryName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitRenameCategory(); }}
            />
            <div style={{ display: 'flex', gap: spacing(2), justifyContent: 'flex-end', marginTop: spacing(5) }}>
              <button style={{ ...s.btn, ...s.btnGhost }} onClick={() => setRenameCategory(null)} disabled={savingRename}>Cancel</button>
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                onClick={submitRenameCategory}
                disabled={savingRename || renameCategoryName.trim() === ''}
              >
                {savingRename ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete category modal (password-gated; also deletes the entries) ── */}
      {deleteCategoryTarget && (() => {
        const entryCount = (expGrouped[deleteCategoryTarget.name] || incGrouped[deleteCategoryTarget.name] || []).length;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: colors.surface, padding: '1.5rem', borderRadius: radius.xl, width: '440px', maxWidth: '90vw', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
              <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.danger, marginBottom: spacing(2) }}>
                Delete “{deleteCategoryTarget.name}”?
              </div>
              <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: spacing(4) }}>
                {entryCount > 0
                  ? <>This will permanently delete this category and its <strong>{entryCount}</strong> entr{entryCount === 1 ? 'y' : 'ies'}. This cannot be undone.</>
                  : <>This category has no entries. It will be permanently deleted.</>}
              </div>
              <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Enter your password to confirm</label>
              <input
                autoFocus
                type="password"
                style={{ ...s.input, marginTop: spacing(1) }}
                placeholder="Password"
                value={deleteCategoryPassword}
                onChange={e => setDeleteCategoryPassword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') submitDeleteCategory(); }}
              />
              <div style={{ display: 'flex', gap: spacing(2), justifyContent: 'flex-end', marginTop: spacing(5) }}>
                <button style={{ ...s.btn, ...s.btnGhost }} onClick={() => setDeleteCategoryTarget(null)} disabled={deletingCategory}>Cancel</button>
                <button
                  style={{ ...s.btn, ...s.btnRed }}
                  onClick={submitDeleteCategory}
                  disabled={deletingCategory || deleteCategoryPassword === ''}
                >
                  {deletingCategory ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Delete-entry confirm (password-gated, same as category delete) ── */}
      {deleteEntryTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: colors.surface, padding: '1.5rem', borderRadius: radius.xl, width: '440px', maxWidth: '90vw', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.danger, marginBottom: spacing(2) }}>
              Delete this entry?
            </div>
            {deleteEntryTarget.entry && (
              <div style={{ background: colors.surfaceMuted, border: `1px solid ${colors.borderMuted}`, borderRadius: radius.md, padding: '0.6rem 0.75rem', marginBottom: spacing(3) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing(3) }}>
                  <span style={{ fontWeight: fontWeight.semibold, color: colors.textPrimary }}>
                    {deleteEntryTarget.entry.description || deleteEntryTarget.entry.sub_category || deleteEntryTarget.entry.category}
                  </span>
                  <span style={{ fontWeight: fontWeight.bold, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtSigned(deleteEntryTarget.entry.amount)}
                  </span>
                </div>
                <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 2 }}>
                  {[deleteEntryTarget.entry.category, deleteEntryTarget.entry.sub_category,
                    ...(deleteEntryTarget.entry.subledger_names || [])].filter(Boolean).join(' / ')}
                </div>
              </div>
            )}
            <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: spacing(4) }}>
              This permanently removes the entry from the ledger and the P&amp;L. This cannot be undone.
            </div>
            <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Enter your password to confirm</label>
            <input
              autoFocus
              type="password"
              style={{ ...s.input, marginTop: spacing(1) }}
              placeholder="Password"
              value={deleteEntryPassword}
              onChange={e => setDeleteEntryPassword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitDeleteEntry(); }}
            />
            <div style={{ display: 'flex', gap: spacing(2), justifyContent: 'flex-end', marginTop: spacing(5) }}>
              <button style={{ ...s.btn, ...s.btnGhost }} onClick={() => setDeleteEntryTarget(null)} disabled={deletingEntry}>Cancel</button>
              <button
                style={{ ...s.btn, ...s.btnRed }}
                onClick={submitDeleteEntry}
                disabled={deletingEntry || deleteEntryPassword === ''}
              >
                {deletingEntry ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Petty-cash top-up / set-balance modal ── */}
      {drawerModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Scrolls instead of running off-screen — the top-up form carries
              three dropdowns the Set Balance one doesn't. */}
          <div style={{ background: colors.surface, padding: '1.5rem', borderRadius: radius.xl, width: '420px', maxWidth: '90vw', maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textPrimary, marginBottom: spacing(1) }}>
              {drawerModal === 'TOPUP' ? 'Add Top-up' : 'Set Balance'}
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: spacing(4) }}>
              {drawerModal === 'TOPUP'
                ? 'Money added to the petty-cash account (increases the balance).'
                : 'Set the account’s opening amount. Adds to the running balance.'}
            </div>
            <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Amount (₹)</label>
            <input
              autoFocus
              type="number"
              step="0.01"
              min="0"
              style={{ ...s.input, textAlign: 'right', marginTop: spacing(1) }}
              placeholder="0.00"
              value={drawerAmount}
              onChange={e => setDrawerAmount(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveDrawerEvent(); }}
            />
            {/* Top-up only — a Set Balance is an accounting correction, not a
                transfer between two parties. All three are petty-cash-only
                lists: nothing is read from or added to the ledger's own
                Transferred From / Sender / Receiver dropdowns. */}
            {drawerModal === 'TOPUP' && (
              <>
                <label style={{ ...s.drawerFieldLabel }}>
                  Withdrawn From <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
                </label>
                <div style={{ marginTop: spacing(1) }}>
                  <SearchableSelect options={pettyWithdrawnFromOptions} value={drawerWithdrawnFrom}
                    onChange={setDrawerWithdrawnFrom} onAddNew={addNewPettyWithdrawnFrom}
                    placeholder="Select account..." addNewLabel="account" />
                </div>

                <label style={{ ...s.drawerFieldLabel }}>
                  Sender <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
                </label>
                <div style={{ marginTop: spacing(1) }}>
                  <SearchableSelect options={pettySenderOptions} value={drawerSender}
                    onChange={setDrawerSender} onAddNew={addNewPettySender}
                    placeholder="Select sender..." addNewLabel="sender" />
                </div>

                <label style={{ ...s.drawerFieldLabel }}>
                  Receiver <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
                </label>
                <div style={{ marginTop: spacing(1) }}>
                  <SearchableSelect options={pettyReceiverOptions} value={drawerReceiver}
                    onChange={setDrawerReceiver} onAddNew={addNewPettyReceiver}
                    placeholder="Select receiver..." addNewLabel="receiver" />
                </div>
              </>
            )}

            {/* Note last — the free-text afterthought, not something to get
                past on the way to the fields that matter. */}
            <label style={{ ...s.drawerFieldLabel }}>
              Note <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
            </label>
            <input
              type="text"
              style={{ ...s.input, marginTop: spacing(1) }}
              placeholder="e.g. withdrew from HDFC"
              value={drawerNote}
              onChange={e => setDrawerNote(e.target.value)}
            />

            <div style={{ display: 'flex', gap: spacing(2), justifyContent: 'flex-end', marginTop: spacing(5) }}>
              <button style={{ ...s.btn, ...s.btnGhost }} onClick={() => setDrawerModal(null)} disabled={savingDrawer}>Cancel</button>
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                onClick={saveDrawerEvent}
                disabled={savingDrawer || drawerAmount.trim() === ''}
              >
                {savingDrawer ? 'Saving…' : drawerModal === 'TOPUP' ? 'Add Top-up' : 'Set Balance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bad debts manage modal ── */}
      {badDebtModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: colors.surface, padding: '1.5rem', borderRadius: radius.xl, width: '520px', maxWidth: '92vw', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: spacing(1) }}>
              <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textPrimary }}>
                Bad Debts — {MONTH_NAMES[month - 1]} {year}
              </div>
              <button onClick={() => setBadDebtModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: colors.textMuted, lineHeight: 1 }}>&times;</button>
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: spacing(4) }}>
              Manual reductions to this month’s revenue. Each entry lowers Total Sales &amp; EBITDA. Does not affect petty cash.
            </div>

            {/* Existing entries */}
            <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${colors.borderMuted}`, borderRadius: radius.md, marginBottom: spacing(4) }}>
              {badDebts.length === 0 ? (
                <div style={{ padding: spacing(4), color: colors.textFaint, fontStyle: 'italic', fontSize: fontSize.sm }}>No bad debts recorded this month.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.md }}>
                  <tbody>
                    {badDebts.map(bd => (
                      <tr key={bd.id}>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: `1px solid ${colors.surfaceMuted}`, color: colors.textMuted, whiteSpace: 'nowrap' }}>
                          {new Date(bd.entry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: `1px solid ${colors.surfaceMuted}` }}>
                          <div>{bd.patient_name || bd.note || <span style={{ color: colors.textFaint }}>—</span>}</div>
                          {(bd.patient_name && bd.note) && (
                            <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>{bd.note}</div>
                          )}
                          <div style={{ fontSize: fontSize.xs, color: colors.textFaint }}>
                            {[
                              bd.reason_name,
                              bd.approved_by_name ? `approved by ${bd.approved_by_name}` : null,
                              bd.created_by_email ? `added by ${bd.created_by_email}` : null,
                            ].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: `1px solid ${colors.surfaceMuted}`, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: colors.danger, fontWeight: fontWeight.semibold }}>
                          −{fmt(Number(bd.amount))}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', borderBottom: `1px solid ${colors.surfaceMuted}`, textAlign: 'right' }}>
                          <button onClick={() => deleteBadDebt(bd.id)} style={{ ...s.btnSm, background: colors.surfaceMuted, color: colors.danger }} title="Remove">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Add form */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing(3), marginBottom: spacing(3) }}>
              <div>
                <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Amount (₹)</label>
                <input type="number" step="0.01" min="0" style={{ ...s.input, textAlign: 'right', marginTop: spacing(1) }} placeholder="0.00"
                  value={badDebtAmount} onChange={e => setBadDebtAmount(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveBadDebt(); }} />
              </div>
              <div>
                <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>Date</label>
                <input type="date" style={{ ...s.input, marginTop: spacing(1) }} value={badDebtDate} onChange={e => setBadDebtDate(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing(3), marginBottom: spacing(3) }}>
              <div>
                <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>
                  Patient / client <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
                </label>
                <div style={{ marginTop: spacing(1) }}>
                  <SearchableSelect options={patientOptions} value={badDebtPatientId} onChange={setBadDebtPatientId}
                    onAddNew={async () => { toast.error('Add patients from the Patients screen'); return null; }}
                    placeholder="Whose debt…" addNewLabel="patient" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>
                  Reason <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
                </label>
                <div style={{ marginTop: spacing(1) }}>
                  <SearchableSelect options={reasonOptions} value={badDebtReasonId} onChange={setBadDebtReasonId}
                    onAddNew={addNewBadDebtReason} placeholder="Why written off…" addNewLabel="reason" />
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing(3), marginBottom: spacing(4) }}>
              <div>
                <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>
                  Approved by <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
                </label>
                <div style={{ marginTop: spacing(1) }}>
                  <SearchableSelect options={approverOptions} value={badDebtApproverId} onChange={setBadDebtApproverId}
                    onAddNew={addNewApprover} placeholder="Who authorised…" addNewLabel="approver" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.textSecondary }}>
                  Note <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>(optional)</span>
                </label>
                <input type="text" style={{ ...s.input, marginTop: spacing(1) }} placeholder="e.g. partial waiver"
                  value={badDebtNote} onChange={e => setBadDebtNote(e.target.value)} />
              </div>
            </div>
            {currentUser?.email && (
              <div style={{ fontSize: fontSize.xs, color: colors.textFaint, marginBottom: spacing(3) }}>
                Will be recorded as added by <strong>{currentUser.email}</strong>
              </div>
            )}

            <div style={{ display: 'flex', gap: spacing(2), justifyContent: 'flex-end' }}>
              <button style={{ ...s.btn, ...s.btnGhost }} onClick={() => setBadDebtModalOpen(false)}>Close</button>
              <button style={{ ...s.btn, ...s.btnPrimary }} onClick={saveBadDebt} disabled={savingBadDebt || badDebtAmount.trim() === ''}>
                {savingBadDebt ? 'Adding…' : 'Add Bad Debt'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FinancialModule;
